import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import IframeViews from "rc-app/components/IframeViews/IframeViews";
import { getProjectById, listAmenities } from "rc-app/api/projectActionsApi";
import { getVrSessionList } from "rc-app/api/vrSessionActionsApi.js";
import { createShareLink, recordShareLinkClick, getPublicProject } from "rc-app/api/shareLinkActionsApi";
import { getAgencyById } from "rc-app/api/agencyActionsApi";
import { getMatterports } from "rc-app/api/matterportActionsApi";
import { getMetaModels } from "rc-app/api/metaModelsActionsApi";
import { getClientManagerByEmailId, getClientAdminByEmailId, getSystemAdminList } from "rc-app/api/systemBusinessAdminActionApi";
import { getAgentProfile, getAllAgent } from "rc-app/api/agentActionsApi";
import { getCustomerProfile, getCustomerList, createReCustomer } from "rc-app/api/customerActionsApi";
import { createGuidedRoom, getGuidedRoom, listHostGuidedRooms } from "rc-app/api/guidedRoomActionsApi";
import {
  guidedTourReducer as gtReduce,
  initialGuidedTourState as gtInitialState,
  hasEnded as gtHasEnded,
  isLiveHere as gtIsLiveHere,
  isBlockedElsewhere as gtIsBlockedElsewhere,
  roomToOpen as gtRoomToOpen,
} from "rc-app/utils/guidedTourMachine";
import { useViewportInsets } from "rc-app/hooks/useViewportInsets";

/* One live room per HOST, remembered across reloads. Imported rather than
   re-declared: the host page reads the same keys, and a claim the two
   pages disagree about is a room nobody can release.
   localStorage, not sessionStorage: the host opens the room in a NEW TAB,
   and sessionStorage is per-tab — the room tab would see nothing. */
import {
  activeKeyFor as gtActiveKeyFor,
  hostCredKeyFor as gtHostCredKeyFor,
  affectsActiveRoom as gtAffectsActiveRoom,
  apiMessage as gtApiMessage,
  readConflict as gtReadConflict,
  readConflictRoom as gtReadConflictRoom,
  conflictMessage as gtConflictMessage,
  isRoomLive as gtIsRoomLive,
  isRoomGone as gtIsRoomGone,
  localPreviewKeyFor as gtLocalPreviewKeyFor,
  type ActiveClaim as GtActiveClaim,
} from "rc-app/utils/guidedRoomFlow";
import PublishEmbedModal from "rc-app/components/PublishEmbedModal";
import { listEmbedKeys } from "rc-app/api/embedActionsApi";
import { getAmenityIcon } from "rc-app/utils/amenityIcons";
import UserService from "rc-app/services/UserService";
import { MATTERPORT_SDK_KEY, useMatterportAnalytics } from "rc-app/hooks/useMatterportAnalytics";
import MatterportLeadGate from "rc-app/components/MatterportLeadGate";
import { promptUserGeo } from "rc-app/utils/userGeoCache";
import { guidedTourEnabled } from "rc-app/utils/featureFlags";
import * as Icon from "./icons";
import "./ProjectDetailsV3.css";

/* ── ProjectDetailsV3 — translated from design/details-v5/details-v5.jsx ──
   Skips the design's TopNav (Layout/MiniDrawer provides shared chrome) and
   the prototype Tweaks panel. Phase 1 wiring: real project/agency/meta-model/
   matterport fetch (mirrors V2). The lower content panels (Description through
   Media) still use the Triton sample data — those are Phase 2-3. */

type ModeId = "meta" | "twin" | "route";
type TabId = "description" | "amenities" | "location" | "highlights" | "unit-details" | "media";

/* Description is stored as a JSON-encoded string with `{headline, lede, body, quote}`.
   Older / legacy projects may have plain text — we treat that as body only. */
type DescriptionBlock = { headline: string; lede: string; body: string; quote: string };
const parseDescription = (raw: string | undefined | null): DescriptionBlock => {
  if (!raw) return { headline: "", lede: "", body: "", quote: "" };
  try {
    const p = JSON.parse(raw);
    return {
      headline: typeof p?.headline === "string" ? p.headline : "",
      lede: typeof p?.lede === "string" ? p.lede : "",
      body: typeof p?.body === "string" ? p.body : "",
      quote: typeof p?.quote === "string" ? p.quote : "",
    };
  } catch {
    return { headline: "", lede: "", body: String(raw), quote: "" };
  }
};

/* Format an ISO date string (e.g. "2027-03-01T00:00:00.000Z") into the design's
   "Month YYYY" form (e.g. "March 2027"). Falls back to the raw string if it
   doesn't parse. Returns empty string for nullish input. */
const formatMonthYear = (raw: string | undefined | null): string => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
};

/* Full thousand-separated number — e.g. 5000 -> "5,000", 32500 -> "32,500".
   Never abbreviates to "K" since users want the precise value at a glance. */
const formatThousands = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n as number)) return "";
  return (n as number).toLocaleString("en-IN");
};

/* ── Entry loader (branded full-screen "preparing" overlay) ───
   Per design/details-v5/ENTRY_LOADER.md. Rendered while the page's critical
   fetches (project, agency, matterports, meta-models, amenity catalog) are
   in flight, so users see a polished branded transition instead of a blank
   or half-rendered page. Replaces the older plain "Loading…" placeholder. */
type EntryLoaderProps = {
  projectName: string;
  locationStr: string;
  agencyLogo: string;          // agency logo URL — shown inside the spinning ring
  fadingOut: boolean;          // true → start fade-out + then unmount
  /* Public share-link viewer extras (per SHAREABLE_PROJECT_LINKS_SPEC).
     When present, render a "Shared by ..." eyebrow above the project
     name so the buyer sees who sent the link the moment they land. */
  sharerName?: string;
  sharerRoles?: string[];
};

function EntryLoader({ projectName, locationStr, agencyLogo, fadingOut, sharerName, sharerRoles }: EntryLoaderProps) {
  const sharerInitials =
    (sharerName || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "•";
  const sharerNorm = (sharerRoles || []).map((r) =>
    String(r || "").toLowerCase().replace(/^agency_/, ""),
  );
  const sharerIsAgencySide = sharerNorm.some((r) =>
    ["agent", "business_admin", "system_admin", "aba", "asa"].includes(r),
  );
  const sharerLabel = sharerIsAgencySide ? "Shared by" : "Recommended by";
  return (
    <div className={"page-loader" + (fadingOut ? " page-loader--done" : "")} role="status" aria-live="polite">
      <div className="pl-inner">
        {agencyLogo && (
          <img className="pl-agency-logo" src={agencyLogo} alt="" aria-hidden="true" />
        )}
        {sharerName && (
          <div className="pl-sharer-pill" title={`${sharerLabel} ${sharerName}`}>
            <span className="pl-sps-avatar">{sharerInitials}</span>
            <span className="pl-sps-text">
              <span className="pl-sps-label">{sharerLabel}</span>
              <span className="pl-sps-name">{sharerName}</span>
            </span>
          </div>
        )}
        {projectName ? (
          <div className="pl-name">{projectName}</div>
        ) : (
          <div className="pl-name-skeleton" aria-label="Loading project" />
        )}
        {locationStr && <div className="pl-sub">{locationStr}</div>}
        <div className="pl-bar"><span /></div>
        <div className="pl-status">Preparing your experience</div>
      </div>
    </div>
  );
}

/* ── Guided Tour control (button + create-room modal) ──────────────────
   Sits beside Share in the subhead. STAFF ONLY and never rendered for a
   public/share viewer — a buyer landing on my.openskyvr.com must not see
   it (gated at the call site on `canHostGuidedTour`).

   v1 scope: create the room and hand back a forwardable join link. The
   in-room experience (A/V + synced canvas) is next: both tokens it needs
   now mint for real — rtc_token for Agora and ws_token for the VR sync —
   see the notes in api/guidedRoomActionsApi.js.

   Reuses the `.share-overlay` / `.share-modal` shell so the portal,
   backdrop and mobile breakpoints behave identically to the share modal;
   the form itself is `.gt-*`. */
/* Anyone who can be added to a room. `kind` drives the avatar colour and,
   backend-side, which entrance the person uses: `lead` takes the OTP path,
   `agent`/`asa` the Keycloak one. Everyone goes up in a single top-level
   `invitees` array — there's no longer a privileged primary lead. */
type GtPerson = {
  id: string;
  name: string;
  mobile?: string;
  /* Carried so the inline create form can detect a duplicate before
     posting — lead uniqueness is (agency, project, email, phone). */
  email?: string;
  kind: "lead" | "agent" | "asa";
};

/* Comparison keys for duplicate detection. Phone strips formatting and
   keeps the last 10 digits so +91-98503 02296 and 9850302296 match. */
const gtPhoneKey = (v?: string) => (v || "").replace(/\D/g, "").slice(-10);
const gtEmailKey = (v?: string) => (v || "").trim().toLowerCase();

/* Avatar colour is a CLASS in the redesigned modal (.gtm-av / .tm / .sa)
   rather than an inline value, so the gradients live with the rest of the
   styling. The old GT_KIND_COLOR map went with it. */
const GT_KIND_LABEL: Record<GtPerson["kind"], string> = {
  asa: "System Admin",
  agent: "Agent",
  lead: "Lead",
};

const gtInitials = (name: string) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

/* Ticked line in the "When you create the room" summary. */
const TickSm = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

/* ── "Add a new lead" ─────────────────────────────────────────────────
   Its own component, and that is the whole point of it being one.

   These four fields used to live in GuidedTourControl, which also renders
   the tour modal: the people list, the invited rail, the explainer. So
   every character the host typed re-rendered all of that — a few hundred
   elements — to update one input. The search box next to it was fixed by
   deferring the list; this form had no such escape, because the thing it
   was blocking on was the modal around it.

   State lives here now. A keystroke re-renders four inputs, and the tour
   modal behind the sheet does not hear about it at all. The parent is
   told once, when the host presses Create. */
/* ── The directory, remembered ────────────────────────────────────────
   Founder: "in gmeet it's instant, people get loaded very fast — can it
   be something like that?" It can, and the trick is not a faster request:
   it is not making the host wait for one. The list they saw last time is
   almost always still right, so it is shown immediately and refreshed
   behind them; if anything changed, the list updates in place.

   Kept in localStorage as well as memory, so it survives a reload and a
   fresh tab — the host opening the modal on a page they just loaded is
   the common case, and an in-memory cache would be empty exactly then.

   A day is deliberate. A lead added on another device appears on the next
   refresh after this one, which is a second, not a day — the age only
   decides when we stop trusting the cache enough to SHOW it first. */
const GT_DIR_KEY = "guidedRoom:directory:v1";
const GT_DIR_MAX_AGE = 24 * 60 * 60 * 1000;

const gtDirKey = (agencyId: string, agentId: string) =>
  `${GT_DIR_KEY}:${agencyId || "-"}:${agentId || "-"}`;

const gtDirRead = (agencyId: string, agentId: string): GtPerson[] | null => {
  try {
    const raw = localStorage.getItem(gtDirKey(agencyId, agentId));
    if (!raw) return null;
    const box = JSON.parse(raw);
    if (!box?.at || !Array.isArray(box.people)) return null;
    if (Date.now() - Number(box.at) > GT_DIR_MAX_AGE) return null;
    return box.people as GtPerson[];
  } catch { return null; }
};

const gtDirWrite = (agencyId: string, agentId: string, people: GtPerson[]): void => {
  try {
    localStorage.setItem(gtDirKey(agencyId, agentId), JSON.stringify({ at: Date.now(), people }));
  } catch { /* storage full or blocked — the list still works, just not instantly */ }
};

type NewLeadSheetProps = {
  /* Prefill from whatever the host had typed in the search box — they
     were already halfway through writing this person's name. */
  initialQuery: string;
  people: GtPerson[];
  saving: boolean;
  err: string;
  onClose: () => void;
  onCreate: (v: { first: string; last: string; mobile: string; email: string }) => void;
  onUseExisting: (p: GtPerson) => void;
};

const NewLeadSheet = React.memo(function NewLeadSheet({
  initialQuery, people, saving, err, onClose, onCreate, onUseExisting,
}: NewLeadSheetProps) {
  const startsWithDigit = /^\d/.test(initialQuery);
  const [first, setFirst] = useState(startsWithDigit ? "" : initialQuery);
  const [last, setLast] = useState("");
  const [mobile, setMobile] = useState(startsWithDigit ? initialQuery : "");
  const [email, setEmail] = useState("");

  /* The duplicate check reads the whole directory, so it follows the
     typing rather than blocking it — same reason as the search box. Lead
     uniqueness is (agency, project, email, phone), so a match on EITHER
     is worth surfacing: it is almost always the same person twice. */
  const dMobile = React.useDeferredValue(mobile);
  const dEmail = React.useDeferredValue(email);
  const dupe = React.useMemo<GtPerson | null>(() => {
    const ph = gtPhoneKey(dMobile);
    const em = gtEmailKey(dEmail);
    if (!ph && !em) return null;
    return (
      people.find(
        (p) =>
          p.kind === "lead" &&
          ((ph.length >= 10 && gtPhoneKey(p.mobile) === ph) || (!!em && gtEmailKey(p.email) === em)),
      ) || null
    );
  }, [dMobile, dEmail, people]);

  return (
    <div className="pdv3-root share-overlay gt-overlay gt-overlay--nested" onClick={onClose}>
      <div className="share-modal gt-modal gt-modal--sm" role="dialog" aria-modal="true"
           aria-label="Add a new lead"
           onClick={(e) => e.stopPropagation()}>
        <button className="share-modal-x" aria-label="Close" onClick={onClose}>
          <Icon.Close />
        </button>
        <div className="share-modal-head">
          <span className="share-modal-ico">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M19 8v6M22 11h-6" />
            </svg>
          </span>
          <div>
            <div className="share-modal-title">Add a new lead</div>
            <div className="share-modal-sub">
              They&apos;ll be created on the platform and added to this tour.
            </div>
          </div>
        </div>
        <div className="gt-body">
          <div className="gt-newlead-grid">
            <div>
              <label className="gt-label" htmlFor="nl-first">First name *</label>
              <input id="nl-first" className="gt-input" value={first}
                     onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div>
              <label className="gt-label" htmlFor="nl-last">Last name *</label>
              <input id="nl-last" className="gt-input" value={last}
                     onChange={(e) => setLast(e.target.value)} />
            </div>
          </div>

          <label className="gt-label" htmlFor="nl-mobile">Mobile number *</label>
          <input id="nl-mobile" className="gt-input" value={mobile} inputMode="tel"
                 onChange={(e) => setMobile(e.target.value)} />

          <label className="gt-label" htmlFor="nl-email">Email *</label>
          <input id="nl-email" className="gt-input" value={email} inputMode="email"
                 onChange={(e) => setEmail(e.target.value)} />

          {/* Surfaced while typing, not on submit — the host finds out
              before filling the rest of the form. */}
          {dupe && (
            <div className="gt-dupe">
              <div className="gt-dupe-t">{dupe.name} already exists</div>
              <div className="gt-dupe-s">
                Matching {dupe.mobile ? `mobile ${dupe.mobile}` : "email"} on this agency.
              </div>
              <button type="button" className="gt-dupe-add" onClick={() => onUseExisting(dupe)}>
                Add {dupe.name} to this tour instead
              </button>
            </div>
          )}

          {err && <div className="gt-err">{err}</div>}

          <div className="gt-newlead-act">
            <button type="button" className="gt-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="gt-cta gt-cta--sm"
                    disabled={saving || !!dupe}
                    onClick={() => onCreate({
                      first: first.trim(), last: last.trim(),
                      mobile: mobile.trim(), email: email.trim(),
                    })}>
              {saving ? "Saving…" : "Create & add"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});


function GuidedTourControl({
  projectId,
  agencyId,
  agentId,
  projectName,
  locationStr,
  configSummary,
}: {
  projectId: string;
  agencyId?: string;
  /* Set only when the viewer is an agent. Scopes the lead picker to their
     own book — an agent must not be able to open a room for somebody
     else's lead. ABA/ASA get the full agency list (agentId empty). */
  agentId?: string;
  projectName: string;
  /* The header's second line — "3 BHK · Kukatpally, Hyderabad". The
     project is the modal's title, so these identify WHICH project without
     needing a card in the rail duplicating it. */
  locationStr?: string;
  configSummary?: string;
}) {
  /* Publishes --sat/--sab/--kb and html[data-kb] on the document, which is
     what the modal's phone and tablet layers are built on. Mounted here
     rather than on the page, because this control is the thing that owns
     a full-screen surface on a phone — and the one the host types into
     with the keyboard up. */
  useViewportInsets();

  type Status = "idle" | "creating" | "ready" | "error";
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  /* Searchable pool + the people actually added to the room. Modelled on
     a calendar guest list rather than a single-select: a room can carry
     several leads (joint buyers), and a manager can sit in on a junior's
     tour. Who you may add is role-scoped — see loadPeople below. */
  const [people, setPeople] = useState<GtPerson[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  /* Hovering or touching the button is a good enough signal that the
     directory is about to be needed. Fetching then rather than on click
     hides most of the wait behind the gesture — the modal opens on a list
     that is already there instead of starting a round trip the host then
     watches. */
  const [peopleWanted, setPeopleWanted] = useState(false);
  const [query, setQuery] = useState("");
  const [invitees, setInvitees] = useState<GtPerson[]>([]);

  /* Which directories this viewer may browse — same matrix the people
     fetch enforces, surfaced as tabs so the host can flip between them
     instead of hunting through one mixed list. Agents only ever see
     leads, so they get no tab strip at all. */
  const viewerKinds = React.useMemo<GtPerson["kind"][]>(() => {
    const isABA =
      UserService.hasRole("agency_business_admin") || UserService.hasRole("super_admin");
    const isASA = !isABA && UserService.hasRole("agency_system_admin");
    if (isABA) return ["lead", "agent", "asa"];
    if (isASA) return ["lead", "agent"];
    return ["lead"];
  }, []);
  const [tab, setTab] = useState<GtPerson["kind"]>("lead");
  /* Scheduling and the host's free-text note were both pulled from the
     create modal — instant rooms only for now. The backend still supports
     `scheduled_at`, so this is a UI removal, not a capability one. */
  /* Inline "lead isn't in the list" form. Deliberately minimal — name and
     mobile are all a room needs; the full lead record can be enriched
     later in the Leads tab rather than blocking the tour being set up. */
  const [showNewLead, setShowNewLead] = useState(false);
  /* The sheet owns its fields, so closing it is all the clearing there
     is — it unmounts and takes them with it. */
  const closeNewLead = React.useCallback(() => { setShowNewLead(false); setNlErr(""); }, []);
  const [nlSaving, setNlSaving] = useState(false);
  const [nlErr, setNlErr] = useState("");
  /* Who is creating the lead — stamped on create so the backend derives
     source = "platform" (same contract AddLeadForm uses). */
  const [actorRole, setActorRole] = useState<"aba" | "asa" | "agent" | "">("");
  const [actorUserId, setActorUserId] = useState("");
  const [joinUrl, setJoinUrl] = useState("");
  const [joinCode, setJoinCode] = useState("");
  /* Needed to send the host into /room/:roomId. The join code identifies
     the room to a buyer; the host side is keyed on room_id. */
  const [roomId, setRoomId] = useState("");
  const [errMsg, setErrMsg] = useState("");
  /* The live tour standing in the way of this one, when the backend says
     there is one. Carries the project name so the refusal can name it. */
  /* ── One answer about this project's tour ──────────────────────────
     Was four: a `status` string, this claim, a second claim in
     localStorage, a `blockingRoom`, and a ref refereeing between them.
     Each fix moved one and broke another — see guidedTourMachine.ts for
     the full account and the tests. The create flow keeps `status`
     because it describes a REQUEST; everything about what is OPEN is the
     machine's, and the two no longer contradict each other. */
  const [tour, dispatchTour] = React.useReducer(gtReduce, gtInitialState);
  /* The host's own live room, if any (see below). Null = free to create.
     Seeded from localStorage on mount and re-verified against the backend,
     because a room ended in another tab would otherwise block this host
     forever.

     Note this is the HOST's room, not this project's — it may well belong
     to a different project, which is what `availability` sorts out. */
  const blockingRoom = tour.blocking;

  /* Identity for the claim key. The Keycloak subject is stable, unique and
     available synchronously; email is a fallback for a token that somehow
     lacks one. Empty means "don't block" — see hostAvailability. */
  const hostId = String(
    UserService.getTokenParsed()?.sub || UserService.getUseremail() || "",
  );

  /* The button asks the machine, in its own words. This used to recompute
     the same answer a second way — from the claim, the project id and the
     host id — which is how the button and the modal came to disagree in
     front of a founder: two derivations of one fact, updated at different
     moments. There is one now. */
  const liveHere = gtIsLiveHere(tour);
  const liveElsewhere = gtIsBlockedElsewhere(tour);
  const liveClaim = gtRoomToOpen(tour);
  const elsewhereName = liveClaim?.project_name || "another project";

  /* Open the host room in a NEW TAB rather than navigating. The project
     page is the host's working surface — they still need it to copy the
     link, add a lead, or check details while the tour runs. Replacing it
     with a full-screen room means a back-button round trip every time. */
  const openRoom = (rid: string) => {
    /* NAMED target, and deliberately without noopener/noreferrer.

       `_blank` opens a fresh tab on every click, so a host who pressed the
       button twice ended up with two tabs of the same room — two lobby
       pollers, and an "end tour" in one that leaves the other showing a
       dead room. A stable name makes the browser reuse and focus the tab
       that's already there.

       noopener would defeat that: with it set, any target name other than
       _top/_self/_parent is treated as _blank. Dropping it is safe here —
       the room is our own same-origin route, not third-party content. */
    const w = window.open(`/room/${rid}`, `openskyGuidedRoom_${rid}`);
    /* Focus can be refused (pop-up settings); the tab still opens. */
    if (w) { try { w.focus(); } catch (_e) { /* no-op */ } }
  };

  /* The localStorage claim is now a HINT, not an authority.

     It is written when a room is created and read here so the button is
     right on the first paint, before the server answers — but it can only
     ever speak for the browser that created the room, which is why a tour
     started on a laptop was invisible to the same host on their phone.
     Everything it finds is dispatched as a provisional SERVER_SAYS and
     overwritten by the real one a moment later. It never clears the
     machine: absence of a local claim is not evidence of absence. */
  const seedFromLocalClaim = React.useCallback(() => {
    if (!hostId) return;
    let stored: any = null;
    try {
      stored = JSON.parse(localStorage.getItem(gtActiveKeyFor(hostId)) || "null");
    } catch (_e) { stored = null; }
    if (!stored?.room_id) return;
    getGuidedRoom(stored.room_id)
      .then((r: any) => {
        if (!gtIsRoomLive(r)) {
          try { localStorage.removeItem(gtActiveKeyFor(hostId)); } catch (_e) { /* no-op */ }
          return;
        }
        const pid = String(r?.project_id || r?.room?.project_id || stored.project_id || "");
        dispatchTour({
          type: "SERVER_SAYS",
          room: {
            room_id: String(stored.room_id),
            join_code: String(stored.join_code || ""),
            project_id: pid,
            project_name: String(stored.project_name || ""),
          },
          isThisProject: !pid || pid === projectId,
        });
      })
      .catch((e: any) => {
        /* Clear on a DEFINITE miss only — 404/410, "this room is gone".
           Anything else (timeout, 5xx, offline) is this request's problem
           and the key stays. */
        if (gtIsRoomGone(e?.response?.status)) {
          try { localStorage.removeItem(gtActiveKeyFor(hostId)); } catch (_e) { /* no-op */ }
        }
      });
  }, [hostId, projectId]);

  useEffect(() => {
    seedFromLocalClaim();
    /* Another tab ending the room releases the claim, which lands here as
       a storage event — the fastest signal we get that a tour is over. */
    const onStorage = (e: StorageEvent) => {
      if (gtAffectsActiveRoom(e.key, hostId)) void checkHostBusy();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [hostId, seedFromLocalClaim]);

  const closeModal = () => setOpen(false);

  /* Marks the page while this modal is open, so the CSS can stop the
     walkthrough painting behind it. The modal covers it completely, and a
     hidden frame is throttled by the browser — which is the difference
     between the host's typing competing with a WebGL canvas and not. */
  useEffect(() => {
    document.body.classList.toggle("gt-modal-open", open);
    return () => document.body.classList.remove("gt-modal-open");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  /* Leads load lazily on first open — no reason to pull the agency's
     customer list on every project-page render.

     `leadsLoading` must NOT be a dependency, and the "already fetched"
     guard must not be state: setting loading state would re-run the
     effect, React would fire the previous run's cleanup, `cancelled`
     would flip, and the in-flight response would be discarded — the
     fetch succeeds but the leads never land. Hence the ref. */
  const peopleFetchedRef = React.useRef(false);
  /* Alive, not "this effect run is stale". The directory is fetched once
     and the fetch outlives the effect that started it: hovering the
     button begins it, opening the modal changes the dependencies, and a
     per-run `cancelled` flag then threw away the very results it was
     waiting for — while the re-run exited early because the fetch was
     already marked done. The list never arrived. */
  const peopleAliveRef = React.useRef(true);

  /* Re-ARMED on mount, not merely cleared on unmount. StrictMode mounts
     a component, unmounts it and mounts it again in development; the
     cleanup from that throwaway unmount set this to false and nothing
     ever set it back, so every fetch completed and then discarded its own
     result. The list never appeared, and only in development, which is
     the only place anyone was looking at it. */
  useEffect(() => {
    peopleAliveRef.current = true;
    /* Deliberately NOT cleared here: the fetch outlives this effect by
       design (it starts on hover and must survive the modal opening), and
       a StrictMode remount would otherwise kill it again. React discards
       state updates to unmounted components by itself — it no longer even
       warns — so there is nothing left for this flag to protect against
       that is worth losing the list for. */
    return undefined;
  }, []);
  useEffect(() => {
    if ((!open && !peopleWanted) || !agencyId || peopleFetchedRef.current) return undefined;
    peopleFetchedRef.current = true;
    /* Show what we had, straight away. The request still goes out; this
       only decides whether the host watches it. */
    const cached = gtDirRead(agencyId, agentId || "");
    if (cached && cached.length) {
      setPeople(cached);
      setPeopleLoading(false);
    } else {
      setPeopleLoading(true);
    }

    /* Who this viewer may put in a room (mirrors the role matrix used
       across Leads / Agents / Analytics):
         ABA / super → System Admins · any agent · leads
         ASA         → their own agents · leads
         Agent       → their own leads only                            */
    const isABA =
      UserService.hasRole("agency_business_admin") || UserService.hasRole("super_admin");
    const isASA = !isABA && UserService.hasRole("agency_system_admin");

    const nameOf = (r: any, fallback: string) =>
      [r?.first_name, r?.last_name].filter(Boolean).join(" ").trim() ||
      r?.name ||
      r?.mobile_number ||
      fallback;

    (async () => {
      const out: GtPerson[] = [];
      try {
        /* ── All at once, not one after another ──────────────────────
           These four requests used to await each other: leads, then the
           viewer's own profile, then agents, then system admins. Nothing
           in that chain depends on the one before it, so the host waited
           for the SUM of four round trips to a backend in another region
           — measured at about ten seconds on a normal connection, every
           time the modal opened. They now cost the slowest one.

           The profile call is skipped entirely when the id is already
           known: the page resolves it on load for the Guided Tour
           button, and asking twice was free only in the sense that
           nobody was billed for it. */
        const needProfile = !agentId && !actorUserId && (isABA || isASA);

        /* ── A first page that lands fast ────────────────────────────
           500 leads in one request is the floor on how quickly this can
           open, and it was about ten seconds. So ask for the newest 50 as
           well, in parallel, and paint those the moment they arrive —
           the full book replaces them a beat later, in place.

           The extra request is deliberate: a second query for 50 rows
           costs the backend almost nothing next to the one for 500, and
           it buys the host a list instead of a spinner. The newest leads
           are also the ones a tour is most likely to be for. */
        const leadsOf = (res: any): GtPerson[] =>
          ((res?.re_customer_list || []) as any[])
            .map((c) => ({
              id: String(c?._id ?? c?.id ?? ""),
              name: nameOf(c, "Unnamed lead"),
              mobile: c?.mobile_number || c?.phone_number || "",
              email: c?.email || c?.email_id || "",
              kind: "lead" as const,
            }))
            .filter((x) => !!x.id);

        let fullArrived = false;
        void getCustomerList(0, 50, agencyId, agentId || "")
          .then((quick: any) => {
            if (!peopleAliveRef.current || fullArrived) return;
            const first = leadsOf(quick);
            if (!first.length) return;
            setPeople((prev) => (prev.length ? prev : first));
            setPeopleLoading(false);
          })
          .catch(() => { /* the full request is the one that matters */ });

        const [cRes, aRes, sRes, meRes] = await Promise.all([
          getCustomerList(0, 500, agencyId, agentId || "").catch(() => null),
          (isABA || isASA) ? getAllAgent(0, 500, agencyId as any).catch(() => null) : Promise.resolve(null),
          isABA ? getSystemAdminList(agencyId).catch(() => null) : Promise.resolve(null),
          needProfile
            ? (isABA
              ? getClientManagerByEmailId(UserService.getUseremail()).catch(() => null)
              : getClientAdminByEmailId(UserService.getUseremail()).catch(() => null))
            : Promise.resolve(null),
        ]);

        fullArrived = true;
        out.push(...leadsOf(cRes));

        /* Who the viewer is, for the lead-origin stamp — and, for an ASA,
           the id their agents are filtered by below. */
        let myAsaId = isASA ? actorUserId : "";
        if (meRes && peopleAliveRef.current) {
          const m: any = meRes;
          if (isABA) {
            const abaId = String(m?.agency_business_admin?._id || m?.agency_business_admin?.id || "");
            if (abaId) { setActorRole("aba"); setActorUserId(abaId); }
          } else if (isASA) {
            myAsaId = String(m?.agency_system_admin?._id || m?.agency_system_admin?.id || "");
            if (myAsaId) { setActorRole("asa"); setActorUserId(myAsaId); }
          }
        }

        for (const a of (aRes as any)?.re_agent_list || []) {
          const id = String(a?._id ?? a?.id ?? "");
          if (!id) continue;
          if (isASA) {
            const rawOwner = a?.assigned_asa_id ?? a?.asa_id;
            const owner = rawOwner ? String(Array.isArray(rawOwner) ? rawOwner[0] : rawOwner) : "";
            if (!myAsaId || owner !== myAsaId) continue;   // not my downline
          }
          out.push({ id, name: nameOf(a, "Agent"), mobile: a?.mobile_number || "", kind: "agent" });
        }

        // Key is `agency_system_admin_list` — NOT the re_-prefixed form the
        // other list endpoints use. Confirmed against the OpenAPI schema
        // (REAgencySystemAdminListResponse); guessing it cost an empty tab.
        const sList = (sRes as any)?.agency_system_admin_list || (Array.isArray(sRes) ? sRes : []);
        for (const sa of Array.isArray(sList) ? sList : []) {
          const id = String(sa?._id ?? sa?.id ?? "");
          if (id) {
            out.push({ id, name: nameOf(sa, "System Admin"), mobile: sa?.mobile_number || "", kind: "asa" });
          }
        }
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error("[GuidedTour] people list failed", e?.response?.status, e);
        peopleFetchedRef.current = false; // let the next open retry
      }
      if (!peopleAliveRef.current) return;
      /* An empty result after a failure must not wipe a good cached list
         off the screen — the host would watch people disappear. */
      if (out.length || !cached?.length) {
        setPeople(out);
        if (out.length) gtDirWrite(agencyId, agentId || "", out);
      }
      setPeopleLoading(false);
    })();

    return undefined;
  }, [open, peopleWanted, agencyId, agentId]);

  /* Capacity model (revised 2026-08-06).

     ── The distinction that matters ──
     CAPTURING a lead and OCCUPYING A SEAT are different things, and the
     earlier model conflated them with a `MAX_CONVERTED_LEADS = 5` cap.

     There is deliberately **no cap on lead conversion**. A forwarded link
     is a distribution channel — the buyer sends it to their brother, who
     sends it to a colleague — and every person who verifies their mobile
     becomes an attributed lead whether or not they ever enter the room.
     Capping that would throttle the most valuable thing the feature does.

     The cap is on people IN THE ROOM AT ONCE, which is a genuine limit:
     Agora's interactive tier, and one host admitting through a lobby
     mid-tour. The lobby is the natural gate — beyond capacity the host
     simply doesn't admit, and those people are still captured as leads.

     There's no separate "lead pass": someone a buyer brings along forwards
     the MAIN link, OTPs in, and gets their own lead record and
     attribution. Family are different — they arrive on a guest pass, need
     no OTP, and never create a lead. */
  const MAX_LEADS = 2;
  const MAX_STAFF = 2;
  const MAX_GUEST_PASSES_PER_LEAD = 4;
  /* An agent has no colleagues to invite — leads are all they can add — so
     the Team row of the seat meter is hidden for them entirely. */
  const canInviteStaff = viewerKinds.length > 1;
  /* The "around 13 in the room at once" line is gone, and so is the number
     behind it. It only ever counted INVITED seats — host + buyers +
     colleagues + their family — and the room has no such ceiling: anyone a
     forwarded link reaches can verify and be admitted, with no cap. */

  const invitedIds = React.useMemo(
    () => new Set(invitees.map((p) => p.id)),
    [invitees],
  );
  const leadCount = invitees.filter((p) => p.kind === "lead").length;
  const staffCount = invitees.length - leadCount;
  const leadsFull = leadCount >= MAX_LEADS;
  const staffFull = staffCount >= MAX_STAFF;
  /* Caps are per-kind, not a single headcount — a room is a household
     tour, so two buyers and two colleagues is the shape. Only "both full"
     closes the picker entirely. */
  const kindFull = (k: GtPerson["kind"]) => (k === "lead" ? leadsFull : staffFull);
  const primaryLead = invitees.find((p) => p.kind === "lead") || null;

  /* The list follows the typing instead of blocking it. React keeps the
     input responsive and re-filters at a lower priority, so a keystroke
     paints immediately even when the directory is large and the modal is
     a big tree to reconcile. The visible cost is that the list can trail
     the caret by a frame, which nobody notices; the alternative is what
     the host noticed. */
  const deferredQuery = React.useDeferredValue(query);

  const filteredPeople = React.useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    // Already-added people drop out of the results; the active tab scopes
    // the directory. Typing searches within the current tab only.
    const pool = people.filter((p) => !invitedIds.has(p.id) && p.kind === tab);
    if (!q) return pool.slice(0, 60);
    return pool
      .filter((p) => `${p.name} ${p.mobile || ""}`.toLowerCase().includes(q))
      .slice(0, 60);
  }, [people, deferredQuery, invitedIds, tab]);

  /* How many people each tab holds, shown on the tab itself. Counted off
     the WHOLE directory, not the filtered view — the number answers "how
     big is my book", so it must not move while the host types a search.
     Already-invited people still count; they're in the directory. */
  const kindCounts = React.useMemo(() => {
    const c: Record<string, number> = { lead: 0, agent: 0, asa: 0 };
    people.forEach((p) => { c[p.kind] = (c[p.kind] || 0) + 1; });
    return c;
  }, [people]);

  const addPerson = (p: GtPerson) => {
    if (kindFull(p.kind)) return;   // per-kind cap, not a shared headcount
    setInvitees((prev) => [...prev, p]);
    setQuery("");   // clear so the next person can be searched straight away
  };
  const removePerson = (id: string) =>
    setInvitees((prev) => prev.filter((p) => p.id !== id));

  const create = async () => {
    if (!projectId || !primaryLead) return;
    setStatus("creating");
    setErrMsg("");
    try {
      const payload: any = {
        project_id: projectId,
        /* The whole invite list, one flat array — `primary_lead_id` is gone
           and every participant is an {id, kind} entry. `kind` is what
           splits the two entrances backend-side: `lead` takes the OTP path
           and auto-admits as an invitee, `agent`/`asa` take the Keycloak
           path and skip the lobby entirely. */
        invitees: invitees.map((p) => ({ id: p.id, kind: p.kind })),
        /* Always instant — the Schedule option is out of the UI for now.
           `scheduled_at` is simply never sent. */
        instant: true,
        settings: {
          /* Always true — no longer a host decision, see the create modal.
             Still sent explicitly rather than omitted, so the backend
             default can never silently flip it off. */
          allow_guest_passes: true,
          /* ── RECORDING: CAPABILITY AT CREATE, DECISION IN THE ROOM ──
             This used to be omitted, on the reasoning that recording is a
             live action rather than a property of the room's setup. True
             as product intent, wrong as an instruction to the backend:
             `recording/start` refuses with 400 "Recording is switched off
             for this room (settings.recording_enabled)" and the default is
             False, so no room created here could EVER be recorded. The
             join page's consent notice reads the same flag off the public
             preview, so it never appeared either. Verified end to end on
             2026-09-07 — the notice only rendered after the flag was
             flipped by hand.

             So the flag now means "this room MAY be recorded", which is
             what it has to mean for consent to work at all: a notice shown
             only after the host presses record reaches nobody who joined
             before. Whether a recording actually happens stays a live
             decision in the room, and the notice is worded accordingly. */
          recording_enabled: true,
          /* Names mirrored into settings. `Invitee` carries id and kind
             only, so this is the only place the host's chosen labels
             survive the round trip — the host page reads them back to
             render the invite list without re-resolving the directory. */
          invitees: invitees.map((p) => ({ id: p.id, kind: p.kind, name: p.name })),
        },
      };
      // No experience_id — a room covers the whole project, and the host
      // picks which experience to walk once they're inside.
      const data: any = await createGuidedRoom(payload);
      const code = data?.join_code || data?.room?.join_code || "";
      const rid = data?.room_id || data?.room?.room_id || "";
      setJoinCode(code);
      setRoomId(rid);
      /* Create returns the HOST's own participant credential, and this is
         the only time it's ever issued — it isn't recoverable server-side.
         The admit/end controls authorise off the Keycloak token, so the
         host room works without it — but the room's VOICE does not: this
         is what buys the Agora token, so a host whose browser never
         stored it lands in a silent room. Losing it costs voice in this
         browser and nothing else. */
      const hs = data?.host_session_id;
      const hsec = data?.host_session_secret;
      if (rid && hs && hsec) {
        try {
          localStorage.setItem(
            gtHostCredKeyFor(rid),
            JSON.stringify({ session_id: hs, session_secret: hsec, room_id: rid }),
          );
        } catch (_e) { /* private mode — RTC will re-issue later */ }
      }
      /* Claim the HOST, not the project. Until this room ends, the Guided
         Tour button becomes "Tour live" here and "you're already hosting"
         on every other project — which is what the backend enforces.
         The project is stored so those other pages can name it. */
      if (rid) {
        const claim: GtActiveClaim = {
          room_id: rid,
          join_code: code,
          project_id: projectId,
          project_name: projectName || "",
        };
        if (hostId) {
          try {
            localStorage.setItem(gtActiveKeyFor(hostId), JSON.stringify(claim));
          } catch (_e) { /* non-fatal — worst case a second room is attempted */ }
        }
        /* CREATED outranks any poll in flight: the response is newer than
           anything the server was asked before the room existed. */
        dispatchTour({ type: "CREATED", room: claim as any });
      }
      /* Hand the join page the real project, for local development only.
         Until the public preview endpoint exists, /visit/<code> can't
         resolve a join code to anything — this browser can, because it
         just created the room. See localPreviewKeyFor. */
      if (code) {
        try {
          localStorage.setItem(
            gtLocalPreviewKeyFor(code),
            JSON.stringify({
              project_id: projectId,
              project_name: projectName || "",
              host_name: hostName || "",
              host_first_name: firstNameOf(hostName || ""),
            }),
          );
        } catch (_e) { /* private mode — the join page just shows less */ }
      }
      /* Prefer a backend-issued URL. Until `visit.openskyvr.com` DNS is
         live we fall back to the current origin so the link is testable
         locally and on stage. */
      setJoinUrl(data?.join_url || (code ? `${window.location.origin}/visit/${code}` : ""));
      setStatus(code ? "ready" : "error");
      if (!code) setErrMsg("Room was created but no join code came back.");
    } catch (e: any) {
      setStatus("error");
      /* 409 is a room-conflict, not a failure: either this host is already
         running a tour somewhere, or the buyer is on someone else's. Both
         need plain words and a way forward, not a raw `detail` string.
         Re-verify too — a host-busy 409 means a claim exists that this
         browser doesn't know about (created on another device). */
      const conflict = gtReadConflict(e);
      if (conflict) {
        const message = gtConflictMessage(conflict, primaryLead?.name);
        setErrMsg(message);
        if (conflict === "buyer_busy") {
          dispatchTour({ type: "FAILED", message });
        } else {
          /* The 409 carries the clashing room's id, and that is the only
             account of it that crosses browsers — the local claim speaks
             only for the browser that made the room. Name the project and
             hand the room over. */
          void (async () => {
            const fromError = gtReadConflictRoom(e);
            if (fromError) {
              let name = "";
              try {
                const r: any = await getGuidedRoom(fromError.room_id);
                name = String(r?.project_name || r?.room?.project_name || "").trim();
              } catch { /* a name is a nicety; the block is not */ }
              dispatchTour({
                type: "CONFLICT",
                room: {
                  room_id: fromError.room_id,
                  join_code: fromError.join_code,
                  project_id: "",
                  project_name: name,
                },
                message: name
                  ? `Your tour for ${name} is still open. End it there, or open it below.`
                  : message,
              });
              if (name) {
                setErrMsg(`Your tour for ${name} is still open. End it there, or open it below.`);
              }
              return;
            }
            dispatchTour({ type: "CONFLICT", room: null, message });
            void checkHostBusy();
          })();
        }
      } else {
        setErrMsg(e?.response?.data?.detail || e?.message || "Could not create the room.");
      }
    }
  };

  /* Per-person messages are built further down, once the emoji table and
     the host's name exist. Each invitee row links straight to WhatsApp
     with their own wording — there is no generic "share" message, because
     buyers and colleagues must never receive the same link. */

  /* Per-person WhatsApp, from the host's OWN number.
     Not a bulk send: that needs the WhatsApp Business API, approved
     message templates and per-message pricing, and it would arrive from a
     business shortcode. In Indian real estate a buyer answers the agent
     they've spoken to, not a brand — so one tap per person, sent as them,
     converts better and ships today.

     WhatsApp wants a bare international number: no +, no spaces, no
     dashes. Assumes +91 when the stored number is a local 10-digit one. */
  const waNumber = (mobile?: string): string => {
    const d = (mobile || "").replace(/\D/g, "");
    if (!d) return "";
    if (d.length === 10) return `91${d}`;
    /* 0-prefixed local format (0XXXXXXXXXX) — strip the trunk digit. */
    if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;
    return d;
  };

  /* WhatsApp markup: *bold*, _italic_. Kept minimal — over-formatted
     messages read as marketing blasts, which is the opposite of what a
     personal invite from your own agent should feel like.

     Emoji are assembled at RUNTIME from code points. Literal emoji and
     \u escapes both render as the replacement character — the build
     pipeline mangles non-ASCII in the bundle. Same fix the share-link
     messages already use; keeps source and bundle pure ASCII.
     BMP punctuation (— °) survives fine, so only emoji need this. */
  const cp = (...c: number[]) => String.fromCodePoint(...c);
  const GT_E = {
    home: cp(0x1f3e1),   // house with garden
    cal: cp(0x1f4c5),    // calendar — single code point, no variation selector
    pin: cp(0x1f4cd),    // round pushpin
    link: cp(0x1f517),   // link
    phone: cp(0x1f4f1),  // mobile phone
    family: cp(0x1f46a), // family — NOT the man+woman+girl ZWJ sequence, which
                         // needs three code points joined by U+200D and is the
                         // most fragile thing to put in a message pipeline
    cam: cp(0x1f3a5),    // movie camera
    key: cp(0x1f511),    // key
    person: cp(0x1f464), // bust in silhouette
    people: cp(0x1f465), // busts in silhouette
  };

  /* The host is whoever is logged in — the same name shown on the
     organizer row at the bottom of the invitee list. */
  const hostName = (UserService.getFullName && UserService.getFullName()) || "";

  const firstNameOf = (full: string) => (full || "").trim().split(/\s+/)[0] || "";

  /* The buyer's invite. Longer and warmer on purpose: this arrives cold on
     a phone, often from a number they've spoken to once, and it has to do
     three jobs — say what it is, say what's expected of them, and remove
     the fear that it's complicated. Hence "nothing to install" and the
     one-line note about verifying their number: both are the objections
     that stop people tapping. */
  const leadMessage = (p: GtPerson | null): string => {
    /* Full name, not just the first — buyers are addressed formally in
       Indian real estate, and the record's name is what they gave us. */
    const name = p ? (p.name || "").trim() : "";
    const proj = projectName || "the project";
    return [
      `${GT_E.home} *${proj}* — your private guided tour`,
      "",
      name ? `Hi ${name},` : "Hi,",
      "",
      `I'd like to walk you through ${proj} live. We'll explore it together — `
        + `I'll take you through the homes, the clubhouse and the layout, and you `
        + `can ask me anything as we go.`,
      "",
      locationStr ? `${GT_E.pin} ${locationStr}` : "",
      hostName ? `${GT_E.person} Your advisor: ${hostName}` : "",
      "",
      /* URL alone on its own line, no emoji prefix. WhatsApp linkifies a
         bare URL reliably; an emoji sharing the line suppresses it on some
         clients. (A localhost URL never linkifies anywhere — no TLD — so
         this only looks plain in local dev.) */
      `${GT_E.link} Your tour link:`,
      joinUrl,
      "",
      `${GT_E.phone} Works on your phone, tablet or laptop — nothing to download.`,
      /* Two DIFFERENT paths, deliberately spelled out — conflating them is
         what made the earlier "same link" line wrong:
           family  → a guest pass the buyer mints from inside the room
                     (?g=<token>, no OTP, first name only, no lead record)
           anyone else → opens THIS link, OTPs in, becomes a new lead with
                     their own attribution
         No number on the second line: forwarding is a distribution channel
         and we don't cap it. Quoting a limit would discourage the exact
         behaviour we want. */
      `${GT_E.family} Bring your family — once you're in, you can invite up to `
      + `${MAX_GUEST_PASSES_PER_LEAD} of them to join from anywhere. They only `
      + `enter a first name, nothing else.`,
      `${GT_E.people} Anyone else who should see it is welcome — just forward `
      + `this link.`,
      "",
      `Just tap the link and verify your number once. See you there!`,
      /* Signed off by first name — the message already arrives from the
         agent's own WhatsApp, so the full name twice would read like a
         template rather than a note from a person. */
      hostName ? `— ${firstNameOf(hostName)}` : "",
    ].filter((l) => l !== "").join("\n");
  };

  /* Colleagues get the HOST-side link and a shorter, plainer note — they
     sign in with their OpenSky email rather than an OTP. Sending staff to
     /visit would push them through the buyer flow and create a lead row
     for our own team member. */
  const staffMessage = (p: GtPerson): string => {
    const name = firstNameOf(p.name);
    const proj = projectName || "this project";
    const staffUrl = roomId ? `${window.location.origin}/room/${roomId}` : joinUrl;
    return [
      name ? `Hi ${name},` : "Hi,",
      "",
      `${GT_E.cam} Sitting in on a guided tour of *${proj}*.`,
      hostName ? `${GT_E.person} Hosted by ${hostName}` : "",
      "",
      `${GT_E.link} Join here:`,
      staffUrl,
      "",
      `${GT_E.key} Sign in with your OpenSky email — no OTP needed.`,
    ].filter((l) => l !== "").join("\n");
  };

  const messageFor = (p: GtPerson): string =>
    (p.kind === "lead" ? leadMessage(p) : staffMessage(p));

  /* api.whatsapp.com/send, NOT wa.me — wa.me's redirect step mangles
     4-byte/astral emoji into the replacement char while BMP characters
     survive, which is why a message can look fine in the preview and
     arrive broken. Same reason ShareControl uses this endpoint. */
  const waHrefFor = (p: GtPerson): string => {
    const n = waNumber(p.mobile);
    const text = encodeURIComponent(messageFor(p));
    return n
      ? `https://api.whatsapp.com/send?phone=${n}&text=${text}`
      : `https://api.whatsapp.com/send?text=${text}`;
  };

  const reset = () => {
    dispatchTour({ type: "RESET" });
    setStatus("idle"); setJoinUrl(""); setJoinCode(""); setRoomId(""); setErrMsg("");
    setEnteredAt(null);
    setInvitees([]); setQuery("");
    setShowNewLead(false); setNlErr("");
  };

  /* Reopening after a finished tour must NOT show the old room's link —
     it's dead, and the host would copy it and send it. Reset only from the
     completed state: closing mid-setup keeps the draft, which is the more
     common accident and the more annoying one to redo.

     This used to be the "Create another room" button's job. That button is
     gone (a host can only have one live room), so the reset moved here. */
  /* ── Is a room of mine already open, anywhere? ─────────────────────
     The host credential in localStorage answers this only for the browser
     that created the room: a tour started on a laptop is invisible to the
     same person on their phone, and the project page cheerfully offers a
     second room that the backend then refuses.

     The server has always been able to answer it — GET
     /views/guided_room/host/{id} lists a host's own rooms. It just has to
     be asked with the host's INTERNAL id, not their Keycloak subject,
     which is why an earlier attempt at this got a 403 and was abandoned.

     Asked when the modal opens, because that is when the id is resolved
     and before the host has filled anything in. Being told after you
     picked your buyers is being told too late. */
  /* Who this viewer is, resolved on PAGE LOAD rather than when the modal
     opens. The button's state depends on it, and a button that only tells
     the truth after you press it is not telling you anything. One profile
     call; the directory effect below reuses whatever lands here. */
  useEffect(() => {
    if (agentId || actorUserId) return undefined;
    let cancelled = false;
    void (async () => {
      const isABA = UserService.hasRole("agency_business_admin") || UserService.hasRole("super_admin");
      const isASA = !isABA && UserService.hasRole("agency_system_admin");
      try {
        if (isABA) {
          const me: any = await getClientManagerByEmailId(UserService.getUseremail());
          const id = String(me?.agency_business_admin?._id || me?.agency_business_admin?.id || "");
          if (id && !cancelled) { setActorRole("aba"); setActorUserId(id); }
        } else if (isASA) {
          const me: any = await getClientAdminByEmailId(UserService.getUseremail());
          const id = String(me?.agency_system_admin?._id || me?.agency_system_admin?.id || "");
          if (id && !cancelled) { setActorRole("asa"); setActorUserId(id); }
        }
      } catch { /* no identity, no claim — the backend stays the authority */ }
    })();
    return () => { cancelled = true; };
  }, [agentId, actorUserId]);

  /* ── A window onto the machine, off production ──────────────────────
     Four bugs in this area were chased by reasoning about what the code
     ought to be doing. The values themselves settle it in one line:

         window.__guidedTour

     Shows the phase, the room the machine believes is open, and the two
     ids whose comparison decides "is this tour mine or someone else's" —
     which is precisely where a mismatch would make a tour on THIS project
     look like a tour blocking it from another. */
  useEffect(() => {
    if (guidedTourEnabled() === false) return;
    try {
      (window as any).__guidedTour = {
        phase: tour.phase,
        room: tour.room,
        blocking: tour.blocking,
        pageProjectId: projectId,
        thisModalsRoomId: roomId,
        status,
        hostId: agentId || actorUserId || "(unresolved)",
      };
    } catch { /* nothing here is load-bearing */ }
  }, [tour, projectId, roomId, status, agentId, actorUserId]);

  const checkHostBusy = React.useCallback(async () => {
    const hostId = agentId || actorUserId;
    if (!hostId) return;
    try {
      const res: any = await listHostGuidedRooms(hostId);
      const rooms: any[] = Array.isArray(res?.rooms) ? res.rooms : [];

      /* Two different questions, and conflating them broke the modal.

         RUNNING — is any room of mine open? That is what the claim means,
         and the claim is what tells this page a tour exists. Dropping it
         made `tourEnded` (status ready && no claim) true the moment a
         host created a room, so the modal announced "This tour has
         ended" over the join links it had just minted.

         BLOCKING — is one of them in the way of a NEW room here? That
         excludes the room this modal just created, because telling the
         host to go end the thing they made thirty seconds ago is the
         product arguing with itself.

         The server's cap is LOBBY_OPEN or LIVE, per host and regardless
         of project (routers.py `_running_room`); SCHEDULED does not
         block, so counting it would refuse a room the backend allows. */
      /* The server's cap is LOBBY_OPEN or LIVE, per host and regardless of
         project (routers.py `_running_room`); SCHEDULED does not block, so
         counting it would refuse a room the backend allows. */
      const running = rooms.find((r) => {
        const st = String(r?.state || "").toUpperCase();
        return st === "LOBBY_OPEN" || st === "LIVE";
      });

      if (!running) {
        /* What "nothing is open" MEANS depends on where we are, and that
           judgement belongs to the machine: a modal showing join links has
           just watched its tour end; a page that was never in one is free. */
        dispatchTour({ type: "SERVER_SAYS", room: null, isThisProject: false });
        return;
      }

      const pid = String(running.project_id || "");
      const isThisProject = !!pid && pid === projectId;
      let name = isThisProject ? projectName : "";
      if (!name) {
        try {
          const full: any = await getGuidedRoom(String(running.room_id));
          name = String(full?.project_name || full?.room?.project_name || "").trim();
        } catch { /* a name is a nicety; the room is not */ }
      }

      dispatchTour({
        type: "SERVER_SAYS",
        room: {
          room_id: String(running.room_id),
          join_code: String(running.join_code || ""),
          project_id: pid,
          project_name: name,
        },
        /* The room this modal just made counts as "this project's" however
           the ids compare — it is the one on screen. */
        isThisProject: isThisProject || String(running.room_id) === roomId,
      });
    } catch { /* a 403 or a dropped packet must not block a legitimate host */ }
  }, [agentId, actorUserId, projectId, projectName, roomId]);

  /* ── Is a room of mine already open, anywhere? ─────────────────────
     The host credential in localStorage answers this only for the browser
     that created the room: a tour started on a laptop is invisible to the
     same person on their phone, and the project page cheerfully offers a
     second room the backend then refuses.

     The server has always been able to answer it — GET
     /views/guided_room/host/{id} lists a host's own rooms. It just has to
     be asked with the host's INTERNAL id, not their Keycloak subject,
     which is why an earlier attempt at this got a 403 and was abandoned.

     Asked when the modal opens, and again whenever this tab comes back to
     the front — the room runs in ANOTHER tab, so returning from it is
     precisely the moment the answer has just changed. */
  useEffect(() => {
    void checkHostBusy();
    const onBack = () => { if (document.visibilityState === "visible") void checkHostBusy(); };
    window.addEventListener("focus", onBack);
    document.addEventListener("visibilitychange", onBack);
    return () => {
      window.removeEventListener("focus", onBack);
      document.removeEventListener("visibilitychange", onBack);
    };
  }, [checkHostBusy]);

  const openModal = () => {
    if (status === "ready") reset();
    setOpen(true);
  };

  /* The room this modal created has since ended — almost always because
     the host ended it in the room tab, which releases the claim and fires
     a `storage` event straight back here.

     Detected as "we made a room, and the host no longer holds a live
     claim" rather than by polling the room: verifyActiveRoom already does
     that work on storage, focus and visibility change, and it now only
     gives up on a definite 404/410. Without this the modal keeps offering
     "Go to tour" and a link that leads nowhere. */
  /* The machine's word, not "ready and no claim" — that formulation is
     exactly what announced "This tour has ended" over join links a
     moment after they were minted, because a poll had cleared the claim
     the create had just set. */
  const tourEnded = gtHasEnded(tour);

  /* Create a lead that isn't in the list yet, straight from the picker.
     Name + mobile only — enough to open a room and send the link; the
     full record can be filled in later from the Leads tab rather than
     blocking the host mid-flow. */
  /* Live duplicate check against the loaded lead pool. Runs as the host
     types rather than on submit, so they find out before filling the
     whole form. Lead uniqueness is (agency, project, email, phone), so a
     match on EITHER is worth surfacing — it's almost always the same
     person entered twice. */

  /* Called by NewLeadSheet, which owns the fields — see the note on that
     component for why they do not live here any more. */
  const createLeadInline = async (v: { first: string; last: string; mobile: string; email: string }) => {
    const { first, last, mobile, email } = v;
    if (!first || !last || !mobile || !email) {
      setNlErr("First name, last name, mobile and email are all required.");
      return;
    }
    if (!/^\d{10,15}$/.test(mobile.replace(/\D/g, ""))) {
      setNlErr("That mobile number doesn't look right.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNlErr("That email doesn't look right.");
      return;
    }
    setNlSaving(true); setNlErr("");
    try {
      // Agents stamp themselves; ABA/ASA ids resolve when the pool loads.
      const aRole = agentId ? "agent" : actorRole;
      const aId = agentId || actorUserId;
      /* Field names are the backend's, not ours. `email_id` and
         `country_code` are both REQUIRED by POST /actions/re_customer:
         sending `email` and omitting the code answered 422 every time,
         so this form could never create a lead. Verified against stage
         on 2026-10-01 — the 422 named all three fields. The canonical
         shape is Leads/AddLeadForm.tsx; keep them in step. */
      const payload: any = {
        first_name: first,
        last_name: last,
        country_code: "+91",
        mobile_number: mobile,
        email_id: email,
        agency_id: agencyId,
        ...(agentId ? { agent_id: [agentId] } : {}),
        // Backend derives source = "platform" from the actor stamp.
        ...(aRole && aId ? { actor_role: aRole, actor_user_id: aId } : {}),
      };
      const res: any = await createReCustomer(payload);
      const created = res?.re_customer || res?.customer || res;
      const id = String(created?._id ?? created?.id ?? "");
      if (!id) throw new Error("Lead was created but no id came back.");
      const person: GtPerson = {
        id,
        name: [first, last].filter(Boolean).join(" "),
        mobile,
        kind: "lead",
      };
      setPeople((prev) => [person, ...prev]);   // so it's searchable next time
      addPerson(person);                         // and straight into the room
      closeNewLead();
    } catch (e: any) {
      setNlErr(gtApiMessage(e, "Could not create the lead."));
    }
    setNlSaving(false);
  };

  /* ── State the redesigned modal adds ─────────────────────────────── */

  /* "How people get in" starts collapsed. Expanded, it BORROWS height
     from the people list rather than pushing the footer — see the .list
     min-height override in the CSS. */
  const [howOpen, setHowOpen] = useState(false);

  /* Which row is mid-shake. Clicking a capped-out row has to do
     something — failing silently reads as a broken button. */
  const [nopeId, setNopeId] = useState("");
  const bump = (id: string) => {
    setNopeId(id);
    setTimeout(() => setNopeId((cur) => (cur === id ? "" : cur)), 420);
  };

  /* Copy feedback per link group — buyers and team have separate URLs, so
     one shared "Copied" flag lit up the wrong button. */
  const [copiedKey, setCopiedKey] = useState("");
  const copyGroup = (key: string, url: string) => {
    if (!url) return;
    try { navigator.clipboard.writeText(url); } catch (_e) { /* no-op */ }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((cur) => (cur === key ? "" : cur)), 1500);
  };

  /* "Live for mm:ss". Starts when the host actually enters the room, not
     when it was created — a room sitting unopened is not a live tour.
     NOTE: this is the local clock. The design asks for server truth
     (`room.startedAt`); until the backend returns it, a host who reloads
     mid-tour restarts the count. Cosmetic, and flagged rather than faked. */
  const [enteredAt, setEnteredAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enteredAt || tourEnded) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [enteredAt, tourEnded]);
  const liveFor = (() => {
    if (!enteredAt) return "";
    void tick;                                   // re-render driver
    const s = Math.max(0, Math.floor((Date.now() - enteredAt) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  })();

  const enterRoom = () => {
    if (!roomId) return;
    if (!enteredAt) setEnteredAt(Date.now());
    dispatchTour({ type: "ENTERED" });
    openRoom(roomId);
  };

  /* Search-aware "New lead" label, per the design: typing a number or a
     name carries it into the create form. */
  const trimmedQuery = query.trim();
  const newLeadLabel = trimmedQuery ? `Add "${trimmedQuery}"` : "New lead";
  /* The sheet prefills itself from what was typed in the search box. */
  const startNewLead = () => { setNlErr(""); setShowNewLead(true); };

  /* Selected people, split by slot. The rail shows two groups; the caps
     are per-kind, so they can't share one list. */
  const pickedLeads = invitees.filter((p) => p.kind === "lead");
  const pickedTeam = invitees.filter((p) => p.kind !== "lead");

  /* The two doors. Buyers get the short public link; colleagues get the
     host room and sign in with Keycloak — sending a colleague the buyer
     link would OTP them in and file them as a lead. Unchanged from what
     we shipped; the design's shared `t`-code is deliberately not used. */
  const buyerUrl = joinUrl;
  const teamUrl = roomId ? `${window.location.origin}/room/${roomId}` : "";

  return (
    <>
      {/* One room per HOST at a time — the backend's rule, so the button
          has to answer for the host's other projects too. Three states,
          and the third is why this isn't a boolean: the tour blocking you
          may belong to a DIFFERENT project, and "rejoin" would silently
          take you somewhere else. */}
      <button
        className={
          "gt-btn" + (open ? " gt-btn--on" : "") +
          (liveHere ? " gt-btn--live" : "") +
          (liveElsewhere ? " gt-btn--elsewhere" : "")
        }
        onClick={() => (liveClaim ? openRoom(liveClaim.room_id) : openModal())}
        onMouseEnter={() => setPeopleWanted(true)}
        onFocus={() => setPeopleWanted(true)}
        onTouchStart={() => setPeopleWanted(true)}
        aria-label={
          liveHere ? "Rejoin the live guided tour"
            : liveElsewhere ? "Open your live guided tour on another project"
              : "Start a guided tour"
        }
        title={
          liveHere
            ? "Your guided tour for this project is live — end it before starting another"
            : liveElsewhere
              ? `Your guided tour for ${elsewhereName} is still live. You can only run one at a time — end it before starting one here.`
              : undefined
        }
      >
        {liveHere || liveElsewhere ? (
          <span className="gt-btn-dot" aria-hidden="true" />
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="6" width="13" height="12" rx="2" />
            <path d="M15 10.5 22 7v10l-7-3.5z" />
          </svg>
        )}
        <span className="gt-btn-text">
          {liveHere ? "Tour live — rejoin"
            : liveElsewhere ? "Live on another project"
              : "Guided Tour"}
        </span>
      </button>

      {open && ReactDOM.createPortal(
        <div className="pdv3-root gtm-scrim" onClick={closeModal}>
          <div
            className={"gtm" + (howOpen ? " howopen" : "")}
            role="dialog"
            aria-modal="true"
            aria-label="Start a guided tour"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header. The PROJECT is the title; the action is a pill.
                The modal is about the project — "guided tour" is what
                you're doing to it. */}
            <div className="mhead">
              <span className="mmark" aria-hidden="true">
                {/* VR headset — goggles, nose notch, strap arc, lenses.
                    Not a camcorder: this is the thing they'll wear. */}
                <svg viewBox="0 0 32 32" fill="none">
                  <path d="M6.5 11h19a3.5 3.5 0 0 1 3.5 3.5v4A3.5 3.5 0 0 1 25.5 22h-3.2a2.6 2.6 0 0 1-2-.95l-1.35-1.6a1.3 1.3 0 0 0-2 0l-1.35 1.6a2.6 2.6 0 0 1-2 .95H6.5A3.5 3.5 0 0 1 3 18.5v-4A3.5 3.5 0 0 1 6.5 11Z" fill="currentColor" opacity=".18" />
                  <path d="M6.5 11h19a3.5 3.5 0 0 1 3.5 3.5v4A3.5 3.5 0 0 1 25.5 22h-3.2a2.6 2.6 0 0 1-2-.95l-1.35-1.6a1.3 1.3 0 0 0-2 0l-1.35 1.6a2.6 2.6 0 0 1-2 .95H6.5A3.5 3.5 0 0 1 3 18.5v-4A3.5 3.5 0 0 1 6.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M8 8.2A10.4 10.4 0 0 1 16 5c3.1 0 5.9 1.2 8 3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="9.6" cy="16.5" r="1.7" fill="currentColor" />
                  <circle cx="22.4" cy="16.5" r="1.7" fill="currentColor" />
                </svg>
              </span>
              <div className="hti">
                <h1>{projectName || "This project"}</h1>
                {(configSummary || locationStr) && (
                  <small>{[configSummary, locationStr].filter(Boolean).join(" · ")}</small>
                )}
              </div>
              <span className="hact">Guided tour</span>
              <button className="gtm-x" aria-label="Close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                     strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>

            {/* ── Selection view ─────────────────────────────────────── */}
            {status !== "ready" && (
              <div className="mbody">
                <div className="left">
                  <div className="lrow"><h2>Who is coming</h2></div>

                  {/* Tabs only when there's more than one directory — an
                      agent can add leads and nothing else. */}
                  {viewerKinds.length > 1 && (
                    <div className="tabs" role="tablist" aria-label="Directory">
                      {viewerKinds.map((k) => (
                        <button
                          key={k}
                          type="button"
                          role="tab"
                          aria-selected={tab === k}
                          className={"gtm-tab" + (tab === k ? " on" : "")}
                          onClick={() => setTab(k)}
                        >
                          {k === "lead" ? "Buyers" : k === "agent" ? "Agents" : "System admins"}
                          {!peopleLoading && <span className="n">{kindCounts[k] || 0}</span>}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="srow">
                    <div className="srch">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.6-3.6" />
                      </svg>
                      <input
                        aria-label="Search people"
                        placeholder={peopleLoading ? "Loading…" : "Search by name or mobile"}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    {/* Beside the search, not in the header and not as the
                        last list row — a trailing row is unreachable once
                        the agency has a hundred leads. Leads only; you
                        don't create staff from here. */}
                    {tab === "lead" && !leadsFull && (
                      <button className="newbtn" type="button" onClick={startNewLead}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                             strokeLinecap="round" aria-hidden="true">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        <span>{newLeadLabel}</span>
                      </button>
                    )}
                  </div>

                  <div className="gtm-list" role="listbox" aria-label="People">
                    {peopleLoading && <div className="none">Loading…</div>}

                    {!peopleLoading && filteredPeople.length === 0 && (
                      tab === "lead" && trimmedQuery ? (
                        <div className="nomatch">
                          <span className="nm-i" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.6-3.6" /></svg>
                          </span>
                          <b>No buyer matches &ldquo;{trimmedQuery}&rdquo;</b>
                          <p>
                            {/^\d/.test(trimmedQuery)
                              ? "This number isn't on your list yet — add it as a new lead and they'll join this tour."
                              : "Nobody by that name yet — add them as a new lead and they'll join this tour."}
                          </p>
                          <button className="nm-btn" type="button" onClick={startNewLead}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                                 strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                            Create lead &ldquo;{trimmedQuery}&rdquo;
                          </button>
                        </div>
                      ) : (
                        <div className="none">
                          {trimmedQuery
                            ? `No ${GT_KIND_LABEL[tab].toLowerCase()} matches that search.`
                            : tab === "asa"
                              ? "No system admins in this agency yet."
                              : tab === "agent"
                                ? "No agents available to you."
                                : "No leads yet — add one from the search row."}
                        </div>
                      )
                    )}

                    {filteredPeople.map((p) => {
                      const capped = kindFull(p.kind);
                      return (
                        <button
                          type="button"
                          key={p.id}
                          role="option"
                          aria-selected={false}
                          aria-disabled={capped}
                          className={"p" + (capped ? " off" : "") + (nopeId === p.id ? " nope" : "")}
                          title={capped
                            ? (p.kind === "lead"
                              ? `${MAX_LEADS} buyers is the limit for one tour`
                              : `${MAX_STAFF} from your team is the limit for one tour`)
                            : undefined}
                          onClick={() => (capped ? bump(p.id) : addPerson(p))}
                        >
                          <span className={"gtm-av" + (p.kind === "agent" ? " tm" : p.kind === "asa" ? " sa" : "")}>
                            {gtInitials(p.name)}
                          </span>
                          <span className="pi">
                            <b>{p.name}</b>
                            {p.mobile && <small>{p.mobile}</small>}
                          </span>
                          {p.kind !== "lead" && <span className="pr">{GT_KIND_LABEL[p.kind]}</span>}
                          <span className="tick" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                                 strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ── Right rail: two slots, each with a live status ──── */}
                <div className="right">
                  <div className="grp">
                    <div className="grp-h">
                      <b>Buyers</b>
                      <span className={"st " + (leadCount === 0 ? "need" : leadsFull ? "max" : "ok")}>
                        {leadCount === 0 ? "At least 1" : `${leadCount} of ${MAX_LEADS}`}
                      </span>
                    </div>
                    <div className="slot">
                      {pickedLeads.length === 0 ? (
                        <div className="gtm-empty">
                          Pick a buyer on the left — up to {MAX_LEADS} per tour.
                        </div>
                      ) : pickedLeads.map((p) => (
                        <div className="mini" key={p.id}>
                          <span className="gtm-av">{gtInitials(p.name)}</span>
                          <span className="mi">
                            <b>{p.name}</b>
                            <small>
                              {p.mobile ? `${p.mobile} · ` : ""}
                              up to {MAX_GUEST_PASSES_PER_LEAD} family
                            </small>
                          </span>
                          <button className="rm" type="button" aria-label={`Remove ${p.name}`}
                                  onClick={() => removePerson(p.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                                 strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {canInviteStaff && (
                    <div className="grp">
                      <div className="grp-h">
                        <b>Your team</b>
                        <span className={"st " + (staffCount === 0 ? "opt" : staffFull ? "max" : "ok")}>
                          {staffCount === 0 ? `Optional · max ${MAX_STAFF}` : `${staffCount} of ${MAX_STAFF}`}
                        </span>
                      </div>
                      <div className="slot">
                        {pickedTeam.length === 0 ? (
                          <div className="gtm-empty">
                            Add up to {MAX_STAFF} colleagues to sit in.
                          </div>
                        ) : pickedTeam.map((p) => (
                          <div className="mini" key={p.id}>
                            <span className={"gtm-av" + (p.kind === "agent" ? " tm" : " sa")}>
                              {gtInitials(p.name)}
                            </span>
                            <span className="mi">
                              <b>{p.name}</b>
                              <small>{GT_KIND_LABEL[p.kind]}</small>
                            </span>
                            <button className="rm" type="button" aria-label={`Remove ${p.name}`}
                                    onClick={() => removePerson(p.id)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                                   strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No project card here on purpose — the header already
                      says which project this is. */}
                  <div className="sum">
                    <p className="sum-h">When you create the room</p>
                    <div className="sr"><TickSm /><span>A join link is generated for each buyer</span></div>
                    <div className="sr"><TickSm /><span>You land in the host room and control the walkthrough</span></div>
                    <div className="sr"><TickSm /><span>Every lead captured is attributed to you</span></div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Room created / live ────────────────────────────────── */}
            {status === "ready" && (
              <div className="livev">
                <div className="lv-in">
                  <span className={"lv-badge" + (enteredAt && !tourEnded ? " on" : "")}>
                    <i />{tourEnded ? "Tour ended" : enteredAt ? "Tour is live" : "Room created"}
                  </span>
                  <h2>
                    {tourEnded ? "This tour has ended"
                      : enteredAt ? "Tour is live" : "Ready when you are"}
                  </h2>
                  <p className="lv-sub">
                    {tourEnded
                      ? "The link and room code no longer work. Start a new tour to invite people again."
                      : enteredAt
                        ? "You're hosting in the other tab. Admit anyone waiting in the lobby from there."
                        : "Send each person their link, then enter the room. The tour goes live the moment you join — until then they wait in the lobby."}
                  </p>

                  {!tourEnded && (
                    <>
                      <div className="lv-code">
                        <div>
                          <span className="lv-k">Room code</span>
                          <b className="mono">{joinCode || "—"}</b>
                        </div>
                        <div>
                          <span className="lv-k">{enteredAt ? "Live for" : "Status"}</span>
                          <b className={enteredAt ? "mono" : ""}>
                            {enteredAt ? liveFor : "Not started"}
                          </b>
                        </div>
                        <div>
                          <span className="lv-k">Invited</span>
                          <b>{invitees.length + 1} people</b>
                        </div>
                      </div>

                      <p className="lv-h">Send the join links</p>
                      <div className="lv-links">
                        {/* TWO DOORS, and they must never be swapped.
                            Buyers get the public link and verify a mobile;
                            colleagues get the host room and sign in with
                            their OpenSky email. Send a colleague the buyer
                            link and they'd OTP in and be filed as a lead —
                            our own team member in the CRM. */}
                        {pickedLeads.length > 0 && (
                          <div className="lg">
                            <div className="lg-h">
                              <b>Buyers</b>
                              <span className="lg-n">{pickedLeads.length}</span>
                              <span className="lg-u">{buyerUrl}</span>
                              <button className={"cp" + (copiedKey === "buyers" ? " done" : "")}
                                      type="button" onClick={() => copyGroup("buyers", buyerUrl)}>
                                {copiedKey === "buyers"
                                  ? <><Icon.Check /> Copied</>
                                  : <><Icon.Copy /> Copy</>}
                              </button>
                            </div>
                            <div className="lg-p">
                              {pickedLeads.map((p) => (
                                <div className="lk" key={p.id}>
                                  <span className="gtm-av">{gtInitials(p.name)}</span>
                                  <span className="li">
                                    <b>{p.name}</b>
                                    {p.mobile && <small>{p.mobile}</small>}
                                  </span>
                                  <a className="wa" href={waHrefFor(p)} target="_blank"
                                     rel="noopener noreferrer"
                                     title={p.mobile
                                       ? `WhatsApp ${p.name}`
                                       : `No number saved — pick ${p.name} in WhatsApp`}>
                                    <Icon.WhatsApp /> Send
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {pickedTeam.length > 0 && (
                          <div className="lg">
                            <div className="lg-h">
                              <b>Your team</b>
                              <span className="lg-n">{pickedTeam.length}</span>
                              <span className="lg-u">{teamUrl}</span>
                              <button className={"cp" + (copiedKey === "team" ? " done" : "")}
                                      type="button" onClick={() => copyGroup("team", teamUrl)}>
                                {copiedKey === "team"
                                  ? <><Icon.Check /> Copied</>
                                  : <><Icon.Copy /> Copy</>}
                              </button>
                            </div>
                            <div className="lg-p">
                              {pickedTeam.map((p) => (
                                <div className="lk" key={p.id}>
                                  <span className={"gtm-av" + (p.kind === "agent" ? " tm" : " sa")}>
                                    {gtInitials(p.name)}
                                  </span>
                                  <span className="li">
                                    <b>{p.name}</b>
                                    <small>Signs in with their OpenSky email</small>
                                  </span>
                                  <a className="wa" href={waHrefFor(p)} target="_blank"
                                     rel="noopener noreferrer" title={`WhatsApp ${p.name}`}>
                                    <Icon.WhatsApp /> Send
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="lv-note">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                             strokeLinecap="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
                        </svg>
                        Anyone else the link reaches verifies their number and waits in the lobby
                        for your approval. You can close this and rejoin from the project page.
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── How people get in — collapsed by default. Sibling of the
                body, never a child of the left column: as a flex child it
                competed with the list for a fixed column height and always
                won, pushing itself below the footer. */}
            {status !== "ready" && (
              <div className={"how" + (howOpen ? " on" : "")}>
                <button className="how-t" type="button" aria-expanded={howOpen}
                        onClick={() => setHowOpen((v) => !v)}>
                  <svg className="hi" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
                  </svg>
                  How people get in
                  <span className="how-p">
                    <i className="d a" /><i className="d b" /><i className="d c" />3 ways
                  </span>
                  <svg className="ch" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {howOpen && (
                  <div className="how-g">
                    <div className="hc a">
                      <b>
                        <span className="hi-w">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                               strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5 5L20 6.5" /></svg>
                        </span>
                        Invited here
                      </b>
                      <p>Join straight away, no approval needed.</p>
                    </div>
                    <div className="hc b">
                      <b>
                        <span className="hi-w">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                               strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="8" r="3" /><path d="M3 19a6 6 0 0 1 12 0" />
                            <circle cx="17.5" cy="10" r="2.2" /><path d="M15 19a5 5 0 0 1 6.5-4.3" />
                          </svg>
                        </span>
                        Their family
                      </b>
                      <p>
                        Up to {MAX_GUEST_PASSES_PER_LEAD} per buyer, added from inside the room.
                        First name only — not leads.
                      </p>
                    </div>
                    <div className="hc c">
                      <b>
                        <span className="hi-w">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                               strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5l3.2 1.9" />
                          </svg>
                        </span>
                        Forwarded the link
                      </b>
                      <p>Verifies their number and waits in the lobby. You decide who enters.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Footer ─────────────────────────────────────────────── */}
            <div className="mfoot">
              {status === "ready" && !tourEnded && invitees.length > 0 && (
                <span className="fstack" aria-hidden="true">
                  {invitees.slice(0, 4).map((p) => (
                    <span key={p.id}
                          className={"sq" + (p.kind === "agent" ? " tm" : p.kind === "asa" ? " sa" : "")}>
                      {gtInitials(p.name)}
                    </span>
                  ))}
                </span>
              )}
              {status !== "ready" && invitees.length > 0 && (
                <span className="fstack" aria-hidden="true">
                  {invitees.slice(0, 4).map((p) => (
                    <span key={p.id}
                          className={"sq" + (p.kind === "agent" ? " tm" : p.kind === "asa" ? " sa" : "")}>
                      {gtInitials(p.name)}
                    </span>
                  ))}
                </span>
              )}

              <span className={"fhint" + (leadCount > 0 ? " ready" : "")}>
                {blockingRoom && status !== "error" && status !== "ready" ? (
                  /* Said on opening, not after the host has picked their
                     buyers and pressed the button. The backend would
                     refuse this room anyway; the only question is whether
                     they find out before or after the work. */
                  <>
                    <b className="ferr">
                      {blockingRoom.project_name
                        ? `Your tour for ${blockingRoom.project_name} is still open. End it there first.`
                        : "You already have a tour open. End it first."}
                    </b>
                    <button type="button" className="fl gt-openblocking"
                            onClick={() => openRoom(blockingRoom.room_id)}>
                      Open that tour
                    </button>
                  </>
                ) : status === "error" ? (
                  <>
                    <b className="ferr">{errMsg}</b>
                    {blockingRoom && (
                      <button type="button" className="fl gt-openblocking"
                              onClick={() => openRoom(blockingRoom.room_id)}>
                        Open that tour
                      </button>
                    )}
                  </>
                ) : tourEnded ? (
                  <>
                    <b>This tour has ended</b>
                    <span className="fl">The link and room code no longer work.</span>
                  </>
                ) : status === "ready" ? (
                  enteredAt ? (
                    <>
                      <b>Tour is live</b>
                      <span className="fl">
                        Hosting in the other tab — this window stays as your room summary.
                      </span>
                    </>
                  ) : (
                    <>
                      <b>Room created</b>
                      <span className="fl">
                        The tour goes live when you enter — opens in a new tab.
                      </span>
                    </>
                  )
                ) : leadCount === 0 ? (
                  <>
                    Choose at least one buyer to continue.
                    <span className="fl">
                      Up to {MAX_LEADS} buyers and {canInviteStaff ? `${MAX_STAFF} from your team` : "no team seats on your role"}.
                    </span>
                  </>
                ) : (
                  <>
                    <b>{leadCount}</b> buyer{leadCount > 1 ? "s" : ""}
                    {staffCount > 0 && <> · <b>{staffCount}</b> co-host{staffCount > 1 ? "s" : ""}</>}
                    <span className="fl">
                      Each buyer can bring up to {MAX_GUEST_PASSES_PER_LEAD} family from inside the room.
                    </span>
                  </>
                )}
              </span>

              {tourEnded ? (
                <button className="go" onClick={reset}>Start a new tour</button>
              ) : status === "ready" ? (
                roomId && (
                  <button className={"go " + (enteredAt ? "livebtn" : "enter")} onClick={enterRoom}>
                    {enteredAt && <span className="pulse" aria-hidden="true" />}
                    {enteredAt ? "Go to tour" : "Enter the room"}
                    {!enteredAt && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5" />
                        <path d="M10 17l5-5-5-5M15 12H3" />
                      </svg>
                    )}
                  </button>
                )
              ) : (
                <button className="go"
                        disabled={leadCount === 0 || status === "creating" || !!blockingRoom}
                        onClick={() => void create()}>
                  {status === "creating" ? "Creating room…" : "Create tour room"}
                  {status !== "creating" && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                         strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* New-lead capture — its own layer above the tour modal. Four
          required fields plus a duplicate warning is too much to sit
          inside the picker without crowding it. */}
      {open && showNewLead && ReactDOM.createPortal(
        <NewLeadSheet
          initialQuery={trimmedQuery}
          people={people}
          saving={nlSaving}
          err={nlErr}
          onClose={closeNewLead}
          onCreate={(v) => void createLeadInline(v)}
          onUseExisting={(p) => { addPerson(p); closeNewLead(); }}
        />,
        document.body,
      )}
    </>
  );
}

/* ── Sub header (Breadcrumb · Title · Edit + Share + Views) ─── */
type SubHeaderProps = {
  projectName: string;
  locationStr: string;
  agencySlug: string;
  projectId: string;         // threaded into ShareControl so it can mint the link
  editHref: string | null;   // null when current user has no edit rights
  leadAttribution: boolean;
  viewCount: number | null;  // VR session count for this project; null = still loading
  showViews: boolean;        // hidden for public/share viewers; shown to logged-in agent/admin
  isPublicViewer: boolean;   // share-link visitor → branded 3-col band (logo · title · sharer)
  agencyLogo?: string;
  sharerName?: string;
  sharerRoles?: string[];
  // Threaded into ShareControl for the per-channel share messages.
  configSummary?: string;
  experienceFlags?: { meta: boolean; twin: boolean; route: boolean };
  // Guided Tour: agency scope for the participant picker, and the agent id
  // when the viewer is one (scopes the picker to their own leads).
  agencyId?: string;
  agentId?: string;
  // Distribution replaces the Views pill: a status dot beats a metric the
  // dashboard already reports properly.
  showDistribution?: boolean;
  embedLive?: boolean;
  onOpenDistribution?: () => void;
};
function SubHeader({ projectName, locationStr, agencySlug, projectId, editHref, leadAttribution, viewCount, showViews, isPublicViewer, agencyLogo, sharerName, sharerRoles, configSummary, experienceFlags, agencyId, agentId, showDistribution, embedLive, onOpenDistribution }: SubHeaderProps) {
  const navigate = useNavigate();
  const homeHref = agencySlug ? `/c/${agencySlug}` : "/";
  /* Guided Tour is a STAFF action. Three independent gates, all required:
     - the feature is switched on for this environment (off in prod until it
       ships — see utils/featureFlags.ts),
     - the viewer holds a staff realm role (customers never qualify), and
     - this isn't the public share view (my.openskyvr.com), where even a
       logged-in agent is acting as a viewer, not a host. */
  const canHostGuidedTour =
    guidedTourEnabled() &&
    !isPublicViewer &&
    (UserService.hasRole("agent") ||
      UserService.hasRole("agency_system_admin") ||
      UserService.hasRole("agency_business_admin") ||
      UserService.hasRole("super_admin"));
  const projectsHref = agencySlug ? `/c/${agencySlug}/p/library` : "/";
  // Mobile back — falls through to projects list (matches desktop breadcrumb
  // trail) so deep-linked viewers land somewhere sensible instead of an empty
  // history stack.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(projectsHref);
  };
  // Public share-link visitor: branded 3-column band — agency logo (left),
  // project title + location (center), "Shared by …" (right). No breadcrumb,
  // edit, share or views chrome (those are agent-app only).
  if (isPublicViewer) {
    const initials =
      (sharerName || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() || "")
        .join("") || "•";
    const norm = (sharerRoles || []).map((r) =>
      String(r || "").toLowerCase().replace(/^agency_/, ""),
    );
    const isAgencySide = norm.some((r) =>
      ["agent", "business_admin", "system_admin", "aba", "asa"].includes(r),
    );
    const sharerLabel = isAgencySide ? "Shared by" : "Recommended by";
    // First name only in the pill — the full name reads as too much weight in
    // the header. Full name stays on the hover title for who-it-is clarity.
    const sharerFirstName =
      (sharerName || "").trim().split(/\s+/)[0] || sharerName;
    return (
      <div className="subhead subhead--public">
        <div className="subhead-pub-logo-wrap">
          {agencyLogo && (
            <img className="subhead-pub-logo" src={agencyLogo} alt="" />
          )}
        </div>
        <div className="subhead-title">
          <h1>
            {projectName || "Project"}
            {locationStr && <span className="loc-inline">{locationStr}</span>}
          </h1>
        </div>
        <div className="subhead-pub-sharer-wrap">
          {sharerName && (
            <div className="subhead-pub-sharer" title={`${sharerLabel} ${sharerName}`}>
              <span className="sps-avatar">{initials}</span>
              <span className="sps-text">
                <span className="sps-label">{sharerLabel}</span>
                <span className="sps-name">
                  <span className="sps-name-full">{sharerName}</span>
                  <span className="sps-name-short">{sharerFirstName}</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="subhead">
      {/* Mobile back chevron — hidden on desktop via CSS */}
      <button
        type="button"
        className="subhead-back"
        onClick={goBack}
        aria-label="Go back"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </button>
      <nav className="crumbs" aria-label="Breadcrumb">
        <a className="crumb" href={homeHref} onClick={(e) => { e.preventDefault(); navigate(homeHref); }}>Home</a>
        <span className="crumb-sep">›</span>
        <a className="crumb" href={projectsHref} onClick={(e) => { e.preventDefault(); navigate(projectsHref); }}>My Projects</a>
        <span className="crumb-sep">›</span>
        <span className="crumb crumb--current">{projectName || "Project"}</span>
      </nav>
      <div className="subhead-main">
        <div className="subhead-title">
          <h1>
            {projectName || "Project"}
            {locationStr && <span className="loc-inline">{locationStr}</span>}
          </h1>
        </div>
        <div className="subhead-actions">
          {editHref && (
            <a
              className="edit-btn"
              href={editHref}
              onClick={(e) => { e.preventDefault(); navigate(editHref); }}
              aria-label="Edit project details"
            >
              <Icon.Pencil /> Edit
            </a>
          )}
          {/* Guided Tour — staff only, and NEVER on the public share page
              (my.openskyvr.com): a buyer must not be able to open rooms.
              Sits left of Share so the two selling actions read together. */}
          {canHostGuidedTour && (
            <GuidedTourControl
              projectId={projectId}
              agencyId={agencyId}
              agentId={agentId}
              projectName={projectName}
              locationStr={locationStr}
              configSummary={configSummary}
            />
          )}
          <ShareControl projectId={projectId} leadAttribution={leadAttribution} projectName={projectName} locationStr={locationStr} configSummary={configSummary} experienceFlags={experienceFlags} />
          {/* Views pill — hidden for public/share visitors (a buyer shouldn't
              see view counts); shown only to the logged-in agent/admin. */}
          {/* Replaces the Views pill. Views are reported properly in
              Analytics; what can't be seen anywhere else at a glance is
              whether this project is actually published on a customer's
              website. Green = at least one walkthrough live, grey = none
              configured. */}
          {showDistribution && (
            <button
              type="button"
              className={"pub-btn" + (embedLive ? " pub-btn--live" : "")}
              onClick={onOpenDistribution}
              aria-label={
                embedLive
                  ? "Publish on your website — live"
                  : "Publish on your website — not set up yet"
              }
            >
              {/* A browser window with an upload arrow, not a globe: the action
                  is "put this on YOUR website", and a globe reads as
                  internet/language — it also turned to mud at 14px, with three
                  overlapping arcs inside a circle. */}
              <span className="pub-btn-tile">
                <svg className="pub-btn-ico" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.9"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
                  <path d="M3 9.2h18" />
                  <path d="M12 16.7v-4.5M9.9 14.3 12 12.2l2.1 2.1" />
                </svg>
                <span className="pub-btn-dot" aria-hidden="true" />
              </span>
              <span className="pub-btn-text">Publish</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Hero (experience stage) ────────────────────────────────────── */
type HeroProps = {
  mode: ModeId;
  onMode: (id: ModeId) => void;
  metaModels: any[];
  matterports: any[];
  routeViews: string[];
  urlString: string;
  // Project context passed through so useMatterportAnalytics can build VR
  // session payloads + load the Matterport SDK 3.6.0 (without which the
  // matterport iframe hangs on "Connecting to the Matterport space").
  projectId: string;
  agencyId: string;
  agencySlug: string;
  projectSlug: string;
  clientManagerId: string;
  clientAdminId: string;
  agentId: string;
  customerId: string;
  // Public-share lead-gate context (used only when isPublicViewer + leadgateOn).
  isPublicViewer?: boolean;
  leadgateOn?: boolean;
  projectName?: string;
  sharerName?: string;
  agencyName?: string;
  agencyLogo?: string;
};
const EXPLAINERS: Record<ModeId, { tag: string; title: string; body: string; points: string[] }> = {
  meta: {
    tag: "Pre-construction · 3D render",
    title: "What is a Meta Model?",
    body:
      "An immersive walkthrough of a space that isn’t built yet — fully rendered in 3D so buyers can explore the layout, scale and natural light of a home before a single brick is laid.",
    points: ["Walk unbuilt layouts", "See finishes & daylight", "Decide off-plan with confidence"],
  },
  twin: {
    tag: "As-built · True to life",
    title: "What is a Digital Twin?",
    body:
      "A true-to-life 3D replica of the actual built space, captured from the real property — so buyers can tour every room remotely, exactly as it stands today.",
    points: ["1:1 of the real unit", "Tour any room remotely", "No site visit needed"],
  },
  route: {
    tag: "Neighbourhood · Connectivity",
    title: "What is a Route View?",
    body:
      "A guided journey through the surroundings — travel the approach roads, landmarks and connectivity around the project to understand the neighbourhood and the daily commute.",
    points: ["Drive the approach", "Spot nearby landmarks", "Gauge the commute"],
  },
};

function Hero({
  mode, onMode, metaModels, matterports, routeViews, urlString,
  projectId, agencyId, agencySlug, projectSlug,
  clientManagerId, clientAdminId, agentId, customerId,
  isPublicViewer, leadgateOn, projectName, sharerName, agencyName, agencyLogo,
}: HeroProps) {
  type ModeEntry = { id: ModeId; label: string; icon: React.FC; count: number };
  const modes: ModeEntry[] = [
    metaModels.length > 0 && { id: "meta" as ModeId, label: "Meta Model", icon: Icon.CloudHouse, count: metaModels.length },
    matterports.length > 0 && { id: "twin" as ModeId, label: "Digital Twin", icon: Icon.Cube, count: matterports.length },
    routeViews.length > 0 && { id: "route" as ModeId, label: "Route View", icon: Icon.Route, count: routeViews.length },
  ].filter(Boolean) as ModeEntry[];

  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState<ModeId | null>(null);

  // Matterport iframe ref + analytics hook — V2 calls this hook on the
  // matterport iframe to load Matterport SDK 3.6.0, attach VR session
  // tracking, and message-listen for in-tour events. Skipping it leaves
  // the iframe stuck on "Connecting".
  const matterportIframeRef = useRef<HTMLIFrameElement | null>(null);
  // Fullscreen toggle — the design's `.hero-fs` button. In fullscreen we
  // hide all overlays (mode tabs + cycle chips) so the experience fills the
  // viewport edge-to-edge; the icon swaps to "exit fullscreen".
  const heroStageRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // iOS Safari blocks the Fullscreen API for iframes/divs (only <video>), so
  // the Meta Model can't go truly fullscreen on iPhone. When native fullscreen
  // is unavailable/rejected we fall back to CSS full-bleed ("fake fullscreen").
  const [fakeFs, setFakeFs] = useState(false);
  // Play overlay over the active twin. Tapping it fires fullscreen + landscape —
  // the browser only allows fullscreen from a real click on OUR element, not from
  // Matterport's own in-iframe play button (cross-origin). We track which
  // matterport_id has been "played" so switching to another twin re-shows it.
  const [playedTwinId, setPlayedTwinId] = useState("");
  useEffect(() => {
    const onChange = () => {
      const fs = document.fullscreenElement
        || (document as any).webkitFullscreenElement
        || (document as any).msFullscreenElement;
      setIsFullscreen(!!fs);
      // When the page exits fullscreen — by user pressing ESC, the system
      // back button, swipe gesture, or any other path that bypasses our
      // toggleFullscreen handler — release the landscape orientation lock
      // so the device returns to its natural portrait orientation. iOS /
      // unsupported envs silently no-op via the try/catch.
      if (!fs) {
        try {
          const so = (screen as any).orientation;
          if (so && typeof so.unlock === "function") so.unlock();
        } catch (_e) { /* iOS / unsupported — silent */ }
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);
  const toggleFullscreen = async () => {
    const el = heroStageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement || fakeFs) {
        // Exit — from native OR iOS fake fullscreen.
        if (document.fullscreenElement) await document.exitFullscreen();
        if (fakeFs) {
          setFakeFs(false);
          setIsFullscreen(false);
        }
        // Unlock orientation when leaving fullscreen so the device returns to
        // its natural orientation. Wrapped because iOS Safari throws here.
        try {
          const so = (screen as any).orientation;
          if (so && typeof so.unlock === "function") so.unlock();
        } catch (_e) { /* iOS / unsupported — silent */ }
      } else {
        const req = el.requestFullscreen
          || (el as any).webkitRequestFullscreen
          || (el as any).msRequestFullscreen;
        if (req) {
          try {
            await req.call(el);
          } catch (_e) {
            // Native fullscreen rejected (some iOS / embedded contexts) —
            // fall back to CSS full-bleed so the Meta Model still fills.
            setFakeFs(true);
            setIsFullscreen(true);
          }
        } else {
          // No Fullscreen API on this element — iOS Safari on iPhone only
          // supports fullscreen on <video>, not iframes/divs. Fake it with a
          // full-bleed CSS class (.hero--fakefs) so the tour fills the screen.
          setFakeFs(true);
          setIsFullscreen(true);
        }
        // Lock to landscape so the iframe gets the wider canvas immediately
        // without waiting for the user to physically rotate. Supported on
        // Android Chrome + most modern Android browsers; iOS Safari throws
        // (no Screen Orientation Lock API) — we swallow the error and let
        // the user rotate manually if needed.
        try {
          const so = (screen as any).orientation;
          if (so && typeof so.lock === "function") await so.lock("landscape");
        } catch (_e) { /* iOS / unsupported — silent */ }
      }
    } catch (_e) {
      // user denied or unsupported — quietly ignore
    }
  };
  const activeModeForHook = modes.some((m) => m.id === mode) ? mode : (modes[0]?.id ?? "meta");
  const currentMatterport = matterports[idx % Math.max(matterports.length, 1)];
  const mp = useMatterportAnalytics(matterportIframeRef, activeModeForHook === "twin", {
    matterport: currentMatterport,
    projectId,
    agencyId,
    agencySlug,
    projectSlug,
    clientManagerId,
    clientAdminId,
    agentId,
    customerId,
  });

  // Empty state
  if (modes.length === 0) {
    return (
      <div className="hero-wrap">
        <div className="hero hero--empty">
          <div className="hero-bg"></div>
          <div className="hero-empty">
            <span className="hero-empty-ico"><Icon.CloudHouse /></span>
            <div className="hero-empty-title">No experiences added yet</div>
            <div className="hero-empty-sub">
              Add at least one experience — a Meta Model, Digital Twin, or Route View —
              to bring this project to life for your buyers.
            </div>
            <div className="hero-empty-actions">
              <button className="hero-empty-btn"><Icon.CloudHouse /> Add Meta Model</button>
              <button className="hero-empty-btn"><Icon.Cube /> Add Digital Twin</button>
              <button className="hero-empty-btn"><Icon.Route /> Add Route View</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeMode = modes.some((m) => m.id === mode) ? mode : modes[0].id;
  const activeObj = modes.find((m) => m.id === activeMode)!;
  const modeLabel = activeObj.label;
  const activeCount = activeObj.count;
  const safeIdx = idx % activeCount;
  const showCycle = activeCount > 1;

  // Display name for the cycle chips. Meta Models and Digital Twins carry
  // a real `name` field on each item; route_view is just a string[] so we
  // fall back to "Route View 1", "Route View 2", etc.
  const itemName = (m: ModeId, i: number): string => {
    if (m === "meta") return metaModels[i]?.name || `Meta Model ${i + 1}`;
    if (m === "twin") return matterports[i]?.name || `Digital Twin ${i + 1}`;
    return `Route View ${i + 1}`;
  };

  const switchMode = (id: ModeId) => { setIdx(0); onMode(id); };
  const cycle = () => setIdx((i) => (i + 1) % activeCount);

  const explainer = hovered ? EXPLAINERS[hovered] : null;
  const HoveredIcon = hovered ? (modes.find((m) => m.id === hovered)?.icon || Icon.CloudHouse) : Icon.CloudHouse;

  /* Build the iframe URL for the currently active experience. */
  let iframeUrl: string | null = null;
  if (activeMode === "meta") {
    const mm = metaModels[safeIdx];
    const raw = mm?.url || "";
    const base = /^https?:\/\//i.test(raw) ? raw : raw ? `https://${raw}` : "";
    if (base) {
      const metaModelParam = mm?._id ? `&metaModelId=${mm._id}` : "";
      const experienceSlugParam = mm?.slug ? `&experience_slug=${encodeURIComponent(mm.slug)}` : "";
      const experienceTypeParam = `&experience_type=m`;
      iframeUrl = base + urlString + metaModelParam + experienceSlugParam + experienceTypeParam;
    }
  } else if (activeMode === "twin") {
    const mp = matterports[safeIdx];
    if (mp?.matterport_id) {
      // mls=1 puts matterport in "MLS / embedded real-estate" mode which
      // (we're betting) keeps it inside the iframe on mobile when the
      // user taps play, instead of redirecting the parent window.
      // qs=1 hides the help quick-start panel.
      // play=1 auto-enters the tour on load (behind our play overlay) so a
      // single tap on the overlay → fullscreen + already-running walkthrough.
      iframeUrl = `https://my.matterport.com/show/?m=${mp.matterport_id}&applicationKey=${MATTERPORT_SDK_KEY}&mls=1&qs=1&play=1`;
    }
  } else if (activeMode === "route") {
    iframeUrl = routeViews[safeIdx] || null;
  }

  return (
    <div className="hero-wrap">
      {/* Mode row — sits as an absolute overlay on top of the hero on desktop
          (positioned via .hero-wrap as its containing block) but flows above
          the hero as a horizontal scrollable pill row on mobile. Moved out
          of .hero (which has overflow:hidden) so the mobile layout isn't
          clipped to the iframe's bounds. */}
      <div
        className={"hero-mode-row" + (modes.length === 1 ? " hero-mode-row--single" : "")}
        style={isFullscreen ? { display: "none" } : undefined}
      >
        {modes.map((m) => {
          const I = m.icon;
          const on = activeMode === m.id;
          return (
            <button
              key={m.id}
              className={"hero-overlay-btn" + (on ? " hero-overlay-btn--on" : "")}
              onClick={() => switchMode(m.id)}
              onMouseEnter={() => setHovered(m.id)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(m.id)}
              onBlur={() => setHovered(null)}
            >
              <I /> {m.label}
              <span className="hero-overlay-count">{m.count}</span>
            </button>
          );
        })}
      </div>
      <div className={`hero${fakeFs ? " hero--fakefs" : ""}`} ref={heroStageRef}>
        <div className="hero-bg"></div>
        <span className="hero-bird"><Icon.Bird /></span>

        {explainer && (
          <aside className="exp-tip" key={hovered as string} aria-live="polite">
            <div className="exp-tip-head">
              <span className="exp-tip-ico"><HoveredIcon /></span>
              <span className="exp-tip-tag">{explainer.tag}</span>
            </div>
            <div className="exp-tip-title">{explainer.title}</div>
            <p className="exp-tip-body">{explainer.body}</p>
            <ul className="exp-tip-points">
              {explainer.points.map((p, i) => (
                <li key={i}><span className="exp-tip-check"><Icon.Check /></span>{p}</li>
              ))}
            </ul>
          </aside>
        )}

        {iframeUrl ? (
          // Match V2's exact DOM structure for the experience viewer.
          // Matterport in particular is finicky about its iframe context —
          // straying from V2's pattern can leave it hung on "Connecting".
          <div className="hero-iframe-real" aria-label={`${modeLabel} iframe`}>
            <div className="rv-experience-viewer">
              {activeMode === "meta" && (
                <IframeViews models={[iframeUrl]} />
              )}
              {activeMode === "twin" && (
                // key by matterport_id forces a clean unmount/remount of the
                // iframe whenever the user switches between matterports — the
                // SDK doesn't have to negotiate a mid-load model swap, which
                // can hang on "Connecting" if clicks happen faster than loads.
                // allow + allowFullScreen: mobile browsers require explicit
                // sensor permissions for matterport's 3D rendering (desktop
                // grants these by default).
                // sandbox: deliberately OMITS allow-top-navigation so
                // matterport cannot redirect the parent tab to its
                // standalone fullscreen view on mobile when the user taps
                // play. All other flags are present so the SDK runs normally
                // (scripts, same-origin, forms, popups, pointer-lock,
                // orientation-lock, presentation, downloads, modals).
                <iframe
                  key={matterports[safeIdx]?.matterport_id || safeIdx}
                  ref={matterportIframeRef}
                  className="rv-exp-iframe"
                  src={iframeUrl}
                  title={matterports[safeIdx]?.name || "Digital Twin"}
                  allow="xr-spatial-tracking; gyroscope; accelerometer; magnetometer; fullscreen; autoplay; vr"
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock allow-orientation-lock allow-presentation allow-downloads allow-modals"
                />
              )}
              {activeMode === "twin" &&
                matterports[safeIdx]?.matterport_id &&
                playedTwinId !== matterports[safeIdx]?.matterport_id && (
                  <button
                    type="button"
                    className="twin-play-overlay"
                    aria-label="Start the tour in fullscreen"
                    onClick={async () => {
                      await toggleFullscreen();
                      setPlayedTwinId(matterports[safeIdx]?.matterport_id || "");
                    }}
                  >
                    <span className="twin-play-btn">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                    <span className="twin-play-label">Start the tour</span>
                  </button>
                )}
              {activeMode === "route" && (
                <iframe
                  className="rv-exp-iframe"
                  src={iframeUrl}
                  title={`Route View ${safeIdx + 1}`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              )}
              {/* Digital-Twin lead gate STILL HELD OFF (founder 2026-07-13,
                  reaffirmed 2026-07-27 when the share toggle shipped without it:
                  don't capture DT leads yet — will re-enable later). Real OTP is
                  ready (netlify/functions/vr.js proxy live on stage); to turn it
                  back on, flip `false` → `leadgateOn`. On the share page the
                  toggle therefore drives the Meta Model gate only (leadgate=1|0
                  on the engine URL); the twin never gates regardless of `lg`. */}
              {activeMode === "twin" && isPublicViewer && false && (
                <MatterportLeadGate
                  // Re-mount per DT so the gate resets when the viewer switches
                  // between multiple twins on one project: a still-shown form
                  // from the previous DT clears, and the mount-time unlock check
                  // keeps an already-captured lead unlocked across all of them
                  // (unlock is keyed per project, not per twin).
                  key={`twin-${matterports[safeIdx]?.matterport_id || safeIdx}`}
                  enabled
                  agencyId={agencyId}
                  projectKey={projectSlug || projectId}
                  projectName={projectName}
                  sharerName={sharerName}
                  agencyName={agencyName}
                  agencyLogoUrl={agencyLogo}
                  sessionId={mp.sessionId || undefined}
                  sweepCount={mp.sweepsVisited}
                  totalSpaces={mp.totalSweeps || undefined}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="hero-iframe-slot" aria-label={`${modeLabel} iframe placeholder`}>
            <span className="hero-iframe-corner hero-iframe-corner--tl"></span>
            <span className="hero-iframe-corner hero-iframe-corner--tr"></span>
            <span className="hero-iframe-corner hero-iframe-corner--bl"></span>
            <span className="hero-iframe-corner hero-iframe-corner--br"></span>
            <div className="hero-iframe-body">
              <div className="hero-iframe-eyebrow">
                <span className="hero-live-dot"></span>
                IFRAME · {modeLabel.toUpperCase()}{showCycle ? ` ${safeIdx + 1}` : ""}
              </div>
              <div className="hero-iframe-title">No {modeLabel.toLowerCase()} URL available</div>
              <div className="hero-iframe-sub">
                This experience exists but has no embed URL configured yet.
              </div>
            </div>
          </div>
        )}

        <button
          className="hero-fs"
          aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <Icon.ExitFullscreen /> : <Icon.Fullscreen />}
        </button>

        {!isFullscreen && showCycle && (() => {
          const nextIdx = (safeIdx + 1) % activeCount;
          const nextName = itemName(activeMode, nextIdx);
          return (
            <button
              type="button"
              className="hero-exp-next"
              onClick={cycle}
              aria-label={`Switch to ${nextName}`}
            >
              <span className="hero-exp-next-meta">
                <span className="hero-exp-next-eyebrow">Next {modeLabel} · {String(nextIdx + 1).padStart(2, "0")}</span>
                <span className="hero-exp-next-title">{nextName}</span>
              </span>
              <span className="hero-exp-next-arrow" aria-hidden="true">→</span>
            </button>
          );
        })()}

        {!isFullscreen && showCycle && (() => {
          const currentName = itemName(activeMode, safeIdx);
          return (
            <div className="hero-exp-name" aria-label={`Now exploring ${currentName}`}>
              <span className="hero-exp-name-counter">
                <span className="hero-exp-name-counter-on">{String(safeIdx + 1).padStart(2, "0")}</span>
                <span className="hero-exp-name-counter-of">/{String(activeCount).padStart(2, "0")}</span>
              </span>
              <span className="hero-exp-name-text">
                <span className="hero-exp-name-eyebrow">Currently viewing</span>
                <span className="hero-exp-name-title">{currentName}</span>
              </span>
            </div>
          );
        })()}

        {/* Mobile-only multi-experience pill switcher — hidden on desktop via CSS.
            Default display:none; mobile @media flips it to display:flex.
            Renders current item name + either dots (≤4) or a "i / N" numeric
            counter (5+). Tap a dot → switch to that experience. */}
        {!isFullscreen && showCycle && (() => {
          const showDots = activeCount <= 4;
          return (
            <div className="hero-pill" aria-label={`Experience switcher · ${safeIdx + 1} of ${activeCount}`}>
              {showDots ? (
                <div className="hero-pill-dots" role="tablist" aria-label={`${modeLabel} experiences`}>
                  {Array.from({ length: activeCount }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={"hero-pill-dot" + (i === safeIdx ? " hero-pill-dot--on" : "")}
                      onClick={() => setIdx(i)}
                      aria-label={`Switch to ${itemName(activeMode, i)}`}
                      aria-selected={i === safeIdx}
                      role="tab"
                    />
                  ))}
                </div>
              ) : (
                <div className="hero-pill-count" aria-live="polite">
                  {safeIdx + 1} / {activeCount}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/* ── Tab bar ────────────────────────────────────────────────────── */
const TABS: { id: TabId; label: string; icon: React.FC }[] = [
  { id: "description", label: "Description", icon: Icon.Description },
  { id: "amenities", label: "Amenities", icon: Icon.Amenities },
  { id: "location", label: "Location", icon: Icon.Location },
  { id: "highlights", label: "Highlights", icon: Icon.Highlights },
  { id: "unit-details", label: "Unit Details", icon: Icon.UnitDetails },
  { id: "media", label: "Media", icon: Icon.Media },
];

function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: TabId; label: string; icon: React.FC }[];
  active: TabId;
  onChange: (id: TabId) => void;
}) {
  return (
    <div
      className="tabbar"
      style={{ ["--tab-count" as any]: tabs.length }}
    >
      {tabs.map((t) => {
        const I = t.icon;
        return (
          <button
            key={t.id}
            className={"tab" + (active === t.id ? " tab--on" : "")}
            onClick={() => onChange(t.id)}
          >
            <I />
            <span className="tab-label">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Description panel ──────────────────────────────────────── */
type DescriptionPanelProps = {
  description: DescriptionBlock;
  agencyName: string;
  agencyLogo: string;
  agencyAddress: string;
};
function DescriptionPanel({ description, agencyName, agencyLogo, agencyAddress }: DescriptionPanelProps) {
  const { headline, lede, body, quote } = description;
  // Body is markdown-ish with \n\n paragraph breaks. Split into paragraphs.
  const paragraphs = body
    ? body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    : [];
  const hasAnyDescription = headline || lede || paragraphs.length > 0 || quote;

  return (
    <div className="panel">
      <h3 className="panel-h">About The Property</h3>
      {!hasAnyDescription && (
        <p className="desc-lede" style={{ color: "var(--muted)" }}>No description available yet.</p>
      )}
      {headline && <p className="desc-headline">{headline}</p>}
      {lede && <p className="desc-lede">{lede}</p>}
      {paragraphs.length > 0 && (
        <div className="desc-body">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}

      {quote && (
        <>
          <div className="desc-divider">
            <span className="desc-divider-dot"></span>
          </div>
          <p className="desc-quote">"{quote}"</p>
        </>
      )}

      {/* "About The Developer" section is hidden across all project types
          while we redesign it. To re-enable, change `false &&` back to
          `(agencyName || agencyAddress) &&`. */}
      {false && (agencyName || agencyAddress) && (
        <div className="dev-section">
          <h3 className="panel-h">About The Developer</h3>
          <div className="dev-card">
            <div className="dev-logo">
              {agencyLogo
                ? <img src={agencyLogo} alt={agencyName} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                : (
                  <div className="dev-logo-img">
                    <span className="nsl">{(agencyName || "??").slice(0, 3).toUpperCase()}</span>
                  </div>
                )}
            </div>
            <div>
              {agencyName && <div className="dev-name">{agencyName}</div>}
              {agencyAddress && <div className="dev-addr">{agencyAddress}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Amenities panel ───────────────────────────────────────────
   Reads projectData.amenities (each entry has { amenity_id }) and joins
   against the master list (allAmenities, fetched from listAmenities()) to
   resolve names + category. Tiles are grouped by category. */
type AmenitiesPanelProps = {
  projectAmenities: any[];   // projectData.amenities
  allAmenities: any[];       // master catalog
};
const titleCase = (s: string): string =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function AmenitiesPanel({ projectAmenities, allAmenities }: AmenitiesPanelProps) {
  // Build dict id → master amenity.
  const byId: Record<string, any> = {};
  for (const a of allAmenities) if (a?._id) byId[a._id] = a;

  // Resolve each project amenity to its master entry; bucket by category.
  // slug is what drives the per-amenity icon (Material Design icons via
  // utils/amenityIcons.tsx → getAmenityIcon). Falls back to FALLBACK icon
  // if the slug isn't in the SLUG_ICON_MAP.
  type Resolved = { name: string; category: string; slug: string };
  const groupsMap: Record<string, Resolved[]> = {};
  for (const a of projectAmenities) {
    const master = byId[a?.amenity_id];
    const name: string = master?.name || a?.name || "";
    const category: string = master?.category || a?.category || "other";
    const slug: string = master?.slug || a?.slug || name;
    if (!name) continue;
    if (!groupsMap[category]) groupsMap[category] = [];
    groupsMap[category].push({ name, category, slug });
  }

  const groups = Object.entries(groupsMap)
    .map(([cat, items]) => ({ title: titleCase(cat), items }))
    .sort((a, b) => a.title.localeCompare(b.title));

  if (groups.length === 0) {
    return (
      <div className="panel">
        <div className="pdv3-state" style={{ padding: 24, color: "var(--muted)" }}>
          No amenities have been added for this project yet.
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      {groups.map((g) => (
        <div className="am-group" key={g.title}>
          <h4 className="am-group-title">{g.title}</h4>
          <div className="am-grid">
            {g.items.map((it, i) => (
              <div className="am-tile" key={`${it.name}-${i}`}>
                <span className="am-icon">{getAmenityIcon(it.slug, "")}</span>
                <span className="am-tile-name">{it.name}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Location panel ───────────────────────────────────────── */
/* POI categories. Each project gets 3 category groups, 2 POIs each.
   Right now POIs are auto-discovered via Google Places API around the project
   lat/lng (no backend storage). When backend ships location_pois[], we'll prefer
   curated data over auto-discovery. */
type PoiCategoryId = "tech_parks" | "schools" | "hospitals" | "malls" | "transit" | "landmarks";
type Poi = { name: string; sub?: string; km: number | string; minutes: number | string };
type PoiCategoryGroup = { category: PoiCategoryId; pois: Poi[] };

const POI_CATEGORY_LABEL: Record<PoiCategoryId, string> = {
  tech_parks: "Tech Parks",
  schools:    "Schools",
  hospitals:  "Hospitals",
  malls:      "Lifestyle & Malls",
  transit:    "Transit",
  landmarks:  "Landmarks",
};
const POI_CATEGORY_ICON: Record<PoiCategoryId, React.ComponentType> = {
  tech_parks: Icon.TechPark,
  schools:    Icon.School,
  hospitals:  Icon.Hospital,
  malls:      Icon.Mall,
  transit:    Icon.Transit,
  landmarks:  Icon.Landmark,
};
/* Google Places nearbySearch type+keyword per category.
   `tech_parks` isn't a first-class Places type, so we hit `establishment`
   with a keyword and let the relevance ranker do the work. */
const POI_PLACES_QUERY: Record<PoiCategoryId, { type: string; keyword?: string }> = {
  tech_parks: { type: "establishment", keyword: "tech park" },
  schools:    { type: "school" },
  hospitals:  { type: "hospital" },
  malls:      { type: "shopping_mall" },
  transit:    { type: "transit_station" },
  landmarks:  { type: "tourist_attraction" },
};

const ALL_POI_CATEGORIES: PoiCategoryId[] = [
  "tech_parks", "schools", "hospitals", "malls", "transit", "landmarks",
];

/* Haversine distance (km) between two lat/lng pairs. */
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/* Fisher-Yates shuffle (in-place is fine here, we copy first). */
function shuffle<T>(arr: T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/* Runs inside <Map> so it can pull the Places library + map instance.
   Picks 3 random categories, runs nearbySearch per category, keeps the 2
   closest results each, and surfaces them via onLoaded.
   Uses rankBy: DISTANCE which forbids radius — closest 20 returned. */
type NearbyPoiLoaderProps = {
  center: { lat: number; lng: number };
  categories: PoiCategoryId[];
  perCategory: number;
  onLoaded: (groups: PoiCategoryGroup[]) => void;
};
function NearbyPoiLoader({ center, categories, perCategory, onLoaded }: NearbyPoiLoaderProps): null {
  const map = useMap();
  const placesLib = useMapsLibrary("places");

  useEffect(() => {
    if (!map || !placesLib) return undefined;
    const service = new placesLib.PlacesService(map);

    const fetchOne = (cat: PoiCategoryId): Promise<PoiCategoryGroup | null> =>
      new Promise((resolve) => {
        const q = POI_PLACES_QUERY[cat];
        const request: google.maps.places.PlaceSearchRequest = {
          location: center,
          rankBy: google.maps.places.RankBy.DISTANCE,
          type: q.type,
        };
        if (q.keyword) request.keyword = q.keyword;
        service.nearbySearch(request, (results, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
            resolve(null);
            return;
          }
          // Dedupe by normalized name — Places returns separate entries per
          // platform/direction for transit stops (same name, different place_id).
          // Keep the first (closest, since rankBy: DISTANCE) instance per name.
          const seen = new Set<string>();
          const pois: Poi[] = [];
          for (const r of results) {
            if (!r.geometry?.location || !r.name) continue;
            const key = r.name.trim().toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            const km = haversineKm(center, {
              lat: r.geometry.location.lat(),
              lng: r.geometry.location.lng(),
            });
            pois.push({
              name: r.name,
              sub: r.vicinity || "",
              km: km.toFixed(1),
              minutes: Math.max(1, Math.round(km * 2.5)),
            });
            if (pois.length >= perCategory) break;
          }
          if (pois.length === 0) {
            resolve(null);
            return;
          }
          resolve({ category: cat, pois });
        });
      });

    let cancelled = false;
    Promise.all(categories.map(fetchOne)).then((groups) => {
      if (cancelled) return;
      onLoaded(groups.filter((g): g is PoiCategoryGroup => g !== null));
    });
    return () => { cancelled = true; };
    // Re-running only when location/category list changes; onLoaded is stable enough via setState identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, placesLib, center.lat, center.lng, categories.join("|"), perCategory]);

  return null;
}

type LocationPanelProps = {
  projectName: string;
  addressLine1: string;
  addressLine2: string;
  landmark: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  lat: number | null;
  lng: number | null;
  poiGroups: PoiCategoryGroup[];   // expected length 3
};
function LocationPanel({
  projectName,
  addressLine1, addressLine2, landmark, city, province, country, postalCode, lat, lng,
  poiGroups: poiGroupsProp,
}: LocationPanelProps) {
  void country; // address line uses it indirectly via fullAddress; keep to preserve prop shape
  const hasCoords = lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
  const position = hasCoords ? { lat: lat as number, lng: lng as number } : null;
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapId = process.env.REACT_APP_GOOGLE_MAP_ID;

  // POI groups: prefer curated data from backend if provided, otherwise auto-discover
  // 3 random categories × 2 nearest places each via Google Places nearbySearch.
  // Random pick is locked-in once per panel mount so it doesn't reshuffle on re-render.
  const [autoGroups, setAutoGroups] = useState<PoiCategoryGroup[]>([]);
  const [chosenCats] = useState<PoiCategoryId[]>(() => shuffle(ALL_POI_CATEGORIES).slice(0, 3));
  const useAuto = poiGroupsProp.length === 0;
  const poiGroups = useAuto ? autoGroups : poiGroupsProp;

  return (
    <div className="panel">
      <h3 className="panel-h">Location</h3>
      <div className="loc-row">
        <div className="map" style={{ overflow: "hidden", position: "relative" }}>
          {position && apiKey ? (
            <APIProvider apiKey={apiKey}>
              <Map
                defaultCenter={position}
                defaultZoom={16}
                mapId={mapId}
                gestureHandling="greedy"
                disableDefaultUI={false}
                style={{ width: "100%", height: "100%" }}
              >
                <AdvancedMarker position={position}>
                  {/* Branded callout pin per design/details-v5/LOCATION_PIN.md.
                      AdvancedMarker keeps it pinned to the lat/lng as the map pans/zooms.
                      Anchor is the classic teardrop pin (not a circle) to match the design render. */}
                  <div className="map-marker">
                    <div className="map-marker-card">
                      <span className="map-marker-ico"><Icon.CloudHouse /></span>
                      <span className="map-marker-text">
                        <span className="map-marker-eyebrow">PROJECT</span>
                        <span className="map-marker-name">{projectName}</span>
                      </span>
                    </div>
                    <span className="map-marker-stem" />
                    <span className="map-marker-anchor">
                      <span className="map-marker-pulse" />
                      <span className="map-marker-pulse map-marker-pulse--2" />
                      <svg className="map-marker-pin" viewBox="0 0 24 28" aria-hidden="true">
                        <path
                          d="M12 1.5C6.75 1.5 2.5 5.7 2.5 10.9c0 7.4 8.4 15.1 9 15.6.3.3.7.3 1 0 .6-.5 9-8.2 9-15.6 0-5.2-4.25-9.4-9.5-9.4z"
                          fill="#0E8C5C"
                          stroke="#FFFFFF"
                          strokeWidth="2"
                        />
                        <circle cx="12" cy="10.6" r="3.2" fill="#FFFFFF" />
                      </svg>
                    </span>
                  </div>
                </AdvancedMarker>
                {useAuto && (
                  <NearbyPoiLoader
                    center={position}
                    categories={chosenCats}
                    perCategory={2}
                    onLoaded={setAutoGroups}
                  />
                )}
              </Map>
            </APIProvider>
          ) : (
            <div style={{ padding: 24, color: "var(--muted)" }}>
              {hasCoords ? "Map unavailable (missing API key)." : "No location available."}
            </div>
          )}
        </div>

        <div className="poi-cats">
          {/* Address card sits above the categorized POIs as a compact reference. */}
          <div className="poi-address">
            <span className="poi-icon"><Icon.Location /></span>
            <div>
              <div className="poi-name">
                {[addressLine1, addressLine2].filter(Boolean).join(", ") || "Address"}
              </div>
              <div className="poi-sub">
                {[landmark && `Near ${landmark}`, city, province, postalCode && `PIN ${postalCode}`]
                  .filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>

          {/* Three category groups, two POIs per group. */}
          {poiGroups.length === 0 ? (
            <div className="poi-cats-empty">
              {useAuto ? "Finding nearby points of interest…" : "No nearby points of interest available."}
            </div>
          ) : (
            poiGroups.slice(0, 3).map((group) => {
              const CatIcon = POI_CATEGORY_ICON[group.category];
              return (
                <div className="poi-cat" key={group.category}>
                  <div className="poi-cat-h">
                    <span className="poi-cat-ico"><CatIcon /></span>
                    <span className="poi-cat-label">{POI_CATEGORY_LABEL[group.category]}</span>
                  </div>
                  {group.pois.slice(0, 2).map((p, i) => (
                    <div className="poi" key={i}>
                      <span className="poi-icon"><CatIcon /></span>
                      <div>
                        <div className="poi-name">{p.name}</div>
                        {p.sub && <div className="poi-sub">{p.sub}</div>}
                      </div>
                      <div>
                        <div className="poi-dist">{p.km} Km</div>
                        <div className="poi-dist-min">{p.minutes} min</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Highlights panel ─────────────────────────────────────── */
type HighlightStat = { num: string; unit: string; lbl: string; foot: string };
type HighlightsPanelProps = {
  reraNumber: string;            // empty string → "Not yet registered"
  possessionDate: string;        // ISO date or already-formatted string; empty → "Not specified"
  projectType: "apartment" | "villa";
  projectArea: { value?: number | string; unit?: string } | null;
  landmark: string;              // shown as the project-area footnote
  towers: number | null;
  floors: number | null;
  totalResidences: number;
  bhkOptions: number[];          // e.g. [2, 3, 4] for "2 · 3 · 4 BHK" footnote
  amenitiesCount: number;
  amenityCategoryCount: number; // distinct categories (e.g. Sports, Wellness) → "across N levels"
  clubhouseSqft: number | null;
};
function HighlightsPanel({
  reraNumber, possessionDate, projectType,
  projectArea, landmark, towers, floors, totalResidences, bhkOptions,
  amenitiesCount, amenityCategoryCount, clubhouseSqft,
}: HighlightsPanelProps) {
  const isVilla = projectType === "villa";

  // Build stats array from real schema. Only include cards that have a value
  // — the grid layout adapts to 6-up / 4-up / 3-up automatically via the
  // .hl-stats--{N}up class.
  const stats: HighlightStat[] = [];
  if (projectArea?.value != null) {
    stats.push({
      num: String(projectArea.value),
      unit: projectArea.unit || "",
      lbl: "Land Parcel",
      foot: landmark || "",
    });
  }
  if (!isVilla && towers != null) {
    stats.push({
      num: String(towers),
      unit: "",
      lbl: "Residential Towers",
      foot: towers > 1 ? `Tower A → ${String.fromCharCode(64 + towers)}` : "",
    });
  }
  if (!isVilla && floors != null) {
    stats.push({
      num: String(floors),
      unit: "fl",
      lbl: "Floors",
      foot: "each tower",
    });
  }
  if (totalResidences > 0) {
    stats.push({
      num: totalResidences.toLocaleString(),
      unit: "",
      lbl: isVilla ? "Total Villas" : "Total Residences",
      foot: bhkOptions.length > 0 ? bhkOptions.join(" · ") + " BHK" : "",
    });
  }
  if (amenitiesCount > 0) {
    stats.push({
      num: String(amenitiesCount),
      unit: "",
      lbl: "Amenities",
      foot: amenityCategoryCount > 0 ? `across ${amenityCategoryCount} levels` : "",
    });
  }
  if (clubhouseSqft != null && clubhouseSqft > 0) {
    stats.push({
      num: formatThousands(clubhouseSqft),
      unit: "sf",
      lbl: "Clubhouse",
      foot: "",
    });
  }

  const possessionFormatted = possessionDate
    ? (/^\d{4}-\d{2}/.test(possessionDate) ? formatMonthYear(possessionDate) : possessionDate)
    : "";

  return (
    <div className="panel">
      <h3 className="panel-h">The project, at a glance</h3>

      <div className="hl-badges">
        <div className={"hl-badge-line" + (reraNumber ? " hl-badge-line--ok" : " hl-badge-line--pending")}>
          <span className="hl-badge-ico"><Icon.Shield /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">RERA Registration</span>
            {reraNumber
              ? <span className="hl-badge-val hl-badge-val--mono">{reraNumber}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not yet registered</span>}
          </div>
        </div>
        <div className="hl-badge-line hl-badge-line--possession">
          <span className="hl-badge-ico"><Icon.Calendar /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">Possession</span>
            {possessionFormatted
              ? <span className="hl-badge-val">{possessionFormatted}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not specified</span>}
          </div>
        </div>
      </div>

      {stats.length > 0 && (
        <div className={"hl-stats hl-stats--" + stats.length + "up"}>
          {stats.map((s, i) => (
            <div className="hl-stat" key={i}>
              <div className="hl-stat-num">{s.num}<span className="hl-stat-unit">{s.unit}</span></div>
              <div className="hl-stat-lbl">{s.lbl}</div>
              {s.foot && <div className="hl-stat-foot">{s.foot}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Commercial Highlights panel ────────────────────────────────
   Commercial buildings have no "Units" — they have towers + floor plates.
   This panel rolls the residential Highlights + Units tabs into a single
   view: building grade badge, key metrics, then 1→N tower cards. */
type CommercialTower = {
  name?: string;
  number_of_floors?: number;
  floor_plate_sqft?: number;
};
type CommercialHighlightsPanelProps = {
  reraNumber: string;
  possessionDate: string;
  buildingGrade: string;
  totalLeasableSqft: number | null;
  parkingRatio: string;
  commonAreaSqft: number | null;
  towers: CommercialTower[];
  amenitiesCount: number;
  amenityCategoryCount: number;
  projectArea: { value?: number | string; unit?: string } | null;
  landmark: string;
};
function CommercialHighlightsPanel({
  reraNumber, possessionDate,
  buildingGrade, totalLeasableSqft, parkingRatio, commonAreaSqft,
  towers, amenitiesCount, amenityCategoryCount,
  projectArea, landmark,
}: CommercialHighlightsPanelProps) {
  // Commercial-only abbreviated sqft formatter: K (thousands),
  // L (lakhs = 100K), M (millions). Keeps Highlights numbers tight
  // since commercial values get very large. Residential keeps using
  // formatThousands so this only affects this panel.
  const fmtSqft = (n: number): string => {
    if (!Number.isFinite(n)) return "—";
    if (n < 1000) return String(n);
    if (n < 100000) return (Math.round(n / 100) / 10) + "K";
    if (n < 1000000) return (Math.round(n / 10000) / 10) + "L";
    return (Math.round(n / 100000) / 10) + "M";
  };
  const stats: HighlightStat[] = [];
  // 1. Building Grade — show just the suffix ("A+", "A", "B") not the
  // "Grade " prefix from the backend value. The footnote is a short
  // market-friendly descriptor for each grade.
  if (buildingGrade) {
    const gradeKey = buildingGrade.replace(/^Grade\s+/i, "").trim().toUpperCase();
    const gradeDescriptors: Record<string, string> = {
      "A+": "IGBC Platinum",
      "A": "IGBC Gold spec",
      "B": "Functional spec",
    };
    stats.push({
      num: gradeKey,
      unit: "",
      lbl: "Building Grade",
      foot: gradeDescriptors[gradeKey] || "",
    });
  }
  // 2. Land Parcel (project area + landmark/locality footnote)
  if (projectArea?.value != null) {
    stats.push({
      num: String(projectArea.value),
      unit: projectArea.unit || "",
      lbl: "Land Parcel",
      foot: landmark || "",
    });
  }
  // 3. Total Leasable Area (with tower count tucked into the footnote)
  if (totalLeasableSqft != null && totalLeasableSqft > 0) {
    stats.push({
      num: fmtSqft(totalLeasableSqft),
      unit: "sf",
      lbl: "Total Leasable Area",
      foot: towers.length > 0
        ? `across ${towers.length} ${towers.length === 1 ? "tower" : "towers"}`
        : "",
    });
  }
  // 4. Parking Ratio
  if (parkingRatio) {
    stats.push({
      num: parkingRatio,
      unit: "",
      lbl: "Parking Ratio",
      foot: "spaces : sqft",
    });
  }
  // 5. Amenities
  if (amenitiesCount > 0) {
    stats.push({
      num: String(amenitiesCount),
      unit: "",
      lbl: "Amenities",
      foot: amenityCategoryCount > 0 ? `across ${amenityCategoryCount} levels` : "",
    });
  }
  // 6. Common Area
  if (commonAreaSqft != null && commonAreaSqft > 0) {
    stats.push({
      num: fmtSqft(commonAreaSqft),
      unit: "sf",
      lbl: "Common Area",
      foot: "lobbies + decks",
    });
  }

  const possessionFormatted = possessionDate
    ? (/^\d{4}-\d{2}/.test(possessionDate) ? formatMonthYear(possessionDate) : possessionDate)
    : "";

  return (
    <div className="panel">
      <h3 className="panel-h">The building, at a glance</h3>

      <div className="hl-badges">
        <div className={"hl-badge-line" + (reraNumber ? " hl-badge-line--ok" : " hl-badge-line--pending")}>
          <span className="hl-badge-ico"><Icon.Shield /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">RERA Registration</span>
            {reraNumber
              ? <span className="hl-badge-val hl-badge-val--mono">{reraNumber}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not yet registered</span>}
          </div>
        </div>
        <div className="hl-badge-line hl-badge-line--possession">
          <span className="hl-badge-ico"><Icon.Calendar /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">Possession</span>
            {possessionFormatted
              ? <span className="hl-badge-val">{possessionFormatted}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not specified</span>}
          </div>
        </div>
      </div>

      {stats.length > 0 && (
        <div className={"hl-stats hl-stats--" + stats.length + "up"}>
          {stats.map((s, i) => (
            <div className="hl-stat" key={i}>
              <div className="hl-stat-num">{s.num}<span className="hl-stat-unit">{s.unit}</span></div>
              <div className="hl-stat-lbl">{s.lbl}</div>
              {s.foot && <div className="hl-stat-foot">{s.foot}</div>}
            </div>
          ))}
        </div>
      )}

      {towers.length > 0 && (
        <div className="hl-towers">
          <h4 className="hl-towers-h">{towers.length === 1 ? "Tower" : `Towers (${towers.length})`}</h4>
          <div className={"hl-tower-list" + (towers.length === 1 ? " hl-tower-list--single" : "")}>
            {towers.map((t, i) => {
              const floors = t.number_of_floors;
              const plate = t.floor_plate_sqft;
              return (
                <div className="hl-tower-card" key={i}>
                  <div className="hl-tower-head">
                    <span className="hl-tower-ico" aria-hidden>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="6" y="3" width="12" height="18" rx="1" />
                        <line x1="9" y1="7" x2="11" y2="7" />
                        <line x1="13" y1="7" x2="15" y2="7" />
                        <line x1="9" y1="11" x2="11" y2="11" />
                        <line x1="13" y1="11" x2="15" y2="11" />
                        <line x1="9" y1="15" x2="11" y2="15" />
                        <line x1="13" y1="15" x2="15" y2="15" />
                      </svg>
                    </span>
                    <span className="hl-tower-name">
                      {t.name || `Tower ${String.fromCharCode(65 + i)}`}
                    </span>
                  </div>
                  <div className="hl-tower-stats">
                    <div className="hl-tower-stat">
                      <div className="hl-tower-stat-num">{floors ?? "—"}</div>
                      <div className="hl-tower-stat-lbl">Floors</div>
                    </div>
                    <div className="hl-tower-stat">
                      <div className="hl-tower-stat-num">
                        {plate != null ? fmtSqft(plate) : "—"}
                        {plate != null && <span className="hl-tower-stat-unit">sf</span>}
                      </div>
                      <div className="hl-tower-stat-lbl">Floor Plate</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Unit Details panel ─────────────────────────────────────────
   Reads projectData.project_type_details.unit_configurations[] — each entry
   has { name, facing, size_sqft, count, bhk }. Left column lists units; right
   column shows specs of the selected one. No floor plans yet — backend
   schema doesn't carry them. */
type UnitConfig = {
  name?: string;
  facing?: string;
  size_sqft?: number;
  count?: number;
  bhk?: number;
};
type UnitDetailsPanelProps = { unitConfigurations: UnitConfig[] };

const formatFacing = (f?: string): string => {
  if (!f) return "—";
  const key = f.toLowerCase();
  const map: Record<string, string> = {
    north: "North", south: "South", east: "East", west: "West",
    "north-east": "North-East", "north-west": "North-West",
    "south-east": "South-East", "south-west": "South-West",
  };
  return map[key] || titleCase(f);
};

/* Stub description per unit (30-40 words). Deterministic on the unit's specs
   so it stays stable across re-renders. Real copy from backend will replace
   this when a description field lands on unit_configurations. */
const UNIT_DESC_VARIANTS = [
  "A thoughtfully proportioned home where bedrooms open to natural light and the living spaces invite easy gatherings. The layout balances private quarters with airy, connected commons — designed for everyday calm and weekend ease.",
  "Generous balconies and a wide central living core anchor this configuration. Every bedroom is treated as a retreat, while the kitchen and dining flow without barrier — built for families who value openness and comfort in equal measure.",
  "Cross-ventilated and quietly luxurious, this layout pairs efficient floor planning with refined finishes. Long sight-lines from entry to balcony pull the outdoors in, and bedroom positioning offers a clear separation from common living areas.",
  "Designed for residents who want both privacy and entertaining flow, this home offers a clear master-bedroom wing alongside an expansive shared living-and-dining zone. Tall windows, soft daylight, and a layered material palette define the experience.",
];
const stubUnitDescription = (u: UnitConfig, i: number): string => {
  const variant = UNIT_DESC_VARIANTS[i % UNIT_DESC_VARIANTS.length];
  const bhk = u.bhk != null ? `${u.bhk} BHK` : "this residence";
  const facing = u.facing ? `${formatFacing(u.facing)}-facing ` : "";
  const size = u.size_sqft ? `${formatThousands(u.size_sqft)} sq.ft. ` : "";
  return `${facing}${bhk} · ${size}— ${variant}`.trim();
};

/* ── Layout (plotted gated community) panels ────────────────────
   Same shape as residential but the unit type is `PlotUnitConfiguration`
   (no BHK). Highlights surfaces plot totals + clubhouse + amenities, and
   the Plots tab replaces "Unit Details" with a plot-oriented list/detail. */
type LayoutHighlightsPanelProps = {
  reraNumber: string;
  possessionDate: string;
  projectArea: { value?: number | string; unit?: string } | null;
  landmark: string;
  totalPlots: number;
  uniquePlotTypes: number;
  clubhouseSqft: number | null;
  amenitiesCount: number;
  amenityCategoryCount: number;
};
function LayoutHighlightsPanel({
  reraNumber, possessionDate,
  projectArea, landmark,
  totalPlots, uniquePlotTypes, clubhouseSqft,
  amenitiesCount, amenityCategoryCount,
}: LayoutHighlightsPanelProps) {
  const stats: HighlightStat[] = [];
  if (projectArea?.value != null) {
    stats.push({
      num: String(projectArea.value),
      unit: projectArea.unit || "",
      lbl: "Land Parcel",
      foot: landmark || "",
    });
  }
  if (totalPlots > 0) {
    stats.push({
      num: totalPlots.toLocaleString(),
      unit: "",
      lbl: "Total Plots",
      foot: uniquePlotTypes > 0
        ? `${uniquePlotTypes} unique ${uniquePlotTypes === 1 ? "type" : "types"}`
        : "",
    });
  }
  if (clubhouseSqft != null && clubhouseSqft > 0) {
    stats.push({
      num: formatThousands(clubhouseSqft),
      unit: "sf",
      lbl: "Clubhouse",
      foot: "",
    });
  }
  if (amenitiesCount > 0) {
    stats.push({
      num: String(amenitiesCount),
      unit: "",
      lbl: "Amenities",
      foot: amenityCategoryCount > 0 ? `across ${amenityCategoryCount} levels` : "",
    });
  }

  const possessionFormatted = possessionDate
    ? (/^\d{4}-\d{2}/.test(possessionDate) ? formatMonthYear(possessionDate) : possessionDate)
    : "";

  return (
    <div className="panel">
      <h3 className="panel-h">The development, at a glance</h3>

      <div className="hl-badges">
        <div className={"hl-badge-line" + (reraNumber ? " hl-badge-line--ok" : " hl-badge-line--pending")}>
          <span className="hl-badge-ico"><Icon.Shield /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">RERA Registration</span>
            {reraNumber
              ? <span className="hl-badge-val hl-badge-val--mono">{reraNumber}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not yet registered</span>}
          </div>
        </div>
        <div className="hl-badge-line hl-badge-line--possession">
          <span className="hl-badge-ico"><Icon.Calendar /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">Possession</span>
            {possessionFormatted
              ? <span className="hl-badge-val">{possessionFormatted}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not specified</span>}
          </div>
        </div>
      </div>

      {stats.length > 0 && (
        <div className={"hl-stats hl-stats--" + stats.length + "up"}>
          {stats.map((s, i) => (
            <div className="hl-stat" key={i}>
              <div className="hl-stat-num">{s.num}<span className="hl-stat-unit">{s.unit}</span></div>
              <div className="hl-stat-lbl">{s.lbl}</div>
              {s.foot && <div className="hl-stat-foot">{s.foot}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Farmland highlights — like Layout but no Clubhouse (FarmlandProjectType
   has no clubhouse_size_sqft on the backend). Plots panel is shared with
   Layout since the unit shape (PlotUnitConfiguration) is identical. */
type FarmlandHighlightsPanelProps = {
  reraNumber: string;
  possessionDate: string;
  projectArea: { value?: number | string; unit?: string } | null;
  landmark: string;
  totalPlots: number;
  uniquePlotTypes: number;
  amenitiesCount: number;
  amenityCategoryCount: number;
};
function FarmlandHighlightsPanel({
  reraNumber, possessionDate,
  projectArea, landmark,
  totalPlots, uniquePlotTypes,
  amenitiesCount, amenityCategoryCount,
}: FarmlandHighlightsPanelProps) {
  const stats: HighlightStat[] = [];
  if (projectArea?.value != null) {
    stats.push({
      num: String(projectArea.value),
      unit: projectArea.unit || "",
      lbl: "Land Parcel",
      foot: landmark || "",
    });
  }
  if (totalPlots > 0) {
    stats.push({
      num: totalPlots.toLocaleString(),
      unit: "",
      lbl: "Total Plots",
      foot: uniquePlotTypes > 0
        ? `${uniquePlotTypes} unique ${uniquePlotTypes === 1 ? "type" : "types"}`
        : "",
    });
  }
  if (amenitiesCount > 0) {
    stats.push({
      num: String(amenitiesCount),
      unit: "",
      lbl: "Amenities",
      foot: amenityCategoryCount > 0 ? `across ${amenityCategoryCount} levels` : "",
    });
  }

  const possessionFormatted = possessionDate
    ? (/^\d{4}-\d{2}/.test(possessionDate) ? formatMonthYear(possessionDate) : possessionDate)
    : "";

  return (
    <div className="panel">
      <h3 className="panel-h">The farmland, at a glance</h3>

      <div className="hl-badges">
        <div className={"hl-badge-line" + (reraNumber ? " hl-badge-line--ok" : " hl-badge-line--pending")}>
          <span className="hl-badge-ico"><Icon.Shield /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">RERA Registration</span>
            {reraNumber
              ? <span className="hl-badge-val hl-badge-val--mono">{reraNumber}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not yet registered</span>}
          </div>
        </div>
        <div className="hl-badge-line hl-badge-line--possession">
          <span className="hl-badge-ico"><Icon.Calendar /></span>
          <div className="hl-badge-text">
            <span className="hl-badge-label">Possession</span>
            {possessionFormatted
              ? <span className="hl-badge-val">{possessionFormatted}</span>
              : <span className="hl-badge-val hl-badge-val--pending">Not specified</span>}
          </div>
        </div>
      </div>

      {stats.length > 0 && (
        <div className={"hl-stats hl-stats--" + stats.length + "up"}>
          {stats.map((s, i) => (
            <div className="hl-stat" key={i}>
              <div className="hl-stat-num">{s.num}<span className="hl-stat-unit">{s.unit}</span></div>
              <div className="hl-stat-lbl">{s.lbl}</div>
              {s.foot && <div className="hl-stat-foot">{s.foot}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Plot description variants — deterministic, plot-oriented copy used until
   backend carries a description field on plot configurations. */
const PLOT_DESC_VARIANTS = [
  "A well-defined plot with generous setbacks and a thoughtful orientation, well-suited for a custom residence built to the owner's preference. Direct access to the development's clubhouse and shared landscape.",
  "Wide frontage and a deliberate orientation lend this plot a sense of openness — favorable for natural light, cross-ventilation, and a home designed around the project's amenity core.",
  "Positioned within the gated community's curated layout, this plot offers a calm setting with direct connection to roads, utilities, and the clubhouse — a clear canvas for a thoughtfully proportioned home.",
  "Proportioned for a single residence with comfortable setbacks, this plot pairs a balanced facing direction with proximity to shared amenities — built for owners who want both privacy and community.",
];
type PlotConfig = {
  name?: string; facing?: string; size_sqft?: number; count?: number;
  width_m?: number; depth_m?: number;
};
/* Stored dimensions are in metres (matches the source survey/zoning
   drawing); converted to feet here for display only, since plot buyers in
   India think in L x B feet, not metres. Round each side independently to
   the nearest whole foot -- the standard way plot dimensions are quoted
   -- so this can read a few sqft off from the advertised plot area shown
   elsewhere (double rounding); that's expected, not a bug. */
const M_TO_FT = 3.280839895;
const stubPlotDescription = (p: PlotConfig, i: number): string => {
  const variant = PLOT_DESC_VARIANTS[i % PLOT_DESC_VARIANTS.length];
  const facing = p.facing ? `${formatFacing(p.facing)}-facing ` : "";
  const size = p.size_sqft ? `${formatThousands(p.size_sqft)} sq.ft. plot ` : "";
  const dims = p.width_m && p.depth_m
    ? `(${Math.round(p.width_m * M_TO_FT)}ft × ${Math.round(p.depth_m * M_TO_FT)}ft) `
    : "";
  return `${facing}${size}${dims}— ${variant}`.trim();
};

function LayoutPlotsPanel({ plotConfigurations }: { plotConfigurations: PlotConfig[] }) {
  const plots = plotConfigurations;
  const [sel, setSel] = useState(0);

  if (plots.length === 0) {
    return (
      <div className="panel">
        <h3 className="panel-h">All Plot Types</h3>
        <div className="pdv3-state" style={{ padding: 24, color: "var(--muted)" }}>
          No plot configurations have been added for this project yet.
        </div>
      </div>
    );
  }

  const safeSel = Math.min(sel, plots.length - 1);
  const current = plots[safeSel];

  return (
    <div className="panel">
      <h3 className="panel-h">All Plot Types</h3>
      <div className="units-row">
        <div className="units-list">
          {plots.map((p, i) => {
            // Always render "Type N" — drop "Layout "/"Plot " prefixes from
            // stored names so existing data and new data look the same.
            const label = `Type ${i + 1}${p.size_sqft ? ` · ${formatThousands(p.size_sqft)} sqft` : ""}`;
            return (
              <button
                key={i}
                className={"unit-item" + (i === safeSel ? " unit-item--on" : "")}
                onClick={() => setSel(i)}
              >
                <span className="unit-item-name">{label}</span>
                <span className="unit-item-count">{p.count ?? 0}</span>
              </button>
            );
          })}
        </div>

        <div>
          <div className="unit-detail-card">
            <div className="unit-specs unit-specs--top">
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Area /> Plot Size</div>
                <div className="unit-spec-val">
                  {current.size_sqft ? `${formatThousands(current.size_sqft)} sq.ft.` : "—"}
                </div>
              </div>
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Binoculars /> Facing</div>
                <div className="unit-spec-val">{formatFacing(current.facing)}</div>
              </div>
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Building /> Plots Available</div>
                <div className="unit-spec-val">{current.count ?? 0} Plots</div>
              </div>
            </div>
            <p className="unit-desc-body">{stubPlotDescription(current, safeSel)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function UnitDetailsPanel({ unitConfigurations }: UnitDetailsPanelProps) {
  const units = unitConfigurations;
  const [sel, setSel] = useState(0);

  if (units.length === 0) {
    return (
      <div className="panel">
        <h3 className="panel-h">All Units</h3>
        <div className="pdv3-state" style={{ padding: 24, color: "var(--muted)" }}>
          No unit configurations have been added for this project yet.
        </div>
      </div>
    );
  }

  const safeSel = Math.min(sel, units.length - 1);
  const current = units[safeSel];

  return (
    <div className="panel">
      <h3 className="panel-h">All Units</h3>
      <div className="units-row">
        <div className="units-list">
          {units.map((u, i) => {
            // Derive label from BHK + size for residential — keeps villa and
            // apartment consistent ("3BHK · 4,000 sqft" rather than letting the
            // stored `name` add suffixes like "Villa" / "Bungalow").
            const bhkLabel = u.bhk != null ? `${u.bhk}BHK` : (u.name || `Unit ${i + 1}`);
            const label = `${bhkLabel}${u.size_sqft ? ` · ${formatThousands(u.size_sqft)} sqft` : ""}`;
            return (
              <button
                key={i}
                className={"unit-item" + (i === safeSel ? " unit-item--on" : "")}
                onClick={() => setSel(i)}
              >
                <span className="unit-item-name">{label}</span>
                <span className="unit-item-count">{u.count ?? 0}</span>
              </button>
            );
          })}
        </div>

        <div>
          <div className="unit-detail-card">
            <div className="unit-specs unit-specs--top">
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Bed /> Configuration</div>
                <div className="unit-spec-val">{current.bhk != null ? `${current.bhk} BHK` : "—"}</div>
              </div>
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Area /> Carpet Area</div>
                <div className="unit-spec-val">
                  {current.size_sqft ? `${formatThousands(current.size_sqft)} sq.ft.` : "—"}
                </div>
              </div>
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Binoculars /> Facing</div>
                <div className="unit-spec-val">{formatFacing(current.facing)}</div>
              </div>
              <div className="unit-spec">
                <div className="unit-spec-lbl"><Icon.Building /> Units Available</div>
                <div className="unit-spec-val">{current.count ?? 0} Units</div>
              </div>
            </div>
            <p className="unit-desc-body">{stubUnitDescription(current, safeSel)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* YouTube helpers — pull the video id from any common URL shape so we can
   point to the static thumbnail (img.youtube.com/vi/<id>/hqdefault.jpg)
   instead of eagerly loading every video iframe on tab open. */
const getYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([^?&/#]+)/);
  return m?.[1] || null;
};
const getYouTubeThumbnail = (url: string): string | null => {
  const id = getYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};
const toYouTubeEmbed = (url: string): string => {
  const id = getYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : url;
};

/* ── Media lightbox ─────────────────────────────────────────────
   Fullscreen overlay shared by Photos and Videos. Portaled to document.body
   so it escapes the panel's stacking context. ESC closes, ← → cycle. */
type LightboxItem = { kind: "image" | "video"; url: string };
type MediaLightboxProps = {
  items: LightboxItem[];
  startIndex: number;
  onClose: () => void;
};
function MediaLightbox({ items, startIndex, onClose }: MediaLightboxProps) {
  const [idx, setIdx] = useState(startIndex);
  const safeIdx = Math.max(0, Math.min(idx, items.length - 1));
  const current = items[safeIdx];

  useEffect(() => { setIdx(startIndex); }, [startIndex]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft")  setIdx((i) => (i - 1 + items.length) % items.length);
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % items.length);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [items.length, onClose]);

  if (!current) return null;

  const node = (
    <div className="pdv3-root lightbox-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <button className="lightbox-close" onClick={onClose} aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="lightbox-counter">{safeIdx + 1} / {items.length}</div>
      {items.length > 1 && (
        <>
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + items.length) % items.length); }}
            aria-label="Previous"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % items.length); }}
            aria-label="Next"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}
      <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
        {current.kind === "image" ? (
          <img className="lightbox-img" src={current.url} alt={`Item ${safeIdx + 1}`} />
        ) : (
          <iframe
            className="lightbox-video"
            src={toYouTubeEmbed(current.url)}
            title={`Video ${safeIdx + 1}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* ── Brochure viewer (fullscreen inline PDF) ────────────────────
   The PDF backend serves with Content-Disposition: attachment, which forces
   download for both <a target="_blank"> and <iframe src=url>. The only
   frontend workaround is to fetch the bytes ourselves and serve them via a
   blob: URL (same-origin, no disposition header → browser inlines). This
   requires CORS to be enabled on the bucket; if it's not, the fetch errors
   and we surface a clear message rather than silently triggering a download. */
type BrochureViewerProps = {
  url: string;
  name: string;
  onClose: () => void;
};
function BrochureViewer({ url, name, onClose }: BrochureViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let createdBlob: string | null = null;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        // Always force application/pdf — GCS often serves PDFs as
        // application/octet-stream when uploaded without an explicit Content-Type,
        // and octet-stream in an iframe triggers a download instead of inlining.
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        createdBlob = URL.createObjectURL(pdfBlob);
        setBlobUrl(createdBlob);
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMsg(
          "Inline preview isn't available for this PDF (the file's host doesn't allow cross-origin reads). Use Download instead."
        );
      });

    return () => {
      cancelled = true;
      if (createdBlob) URL.revokeObjectURL(createdBlob);
    };
  }, [url]);

  const node = (
    <div className="pdv3-root lightbox-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <button className="lightbox-close" onClick={onClose} aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="lightbox-counter">{name}</div>
      <div className="lightbox-stage brochure-stage" onClick={(e) => e.stopPropagation()}>
        {errorMsg ? (
          <div className="brochure-status brochure-status--err">
            {errorMsg}
            <div style={{ marginTop: 16 }}>
              <a className="brochure-btn" href={url} download target="_blank" rel="noopener noreferrer">
                <Icon.Download /> Download PDF
              </a>
            </div>
          </div>
        ) : !blobUrl ? (
          <div className="brochure-status">Loading brochure…</div>
        ) : (
          <iframe className="brochure-viewer-frame" src={blobUrl} title={name} />
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* ── Media panel ────────────────────────────────────────────────
   Reads three projectData fields:
   - project_images: string[]   (URLs)
   - property_videos: string[]  (YouTube embed URLs or direct video URLs)
   - project_brochure: string   (single PDF URL)
   Photos: hero + 2×2 preview grid + "View all" overlay → lightbox.
   Videos: static YouTube thumbnails (no iframe until clicked) → lightbox.
   Brochure: inline PDF viewer modal + size + page count meta. */
type MediaPanelProps = {
  images: string[];
  videos: string[];
  brochureUrl: string;
  projectName: string;
};
function MediaPanel({ images, videos, brochureUrl, projectName }: MediaPanelProps) {
  const [sec, setSec] = useState<"photos" | "videos" | "brochure">("photos");
  const [lightbox, setLightbox] = useState<{ items: LightboxItem[]; start: number } | null>(null);
  const [brochureViewer, setBrochureViewer] = useState(false);
  // Brochure metadata — size from a HEAD request (Content-Length), page count
  // from pdf.js getDocument. Both run once per brochureUrl, lazy-loaded.
  const [brochureMeta, setBrochureMeta] = useState<{ pages: number | null; sizeMB: number | null }>({
    pages: null, sizeMB: null,
  });

  useEffect(() => {
    if (!brochureUrl) return undefined;
    let cancelled = false;

    // 1) Lightweight: HEAD for content size.
    fetch(brochureUrl, { method: "HEAD" })
      .then((r) => {
        const len = r.headers.get("content-length");
        if (!cancelled && len) {
          const mb = Number(len) / (1024 * 1024);
          setBrochureMeta((m) => ({ ...m, sizeMB: Math.round(mb * 10) / 10 }));
        }
      })
      .catch(() => { /* CORS or network — silently skip the size badge */ });

    // 2) Heavier: pdf.js for the page count. Dynamic-imported so the worker
    //    bundle doesn't load until we're on this panel. Worker is pulled from
    //    jsdelivr (no .mjs ambient type in node_modules for a local import).
    (async () => {
      try {
        const pdfjs: any = await import("pdfjs-dist");
        if (pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
          pdfjs.GlobalWorkerOptions.workerSrc =
            `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        }
        const task = pdfjs.getDocument({ url: brochureUrl });
        const pdf = await task.promise;
        if (!cancelled) setBrochureMeta((m) => ({ ...m, pages: pdf.numPages }));
      } catch { /* parse failed — graceful degrade, hide page count */ }
    })();

    return () => { cancelled = true; };
  }, [brochureUrl]);

  const openPhotos = (start: number) => {
    setLightbox({
      items: images.map((url) => ({ kind: "image" as const, url })),
      start,
    });
  };
  const openVideos = (start: number) => {
    setLightbox({
      items: videos.map((url) => ({ kind: "video" as const, url })),
      start,
    });
  };

  return (
    <div className="panel">
      <h3 className="panel-h">Media Gallery</h3>
      <div className="media-row">
        <div className="media-side">
          <button className={"media-tab" + (sec === "photos" ? " is-on" : "")} onClick={() => setSec("photos")}>
            <span className="media-tab-ico"><Icon.Media /></span>
            <span className="media-tab-name">Photos</span>
            <span className="media-tab-count">{images.length}</span>
          </button>
          <button className={"media-tab" + (sec === "videos" ? " is-on" : "")} onClick={() => setSec("videos")}>
            <span className="media-tab-ico"><Icon.Play /></span>
            <span className="media-tab-name">Videos</span>
            <span className="media-tab-count">{videos.length}</span>
          </button>
          <button className={"media-tab" + (sec === "brochure" ? " is-on" : "")} onClick={() => setSec("brochure")}>
            <span className="media-tab-ico"><Icon.PDF /></span>
            <span className="media-tab-name">Brochure</span>
            <span className="media-tab-count">{brochureUrl ? 1 : 0}</span>
          </button>
        </div>

        <div className="media-main">
          {sec === "photos" && (
            images.length === 0 ? (
              <div className="pdv3-state" style={{ padding: 24, color: "var(--muted)" }}>
                No photos available.
              </div>
            ) : (
              <div className="photo-layout">
                {/* Hero (first image) */}
                <button
                  className="photo-hero photo-tile"
                  type="button"
                  onClick={() => openPhotos(0)}
                  aria-label="Open photo gallery"
                  style={{
                    backgroundImage: `url("${images[0]}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {images.length > 5 && (
                    <span className="photo-view-all">
                      <Icon.Media /> View all {images.length} photos
                    </span>
                  )}
                </button>
                {/* 2×2 preview grid — images 1..4. If fewer than 5 total, this
                    grid fills with whatever remains and the "View all" button
                    is hidden (already covered by the hero condition above). */}
                {images.length > 1 && (
                  <div className="photo-preview-grid">
                    {images.slice(1, 5).map((src, i) => {
                      const realIndex = i + 1;
                      const isLast = i === 3 && images.length > 5;
                      return (
                        <button
                          key={realIndex}
                          type="button"
                          className="photo-tile photo-mini"
                          onClick={() => openPhotos(realIndex)}
                          aria-label={`Open photo ${realIndex + 1}`}
                          style={{
                            backgroundImage: `url("${src}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        >
                          {isLast && (
                            <span className="photo-more-overlay">+{images.length - 5} more</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          )}
          {sec === "videos" && (
            videos.length === 0 ? (
              <div className="pdv3-state" style={{ padding: 24, color: "var(--muted)" }}>
                No videos available.
              </div>
            ) : (
              <div className="videos-grid">
                {videos.map((url, i) => {
                  const thumb = getYouTubeThumbnail(url);
                  return (
                    <button
                      key={i}
                      type="button"
                      className="video-card"
                      onClick={() => openVideos(i)}
                      aria-label={`Play video ${i + 1}`}
                      style={
                        thumb
                          ? {
                              backgroundImage: `url("${thumb}")`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    >
                      <span className="video-play">
                        <span className="video-play-icon"><Icon.Play /></span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )
          )}
          {sec === "brochure" && (
            !brochureUrl ? (
              <div className="pdv3-state" style={{ padding: 24, color: "var(--muted)" }}>
                No brochure available.
              </div>
            ) : (
              <div className="brochure-card">
                <span className="brochure-icon"><Icon.PDF /></span>
                <div className="brochure-info">
                  <div className="brochure-name">{projectName} Brochure</div>
                  <div className="brochure-fmt">
                    {[
                      "PDF",
                      brochureMeta.pages != null ? `${brochureMeta.pages} page${brochureMeta.pages === 1 ? "" : "s"}` : null,
                      brochureMeta.sizeMB != null ? `${brochureMeta.sizeMB} MB` : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="brochure-actions">
                  <button
                    type="button"
                    className="brochure-btn"
                    onClick={() => setBrochureViewer(true)}
                  >
                    <Icon.Eye /> View PDF
                  </button>
                  <a className="brochure-btn" href={brochureUrl} download target="_blank" rel="noopener noreferrer">
                    <Icon.Download /> Download PDF
                  </a>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      {lightbox && (
        <MediaLightbox
          items={lightbox.items}
          startIndex={lightbox.start}
          onClose={() => setLightbox(null)}
        />
      )}
      {brochureViewer && brochureUrl && (
        <BrochureViewer
          url={brochureUrl}
          name={`${projectName} Brochure`}
          onClose={() => setBrochureViewer(false)}
        />
      )}
    </div>
  );
}

/* ── Share control (mints a real backend link + 7 channel buttons) ──
   POSTs /api/v1/share-links on first open (backend is idempotent — same
   user+project → same code). Every channel button appends &src=<channel>
   to the URL it shares so the click recorder gets attribution data.
   QR sub-view renders the link as a scannable image via the
   qrserver.com public API (no extra dep). */
function ShareControl({
  projectId,
  leadAttribution = true,
  projectName = "",
  locationStr = "",
  configSummary = "",
  experienceFlags,
}: {
  projectId: string;
  leadAttribution?: boolean;
  projectName?: string;
  locationStr?: string;
  configSummary?: string;
  experienceFlags?: { meta: boolean; twin: boolean; route: boolean };
}) {
  type Status = "idle" | "generating" | "ready" | "error";
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [shareUrl, setShareUrl] = useState<string>("");
  const [shareCode, setShareCode] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  // Lead capture toggle: ON (default) arms the gate on shared views; OFF appends
  // &lg=0 so the recipient explores freely (e.g. sharing with a friend).
  const [captureLeads, setCaptureLeads] = useState(true);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const closeModal = () => {
    setOpen(false);
    setShowQR(false);
  };

  const mintLink = async () => {
    if (!projectId) return;
    setStatus("generating");
    try {
      const data = await createShareLink(projectId);
      setShareUrl(data?.share_url || "");
      setShareCode(data?.share_code || "");
      setStatus(data?.share_url ? "ready" : "error");
    } catch (_e) {
      setStatus("error");
    }
  };

  const onShare = () => {
    setOpen(true);
    // Mint on first open only (idempotent on the backend either way, but no
    // reason to spin the network on every reopen).
    if (status === "idle" || status === "error") void mintLink();
  };

  /* Helper: append the channel tag to the share URL the user actually
     forwards. Backend reads `?src=<channel>` from the public viewer URL
     and records it on the click row for per-channel analytics. */
  const withSrc = (src: string) => {
    if (!shareUrl) return "";
    const sep = shareUrl.includes("?") ? "&" : "?";
    return `${shareUrl}${sep}src=${encodeURIComponent(src)}${captureLeads ? "" : "&lg=0"}`;
  };

  /* ── Branded short links (my.<host>/s/<code>) — frontend-only via the
     /s/:code route (no backend; the code lives in the path, not ?ref). Host:
     stage.openskyvr.com → my.stage.openskyvr.com, openskyvr.com → my.openskyvr.com. */
  const shortHost = () => {
    try { return `my.${new URL(shareUrl).host}`; } catch (_e) { return ""; }
  };
  // With scheme — clickable / scannable links (all channels except SMS).
  const shortUrl = (src?: string) => {
    const host = shortHost();
    if (!shareUrl || !shareCode || !host) return src ? withSrc(src) : shareUrl;
    const params = [src ? `src=${encodeURIComponent(src)}` : "", captureLeads ? "" : "lg=0"].filter(Boolean);
    return `https://${host}/s/${shareCode}${params.length ? `?${params.join("&")}` : ""}`;
  };
  // No scheme — SMS only (saves chars; SMS apps auto-link the bare domain).
  const shortUrlNoScheme = (src: string) => {
    const host = shortHost();
    if (!shareUrl || !shareCode || !host) return withSrc(src);
    const params = [`src=${encodeURIComponent(src)}`, captureLeads ? "" : "lg=0"].filter(Boolean);
    return `${host}/s/${shareCode}?${params.join("&")}`;
  };

  const copy = (src: string = "copy") => {
    const url = shortUrl(src);
    if (!url) return;
    try { navigator.clipboard.writeText(url); } catch (_e) { /* no-op */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  // ── Per-channel tailored share messages ──
  const sharerName = (UserService.getFullName && UserService.getFullName()) || "";
  const name = projectName || "this project";
  // Build emoji at RUNTIME from code points. Both literal emoji and \u escapes
  // were rendering as the replacement char — the build/serve pipeline mangles
  // non-ASCII in the bundle. String.fromCodePoint keeps the source AND bundle
  // pure ASCII (just hex numbers); the emoji is assembled in the browser.
  const cp = (...c: number[]) => String.fromCodePoint(...c);
  const E = {
    home: cp(0x1f3e1),          // house with garden
    pin: cp(0x1f4cd),           // round pushpin
    build: cp(0x1f3d7, 0xfe0f), // building construction
    cube: cp(0x1f9ca),          // ice cube
    house: cp(0x1f3e0),         // house
    cam: cp(0x1f3a5),           // movie camera
    play: cp(0x25b6, 0xfe0f),   // play button
    point: cp(0x1f447),         // backhand index down
  };
  // Experience lines — only the experiences this project actually has.
  const expParts = ([
    experienceFlags?.meta && { e: `${E.cube} 3D Meta Model walkthrough`, p: "3D Meta Model walkthrough" },
    experienceFlags?.twin && { e: `${E.house} Digital Twin tour`, p: "Digital Twin tour" },
    experienceFlags?.route && { e: `${E.cam} 360° Route View`, p: "360° Route View" },
  ].filter(Boolean) as { e: string; p: string }[]);
  const expEmoji = expParts.map((x) => x.e);
  const expBullets = expParts.map((x) => `• ${x.p}`);
  const expInline = expParts.map((x) => x.p).join(", ");

  // WhatsApp — *bold* renders, emoji, link inline at the end.
  const waLines: string[] = [`${E.home} *${name}* — step inside in 3D`, ""];
  if (locationStr) waLines.push(`${E.pin} ${locationStr}`);
  if (configSummary) waLines.push(`${E.build} ${configSummary}`);
  waLines.push("");
  if (expEmoji.length) waLines.push("Don't just scroll photos — *explore the real spaces*:", ...expEmoji);
  else waLines.push("*Explore it in immersive 3D.*");
  waLines.push("", `${E.play} Start the experience:`, shortUrl("whatsapp"));
  if (sharerName) waLines.push("", `— Shared by ${sharerName}`);
  const waMsg = waLines.join("\n");

  // Telegram — emoji, no markdown (asterisks show literally); link via url param.
  const tgLines: string[] = [`${E.home} ${name.toUpperCase()} — now in immersive 3D`, ""];
  if (locationStr) tgLines.push(`${E.pin} ${locationStr}`);
  if (configSummary) tgLines.push(`${E.build} ${configSummary}`);
  tgLines.push("");
  if (expEmoji.length) tgLines.push("Don't just see it — experience it:", ...expEmoji);
  else tgLines.push("Explore it in immersive 3D.");
  if (sharerName) tgLines.push("", `Shared by ${sharerName}`);
  const tgMsg = tgLines.join("\n");

  // Email — subject + plain body (mailto can't do HTML).
  const emailSubject = `Step inside ${name} — a 3D experience`;
  const emailIntro =
    `${sharerName ? `${sharerName} shared` : "Here's"} an immersive 3D experience of ${name}` +
    `${locationStr ? ` (${locationStr})` : ""}${configSummary ? ` — ${configSummary}` : ""} with you.`;
  const emailLines: string[] = ["Hi,", "", emailIntro, ""];
  if (expBullets.length) emailLines.push("Instead of static photos, walk the actual spaces:", ...expBullets);
  else emailLines.push("Explore it in immersive 3D.");
  emailLines.push("", "Start the experience:", shortUrl("email"));
  const emailBody = emailLines.join("\n");

  // LinkedIn — composer can't be pre-filled; we copy this caption to the
  // clipboard on click, then open the share dialog with just the URL.
  const linkedinCaption =
    `Exploring ${name}${locationStr ? `, ${locationStr}` : ""}` +
    `${configSummary ? ` — ${configSummary}` : ""} — now a full 3D experience` +
    `${expInline ? `: ${expInline}` : ""}, not just photos. Take a look 👇\n${shortUrl("linkedin")}`;

  // Use api.whatsapp.com/send directly (NOT wa.me) — wa.me's redirect step
  // mangles 4-byte/astral emoji (turns them into �) while BMP chars survive.
  const waHref = shareUrl ? `https://api.whatsapp.com/send?text=${encodeURIComponent(waMsg)}` : "#";
  const tgHref = shareUrl ? `https://t.me/share/url?url=${encodeURIComponent(shortUrl("telegram"))}&text=${encodeURIComponent(tgMsg)}` : "#";
  const inHref = shareUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shortUrl("linkedin"))}` : "#";
  const mailHref = shareUrl ? `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}` : "#";
  const smsHref = shareUrl ? `sms:?body=${encodeURIComponent(`${name} in 3D — tour it: ${shortUrlNoScheme("sms")}`)}` : "#";

  // LinkedIn click: copy the caption (LinkedIn won't accept pre-filled text),
  // then let the anchor open the share dialog with the URL.
  const onLinkedInClick = () => {
    if (!shareUrl) return;
    try { navigator.clipboard.writeText(linkedinCaption); } catch (_e) { /* no-op */ }
  };

  /* QR — encodes the branded short URL (with scheme so scanners open it).
     api.qrserver.com (free, no key/dep). ecc=H = 30% error correction so the
     centered OpenSky logo overlay doesn't break scannability. */
  const qrSrc = (size = 240) => shareUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&ecc=H&data=${encodeURIComponent(shortUrl("qr"))}`
    : "";

  const saveQR = async () => {
    try {
      const blob = await fetch(qrSrc(480)).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "share-qr.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* fetch / blob failed — silent */ }
  };

  const shareQR = async () => {
    try {
      const blob = await fetch(qrSrc(480)).then((r) => r.blob());
      const file = new File([blob], "share-qr.png", { type: "image/png" });
      const nav: any = navigator;
      if (nav?.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: "Share QR code" });
      } else {
        // Fallback for browsers without the Web Share API (most desktop) —
        // just save the image and the user can attach it manually.
        await saveQR();
      }
    } catch (_e) { /* user cancel / unsupported — silent */ }
  };

  return (
    <div className="share">
      <button className={"share-btn" + (open ? " share-btn--on" : "")} onClick={onShare} aria-label="Share">
        <Icon.Share /> <span className="share-btn-text">Share</span>
      </button>
      {open && ReactDOM.createPortal(
        // pdv3-root is added here so the modal (portaled to document.body — outside
        // the page wrapper) still resolves the design's CSS variables.
        <div className="pdv3-root share-overlay" onClick={closeModal}>
          <div className="share-modal" role="dialog" aria-modal="true" aria-label="Share this project"
               onClick={(e) => e.stopPropagation()}>
            <button className="share-modal-x" aria-label="Close" onClick={closeModal}><Icon.Close /></button>

            {status === "generating" && (
              <div className="share-gen">
                <span className="share-spinner" aria-hidden="true"></span>
                <div className="share-gen-text">
                  <div className="share-gen-title">Creating your unique shareable link…</div>
                  <div className="share-gen-sub">Hang tight — generating a trackable link for this project.</div>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="share-err">
                <div className="share-err-title">Could not generate share link</div>
                <div className="share-err-sub">Check your connection and try again.</div>
                <button className="share-err-retry" onClick={() => void mintLink()}>Retry</button>
              </div>
            )}

            {status === "ready" && !showQR && (
              <>
                <div className="share-modal-head">
                  <span className="share-modal-ico"><Icon.Share /></span>
                  <div>
                    <div className="share-modal-title">Share project</div>
                    <div className="share-modal-sub">Send your buyers a private link to the full virtual experience.</div>
                  </div>
                </div>

                <div className="share-modal-body">
                  <div className="share-field-label">Your unique link</div>
                  <div className="share-link-row">
                    <input className="share-link" readOnly value={shortUrl("copy")} onFocus={(e) => e.target.select()} />
                    <button className={"share-copy" + (copied ? " share-copy--done" : "")} onClick={() => copy("copy")}>
                      {copied ? <><Icon.Check /> Copied</> : <><Icon.Copy /> Copy</>}
                    </button>
                  </div>

                  {/* Lead-capture toggle — LIVE as of 2026-07-27, shipped ahead
                      of the Digital-Twin gate on purpose. Scope: it drives the
                      META MODEL gate only. ON (default) → no `lg` on the share
                      URL → engine gets leadgate=1; OFF → `&lg=0` → leadgate=0,
                      a plain link for a friend. The DT gate stays hard-off (see
                      the `false &&` in Hero), so this switch never changes twin
                      behaviour until that gate is re-enabled. */}
                  <div
                    className="lc-capture-card"
                    style={{
                      marginTop: 14, padding: "13px 15px", borderRadius: 12,
                      background: captureLeads ? "#ECFDF5" : "#FFF7ED",
                      border: `2px solid ${captureLeads ? "#34D399" : "#F59E0B"}`,
                      transition: "background .15s, border-color .15s",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 800, letterSpacing: ".5px", padding: "2px 8px",
                          borderRadius: 999, color: "#fff",
                          background: captureLeads ? "#10B981" : "#F59E0B",
                        }}>
                          {captureLeads ? "ON" : "OFF"}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#1F2A37" }}>
                          Lead capture
                        </span>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={captureLeads}
                        aria-label="Toggle lead capture"
                        onClick={() => setCaptureLeads((v) => !v)}
                        style={{
                          width: 44, height: 26, borderRadius: 999, border: "none", cursor: "pointer", padding: 3,
                          background: captureLeads ? "#10B981" : "#F59E0B",
                          transition: "background .15s", flex: "0 0 auto",
                          display: "flex", alignItems: "center",
                          justifyContent: captureLeads ? "flex-end" : "flex-start",
                        }}
                      >
                        <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", display: "block", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                      </button>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: captureLeads ? "#047857" : "#B45309", marginTop: 7, lineHeight: 1.45 }}>
                      {captureLeads
                        ? "Visitors unlock the full experience with a quick verification — and you capture the lead. Clicks & views are tracked in your dashboard."
                        : "⚠ Plain link — no sign-in, so you WON'T capture leads from this share. Best only for a friend or colleague. Clicks & views are still tracked."}
                    </div>
                  </div>

                  <div className="share-channels">
                    <a className="share-channel share-channel--wa" href={waHref} target="_blank" rel="noopener noreferrer">
                      <Icon.WhatsApp /> WhatsApp
                    </a>
                    <a className="share-channel share-channel--tg" href={tgHref} target="_blank" rel="noopener noreferrer">
                      <Icon.Telegram /> Telegram
                    </a>
                    <a className="share-channel share-channel--in" href={inHref} target="_blank" rel="noopener noreferrer" onClick={onLinkedInClick} title="Caption copied — paste it into your LinkedIn post">
                      <Icon.LinkedIn /> LinkedIn
                    </a>
                    <a className="share-channel share-channel--mail" href={mailHref}>
                      <Icon.Mail /> Email
                    </a>
                    <a className="share-channel share-channel--sms" href={smsHref}>
                      <Icon.Sms /> SMS
                    </a>
                    <button className="share-channel share-channel--copy" type="button" onClick={() => copy("copy")}>
                      <Icon.Link /> Copy Link
                    </button>
                    <button className="share-channel share-channel--qr" type="button" onClick={() => setShowQR(true)}>
                      <Icon.Qr /> QR Code
                    </button>
                  </div>

                </div>
              </>
            )}

            {status === "ready" && showQR && (
              <>
                <div className="share-modal-head">
                  <span className="share-modal-ico"><Icon.Qr /></span>
                  <div>
                    <div className="share-modal-title">QR Code</div>
                    <div className="share-modal-sub">Show on your phone or print on a brochure / business card.</div>
                  </div>
                </div>

                <div className="share-modal-body">
                  <div className="share-qr">
                    <button className="share-qr-back" type="button" onClick={() => setShowQR(false)}>← Back to channels</button>
                    <div className="share-qr-img-wrap">
                      <img className="share-qr-img" src={qrSrc(240)} alt="QR code for this project" />
                      <div className="share-qr-logo" aria-hidden="true">
                        <img src="/svg/Opensky.svg" alt="" />
                      </div>
                    </div>
                    <div className="share-qr-hint">
                      Scans of this code go to your shareable link — clicks attribute to you automatically.
                    </div>
                    <div className="share-qr-actions">
                      <button className="share-qr-btn share-qr-btn--primary" type="button" onClick={shareQR}>
                        <Icon.Share /> Share image
                      </button>
                      <button className="share-qr-btn" type="button" onClick={saveQR}>
                        <Icon.Download /> Save image
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────── */
const ProjectDetailsV3: React.FC = () => {
  const { projectId, code: shortCode } = useParams();
  const location = useLocation();

  /* ── Public share-link viewer mode ──
     When the URL carries ?ref=<shareCode>, this page is being viewed via
     a sharer's link. Per SHAREABLE_PROJECT_LINKS_SPEC §6.2, we:
       1. Treat the route as public (no agent chrome, no Edit)
       2. POST /share-links/click to record the click + resolve the real
          project_id (the URL has the slug, not the id)
       3. Use the resolved project_id for all downstream fetches
       4. Pass the ref through to the meta-model iframe so the experience
          server can stamp referrer_share_code on its VR session */
  const searchParams = new URLSearchParams(location.search);
  // Share code from either the long URL (?ref=<code>) or the short URL
  // (/s/<code> path param). Both put the viewer into public-viewer mode and
  // resolve the project via the click endpoint — the short route is purely a
  // frontend convenience (no backend), see the /s/:code route registration.
  const shareCode = searchParams.get("ref") || shortCode || "";
  const shareSrc = searchParams.get("src") || "";
  const isPublicViewer = !!shareCode;
  // Lead-gen flag from the share link: a "plain" share carries &lg=0 → off,
  // default ON. This now drives ONLY the Meta Model gate — i.e. the
  // &leadgate=1 URL param sent to the tour engine on shared views (restored to
  // the pre-release behaviour). The Digital-Twin gate (MatterportLeadGate) is
  // held OFF separately at its render site because its OTP backend still 403s
  // on prod (Nivedita's AuthClaimMissing fix not live).
  const leadgateOn = searchParams.get("lg") !== "0";
  // Resolved project_id from the click response (slug → id resolution).
  // Falls back to whatever was in state or in the URL param while we wait.
  const [refResolvedProjectId, setRefResolvedProjectId] = useState<string>("");
  // click_id from the /click response — threaded onto the meta-model iframe URL
  // (for Rithvik's experience server) and stashed in sessionStorage for the
  // Matterport session hook, so all sessions from one click bundle under it.
  const [clickId, setClickId] = useState<string>("");
  // Sharer / agency context returned by /click. Populates the EntryLoader's
  // "Shared by your agent ..." eyebrow + agency logo for public viewers.
  const [shareContext, setShareContext] = useState<{
    sharer_name?: string;
    sharer_roles_in_agency?: string[];
    agency_name?: string;
    agency_logo_url?: string;
    project_name?: string;
  } | null>(null);
  const resolvedProjectId =
    refResolvedProjectId
    || (location.state as any)?.projectId
    || projectId;

  // Ref-guard so a single page load fires exactly one /click POST per
  // share_code. Without this, React 18's StrictMode (dev) double-invokes
  // the effect and we end up with two click rows for the same visit;
  // even in production, a fast remount could double-fire too.
  const clickRecordedFor = useRef<string>("");

  // Fire click + resolve project_id when the page loads with ?ref=
  useEffect(() => {
    if (!isPublicViewer) return undefined;
    if (clickRecordedFor.current === shareCode) return undefined;
    clickRecordedFor.current = shareCode;
    (async () => {
      try {
        const clickPayload: any = { share_code: shareCode };
        if (shareSrc) clickPayload.src = shareSrc;
        if (document.referrer) clickPayload.referrer = document.referrer;
        const r = await recordShareLinkClick(clickPayload);
        // Intentionally NO `alive` guard — StrictMode's dev-mode
        // unmount-remount cycle would otherwise set alive=false before
        // the POST response returns, swallowing setRefResolvedProjectId
        // and leaving the page stuck on "Project not found". React 18
        // safely no-ops state updates on truly unmounted components.
        if (r?.project_id) setRefResolvedProjectId(r.project_id);
        // click_id: drives Tours bundling. State → meta-model iframe URL;
        // sessionStorage (keyed by share code) → the Matterport session hook.
        if (r?.click_id) {
          setClickId(r.click_id);
          try { sessionStorage.setItem(`vr_click:${shareCode}`, r.click_id); } catch (_e) { /* no-op */ }
        }
        // Stash sharer + agency context for the EntryLoader's "Shared
        // by your agent ..." eyebrow + branded loader logo.
        if (r) {
          setShareContext({
            sharer_name: r.sharer_name,
            sharer_roles_in_agency: r.sharer_roles_in_agency,
            agency_name: r.agency_name,
            agency_logo_url: r.agency_logo_url,
            project_name: r.project_name, // used by the loader if backend sends it
          });
        }
      } catch (_e) {
        // Per spec §6.2: if /click errors, still render the project from
        // the URL slugs — never block the viewer on a click hiccup.
      }
    })();
    return undefined;
  }, [isPublicViewer, shareCode, shareSrc]);

  // Tag the body while V3 is mounted so V3 CSS can selectively trim the
  // shared MiniDrawer/Header chrome (SP profile, etc.) on mobile widths
  // WITHOUT mutating the Header component or affecting any other route.
  // Public-viewer mode trims chrome at ALL widths (not just mobile) since
  // anonymous buyers shouldn't see the sharer's agent-app pills.
  useEffect(() => {
    document.body.classList.add("v3-mobile-chrome");
    if (isPublicViewer) document.body.classList.add("v3-public-viewer");
    return () => {
      document.body.classList.remove("v3-mobile-chrome");
      document.body.classList.remove("v3-public-viewer");
    };
  }, [isPublicViewer]);

  // ── Phase 1 data fetching state (mirrors V2) ──
  const [projectData, setProjectData] = useState<any>(null);
  const [agencyData, setAgencyData] = useState<any>(null);
  const [metaModelsList, setMetaModelsList] = useState<any[]>([]);
  const [matterportsList, setMatterportsList] = useState<any[]>([]);
  // Master amenity catalog (id → { category, name, slug, icon }). projectData.amenities
  // only carries amenity_id, so we join here for category-driven highlight foots etc.
  const [allAmenities, setAllAmenities] = useState<any[]>([]);
  // Loader visibility: keep the EntryLoader up until ALL critical fetches resolve
  // (project + agency + matterports + meta-models + amenity catalog) AND a 900ms
  // minimum has elapsed (so it never flickers on fast loads). A 6s safety
  // timeout flips loaderFadeOut on no matter what — page never gets stuck.
  const [pageReady, setPageReady] = useState(false);
  const [minVisibleElapsed, setMinVisibleElapsed] = useState(false);
  const [loaderMounted, setLoaderMounted] = useState(true); // false → remove from DOM
  // VR session count for THIS project — populates the Views pill in SubHeader.
  // Polled every 30s so the count auto-updates as new sessions roll in.
  const [viewCount, setViewCount] = useState<number | null>(null);

  const [clientManagerId, setClientManagerId] = useState("");
  const [clientAdminId, setClientAdminId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [customerId, setCustomerId] = useState("");

  // Page state
  const [active, setActive] = useState<TabId>("description");
  /* Drives the subhead dot: is any walkthrough on this project actually
     published to a customer's website? Cheap read, failure is silent —
     an unshipped endpoint just leaves the dot grey. */
  const [embedLive, setEmbedLive] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [mode, setMode] = useState<ModeId>("meta");

  useEffect(() => {
    if (!resolvedProjectId) return undefined;
    let cancelled = false;
    listEmbedKeys(resolvedProjectId)
      .then((res: any) => {
        if (cancelled) return;
        /* `published` is the live signal, not `status` — status is a
           backend-only abuse flag with no UI (spec §7.4), and an unpublished
           key still exists so it would have kept the dot green. */
        setEmbedLive((res?.embed_keys || []).some((k: any) => k?.published !== false));
      })
      .catch(() => { /* endpoint not shipped — the dot just stays grey */ });
    return () => { cancelled = true; };
  }, [resolvedProjectId]);

  // Attempt 2 of the location capture flow — only prompts if attempt 1
  // (fired by RenderOnAuthenticated at login) was denied. Cool-down and
  // 2-strike limit are enforced inside promptUserGeo. Inherited from V2.
  useEffect(() => {
    void promptUserGeo();
  }, []);

  // ── Critical-data fetch (drives the EntryLoader) ──
  // All page-content fetches run here in one place so we can flip a single
  // `pageReady` flag once they all resolve. The loader then fades out and
  // unmounts, replacing the previous per-section "Loading..." flicker.
  // Also resets loader state for subsequent in-place navigations (A → B
  // without unmount, since React Router keeps V3 mounted across param change).
  useEffect(() => {
    if (!resolvedProjectId) return;
    // In public-viewer mode the URL gives us the project SLUG, not the id.
    // /click resolves slug → real id (via the share_code lookup) and stores
    // it in refResolvedProjectId. Don't kick off the public-project fetch
    // until that lands, otherwise we'd ping the endpoint with "triton" and
    // get back a 404 before the real id arrives one tick later.
    if (isPublicViewer && !refResolvedProjectId) return;
    setPageReady(false);
    setLoaderMounted(true);
    setProjectData(null);

    const run = async () => {
      // ── Public-viewer fast path (?ref= in URL) ──
      // The /public/projects endpoint returns the project + agency_public
      // + amenities (resolved) + meta_models + matterports all inlined,
      // so we hydrate every piece of state from one round-trip. The
      // separate auth-walled calls (getProjectById / getAgencyById /
      // getMetaModels / getMatterports / listAmenities) would all 401
      // for an anonymous buyer, so we skip them entirely here.
      if (isPublicViewer) {
        try {
          const pub: any = await getPublicProject(resolvedProjectId);
          // Shape it like getProjectById did so downstream selectors
          // (which read projectData.<field>) keep working unchanged.
          setProjectData(pub);
          setMetaModelsList(Array.isArray(pub?.meta_models) ? pub.meta_models : []);
          setMatterportsList(Array.isArray(pub?.matterports) ? pub.matterports : []);
          // agency_public is the sanitized subdoc — alias as agencyData
          // so the rest of V3 finds .logo / .name on the expected key.
          if (pub?.agency_public) setAgencyData(pub.agency_public);
          // Amenities are already resolved on the public response
          // (each entry has name/icon/category), so the master catalog
          // join isn't needed in this mode.
          if (Array.isArray(pub?.amenities)) setAllAmenities(pub.amenities);
        } catch {
          // Public endpoint failed — page will render the not-found state.
        }
        setPageReady(true);
        return;
      }

      // ── Authenticated path (existing behaviour) ──
      // Amenity catalog is project-independent → fire in parallel with project fetch.
      const amenitiesPromise = listAmenities()
        .then((res: any) => setAllAmenities(res?.amenities || []))
        .catch(() => { /* leave empty — category foot just won't render */ });

      let project: any = null;
      try {
        const res: any = await getProjectById(resolvedProjectId);
        project = res?.project ?? null;
        setProjectData(project);
      } catch {
        // Project failed — still flip pageReady so the "not found" state shows.
      }

      const agencyIdLocal: string = project?.agency?.id || project?.agency?.["id"] || "";

      // Fire all project-dependent fetches in parallel. NOTE: view-count fetch
      // intentionally NOT in this batch — it pulls up to 1000 sessions and is
      // the slowest call, would dominate the loader's wait time. It loads
      // independently via the polling effect below, and the Views pill shows
      // a small skeleton until it lands (couple hundred ms after page reveal).
      await Promise.allSettled([
        amenitiesPromise,
        agencyIdLocal
          ? getAgencyById(agencyIdLocal).then((a: any) => setAgencyData(a)).catch(() => { })
          : Promise.resolve(),
        getMetaModels(resolvedProjectId)
          .then((d: any) => setMetaModelsList(d?.meta_models || []))
          .catch(() => { }),
        getMatterports(resolvedProjectId)
          .then((d: any) => setMatterportsList(d?.matterports || []))
          .catch(() => { }),
      ]);

      setPageReady(true);
    };

    run();
  }, [resolvedProjectId]);

  // Min visible time so the loader never just flickers on cached/fast loads.
  // Resets on resolvedProjectId so in-place navigation (A → B without unmount)
  // still respects the 900ms min for the new project.
  useEffect(() => {
    setMinVisibleElapsed(false);
    const t = setTimeout(() => setMinVisibleElapsed(true), 900);
    return () => clearTimeout(t);
  }, [resolvedProjectId]);

  // Safety: if something hangs, force the loader down after 6s no matter what.
  // Resets per project too — each navigation gets its own 6s window.
  useEffect(() => {
    const t = setTimeout(() => { setPageReady(true); setMinVisibleElapsed(true); }, 6000);
    return () => clearTimeout(t);
  }, [resolvedProjectId]);

  // Compose: fade out when ready AND min-time satisfied; unmount after fade.
  const loaderFadingOut = pageReady && minVisibleElapsed;
  useEffect(() => {
    if (!loaderFadingOut) return undefined;
    const t = setTimeout(() => setLoaderMounted(false), 550);  // matches the .5s CSS transition + a tick
    return () => clearTimeout(t);
  }, [loaderFadingOut]);

  // User profile IDs for urlString
  useEffect(() => {
    const email = UserService.getUseremail();
    const fetches: Promise<any>[] = [];

    if (UserService.hasRole("agency_business_admin")) {
      fetches.push(
        getClientManagerByEmailId(email).then((res: any) => {
          setClientManagerId(res.agency_business_admin?._id || "");
        })
      );
    }
    if (UserService.hasRole("agency_system_admin")) {
      fetches.push(
        getClientAdminByEmailId(email).then((res: any) => {
          setClientAdminId(res.agency_system_admin?._id || "");
        })
      );
    }
    if (UserService.hasRole("agent")) {
      fetches.push(
        getAgentProfile(email).then((res: any) => {
          setAgentId(res.agent?._id || "");
        })
      );
    }
    if (UserService.hasRole("customer")) {
      fetches.push(
        getCustomerProfile(email).then((res: any) => {
          setCustomerId(res.customer?._id || "");
        })
      );
    }
    Promise.allSettled(fetches);
  }, []);

  // VR session count for this project — fetch on project load, then poll
  // every 30s so the Views pill auto-updates as new sessions are completed.
  // Agency-wide pull (limited to 1000 sessions) is the only path until the
  // backend adds a project_id filter param.
  useEffect(() => {
    const agencyIdLocal: string = projectData?.agency?.id || "";
    if (!agencyIdLocal || !resolvedProjectId) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res: any = await getVrSessionList(agencyIdLocal, 0, 1000);
        if (cancelled) return;
        const sessions: any[] = res?.sessions || [];
        const n = sessions.filter((s: any) => s.project_id === resolvedProjectId).length;
        setViewCount(n);
      } catch { /* network blip — keep previous count, retry on next tick */ }
    };
    refresh();   // initial fetch — runs in parallel with page render
    const id = setInterval(refresh, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [projectData?.agency?.id, resolvedProjectId]);

  // ── Loader stub: prefer navigation state (passed by ProjectList card click)
  //    so the loader shows real text immediately; fall back to projectData
  //    once it arrives. ──
  const navState = (location.state as any) || {};
  const loaderName: string = (
    projectData?.project_name ||
    projectData?.name ||
    navState.projectName ||
    shareContext?.project_name ||
    ""
  );
  const loaderLocation: string = projectData
    ? [projectData?.address?.landmark || "", projectData?.address?.city || ""]
        .filter(Boolean).join(", ")
    : (navState.locationStr || "");
  // Agency logo — same field the top-nav uses (`agencyData.logo`). To avoid
  // a blank-logo period while the agency fetch is in flight, fall back to the
  // cached URL MiniDrawer/MobileDrawer wrote to localStorage on app boot.
  // Fresh agencyData.logo wins once it lands (covers cross-agency navigation
  // for super_admins / customers, where the cached value may be stale).
  const cachedAgencyLogo: string = (() => {
    try { return localStorage.getItem("agencyLogo") || ""; }
    catch { return ""; }
  })();
  // For public-viewer mode, prefer the agency logo that /click returned
  // (an anonymous buyer has no localStorage cache from the logged-in
  // flow). Falls through to the agencyData → cached chain after that.
  const loaderAgencyLogo: string = (isPublicViewer && shareContext?.agency_logo_url)
    || agencyData?.logo
    || cachedAgencyLogo;
  const loaderNode = loaderMounted ? (
    <EntryLoader
      projectName={loaderName}
      locationStr={loaderLocation}
      agencyLogo={loaderAgencyLogo}
      fadingOut={loaderFadingOut}
      sharerName={isPublicViewer && loaderName ? shareContext?.sharer_name : undefined}
      sharerRoles={isPublicViewer && loaderName ? shareContext?.sharer_roles_in_agency : undefined}
    />
  ) : null;

  // Loader still up + no project yet → render only the loader.
  if (loaderMounted && !projectData) {
    return loaderNode;
  }

  // Loader has unmounted but project never arrived → not-found state.
  if (!projectData) {
    return (
      <div className="pdv3-root" data-tone="slate">
        <div className="pdv3-state">Project not found.</div>
      </div>
    );
  }

  // ── Derived values ──
  const projectName: string = projectData?.project_name || projectData?.name || "Project";
  const landmark: string = projectData?.address?.landmark || "";
  const city: string = projectData?.address?.city || "";
  const province: string = projectData?.address?.province || "";
  const country: string = projectData?.address?.country || "";
  const addressLine1: string = projectData?.address?.address_line1 || "";
  const addressLine2: string = projectData?.address?.address_line2 || "";
  const postalCode: string = projectData?.address?.postal_code || "";
  // Coordinates live on the project root as `geo_location: [lat, lng]` (string|number).
  // Fall back to legacy `address.lat`/`latitude` only if geo_location is absent.
  const geoLoc = projectData?.geo_location as [number | string, number | string] | undefined;
  const lat: number | null = geoLoc?.[0] != null
    ? Number(geoLoc[0])
    : (projectData?.address?.latitude ?? projectData?.address?.lat ?? null);
  const lng: number | null = geoLoc?.[1] != null
    ? Number(geoLoc[1])
    : (projectData?.address?.longitude ?? projectData?.address?.lng ?? null);
  const locationStr: string = [landmark, city].filter(Boolean).join(", ");

  // Resolve agency id across response shapes: authenticated returns
  // `projectData.agency.id`; the PUBLIC (share) endpoint nests it under
  // `agency_public` (aliased to agencyData). Without this fallback, public
  // share sessions were saved with agency_id="" → excluded from the
  // agency-scoped session list → never showed in Views.
  const agencyId: string =
    projectData?.agency?.id ||
    projectData?.agency_id ||
    projectData?.agency_public?.id ||
    projectData?.agency_public?._id ||
    agencyData?.id ||
    agencyData?._id ||
    "";
  const agencySlug: string = agencyData?.slug || "";
  const projectSlug: string = projectData?.slug || "";
  const routeViews: string[] = projectData?.route_view || [];

  // ── Phase 2: Description (parsed JSON), Highlights, Location ──
  const description = parseDescription(projectData?.description);

  // Location POIs — backend schema pending. Read off projectData.location_pois
  // if/when populated; otherwise the panel shows an empty hint, no fake demo data.
  const poiGroups: PoiCategoryGroup[] = Array.isArray(projectData?.location_pois)
    ? (projectData!.location_pois as PoiCategoryGroup[])
    : [];

  const agencyName: string = agencyData?.name || projectData?.agency_name || "";
  const agencyLogo: string = projectData?.logo || agencyData?.logo || "";
  const agencyAddrParts = agencyData?.address
    ? (typeof agencyData.address === "string"
        ? [agencyData.address]
        : [agencyData.address.address_line1, agencyData.address.city, agencyData.address.country].filter(Boolean))
    : [];
  const agencyAddress: string = agencyAddrParts.join(", ");

  const reraNumber: string = projectData?.registration_number || "";
  const possessionDate: string = projectData?.completion_date || "";
  const projectArea = projectData?.project_area || null;
  const towers: number | null = projectData?.project_type_details?.number_of_towers ?? null;
  const floors: number | null = projectData?.project_type_details?.number_of_floors ?? null;
  const clubhouseSqft: number | null = projectData?.project_type_details?.clubhouse_size_sqft ?? null;
  const unitConfigsList: any[] = projectData?.project_type_details?.unit_configurations || [];
  const totalResidences: number = unitConfigsList.reduce((s: number, u: any) => s + (u?.count || 0), 0);
  // Extract distinct BHK counts from unit configurations for the "2 · 3 · 4 BHK" footnote.
  const bhkOptions: number[] = Array.from(new Set(
    unitConfigsList
      .map((u: any) => Number(u?.bhk ?? u?.bhk_count ?? u?.configuration ?? NaN))
      .filter((n: number) => Number.isFinite(n) && n > 0)
  )).sort((a: number, b: number) => a - b) as number[];
  const amenitiesCount: number = Array.isArray(projectData?.amenities) ? projectData.amenities.length : 0;
  // "X amenities across N levels" — N is the count of distinct amenity categories
  // (e.g. Sports, Wellness, Kids…). projectData.amenities entries only carry
  // `amenity_id`; category lives on the master list, so we join on _id.
  const amenityCategoryCount: number = (() => {
    if (!Array.isArray(projectData?.amenities) || allAmenities.length === 0) return 0;
    // Plain-object dict (not Map<>) — the `Map` symbol is shadowed by the
    // @vis.gl/react-google-maps component import above.
    const byId: Record<string, any> = {};
    for (const a of allAmenities as any[]) {
      if (a?._id) byId[a._id] = a;
    }
    const cats = new Set<string>();
    for (const a of projectData.amenities as any[]) {
      const master = byId[a?.amenity_id];
      const cat = master?.category || a?.category;
      if (cat) cats.add(String(cat));
    }
    return cats.size;
  })();
  /* Stored values are the full enum AddProject writes — `villa_gated_community`,
     `layout_gated_community`, `commercial`, `farmland`, `apartment` — not bare
     words. Comparing against "villa" and "commercial" silently fell through to
     the apartment branch, so a villa project shared as "3 & 4 BHK Apartments"
     and showed Towers/Floors stats it doesn't have.

     Prefix-match rather than listing exact values: it also catches the legacy
     `commercial_space` still held by projects created before the dropdown was
     changed, and won't break again if a `villa_standalone` is ever added. */
  const rawProjectType: string = (projectData?.project_type_details?.type || "apartment").toLowerCase();
  const isVillaType = rawProjectType.startsWith("villa");
  const projectType: "apartment" | "villa" = isVillaType ? "villa" : "apartment";
  const isCommercial = rawProjectType.startsWith("commercial");
  const isLayout = rawProjectType.startsWith("layout");
  const isFarmland = rawProjectType.startsWith("farmland");

  // Commercial-specific fields (only meaningful when isCommercial)
  const buildingGrade: string = projectData?.project_type_details?.building_grade || "";
  const totalLeasableSqft: number | null =
    projectData?.project_type_details?.total_leasable_area_sqft ?? null;
  const parkingRatio: string = projectData?.project_type_details?.parking_ratio || "";
  const commonAreaSqft: number | null =
    projectData?.project_type_details?.common_area_size_sqft ?? null;
  const towersList: CommercialTower[] =
    Array.isArray(projectData?.project_type_details?.towers)
      ? projectData.project_type_details.towers
      : [];

  // Layout-specific derived counts (same shape used for residential, just
  // semantically "plots" rather than "residences" for this branch).
  const uniquePlotTypes: number = unitConfigsList.length;

  // One-line config summary for the share messages — adapts by project type:
  // residential → "3 & 4 BHK Apartments"; layout/gated/farmland → "2 plot sizes";
  // commercial → "Tower A · 12,000 sq.ft floor plate". Empty if no data.
  const shareConfigSummary: string = (() => {
    if (isCommercial) {
      const t = towersList[0];
      if (t?.name && t?.floor_plate_sqft)
        return `${t.name} · ${t.floor_plate_sqft.toLocaleString()} sq.ft floor plate`;
      return t?.name || "";
    }
    if (isLayout || isFarmland) {
      return uniquePlotTypes > 0
        ? `${uniquePlotTypes} plot size${uniquePlotTypes > 1 ? "s" : ""}`
        : "";
    }
    if (bhkOptions.length) {
      const list =
        bhkOptions.length <= 2
          ? bhkOptions.join(" & ")
          : `${bhkOptions.slice(0, -1).join(", ")} & ${bhkOptions[bhkOptions.length - 1]}`;
      return `${list} BHK ${isVillaType ? "Villas" : "Apartments"}`;
    }
    return "";
  })();

  // Per-type tab nav:
  // - Commercial         → drop "Unit Details" entirely
  // - Layout / Farmland  → rename "Unit Details" to "Plots"
  // - Else               → unchanged
  const baseTabs: typeof TABS = isCommercial
    ? TABS.filter((t) => t.id !== "unit-details")
    : (isLayout || isFarmland)
      ? TABS.map((t) => (t.id === "unit-details" ? { ...t, label: "Plots" } : t))
      : TABS;
  /* Distribution is operational — embeds and offline builds. Staff only,
     and never on the public share view where a buyer would see it. */
  const canSeeDistribution =
    !isPublicViewer &&
    (UserService.hasRole("agent") ||
      UserService.hasRole("agency_system_admin") ||
      UserService.hasRole("agency_business_admin") ||
      UserService.hasRole("super_admin"));
  /* Publish opens a modal from the subhead, so it never enters the tab
     strip — the content tabs stay purely about the project itself. */
  const visibleTabs: typeof TABS = baseTabs;

  // Same urlString shape as V2 in authenticated mode. In public-viewer
  // mode (per SHAREABLE_PROJECT_LINKS_SPEC §6.2.1) we DROP the four
  // person-identity params (aba_id, asa_id, agentId, customerId — the
  // viewer is anonymous) and ADD:
  //   - &ref=<shareCode>  → experience server stamps referrer_share_code
  //                          on the vr_session row
  //   - &src=<channel>    → experience server stamps src (whatsapp /
  //                          email / linkedin / qr / …) on the vr_session,
  //                          giving direct per-channel funnel reporting
  //                          without joining back through share_link_clicks.
  //                          vr_sessions.src already exists backend-side.
  const urlString = isPublicViewer
    ? `/?projectId=${resolvedProjectId || ""}` +
      `${agencyId ? `&agencyId=${agencyId}` : ""}` +
      `${agencySlug ? `&agency_slug=${encodeURIComponent(agencySlug)}` : ""}` +
      `${projectSlug ? `&project_slug=${encodeURIComponent(projectSlug)}` : ""}` +
      `&ref=${encodeURIComponent(shareCode)}` +
      `${shareSrc ? `&src=${encodeURIComponent(shareSrc)}` : ""}` +
      `${clickId ? `&click_id=${encodeURIComponent(clickId)}` : ""}` +
      // Arms Rithvik's engine lead gate on shared views (his domain rule
      // defaults it OFF on openskyvr subdomains). Harmless until his gate
      // ships — the engine ignores the unknown param. "Plain" shares (lg=0)
      // turn it off so a friend can explore without the gate.
      `&leadgate=${leadgateOn ? "1" : "0"}`
    : `/?projectId=${resolvedProjectId || ""}` +
      `${agencyId ? `&agencyId=${agencyId}` : ""}` +
      `${agencySlug ? `&agency_slug=${encodeURIComponent(agencySlug)}` : ""}` +
      `${projectSlug ? `&project_slug=${encodeURIComponent(projectSlug)}` : ""}` +
      `${clientManagerId ? `&aba_id=${clientManagerId}` : ""}` +
      `${clientAdminId ? `&asa_id=${clientAdminId}` : ""}` +
      `${agentId ? `&agentId=${agentId}` : ""}` +
      `${customerId ? `&customerId=${customerId}` : ""}`;

  // Per-project knobs that aren't yet in schema; defaults stand in for now.
  const leadAttribution = true;

  // Edit button — show only for agency_business_admin or agency_system_admin.
  // Matches the existing /edit_project/:projectId route registered in src/index.tsx.
  const canEdit =
    UserService.hasRole("agency_business_admin") ||
    UserService.hasRole("agency_system_admin");
  const editHref = canEdit && resolvedProjectId ? `/edit_project/${resolvedProjectId}` : null;

  return (
    <>
      {loaderNode}
      <div className="pdv3-root" data-tone="slate">
      <SubHeader
        projectName={projectName}
        locationStr={locationStr}
        agencySlug={agencySlug}
        projectId={resolvedProjectId}
        editHref={editHref}
        leadAttribution={leadAttribution}
        viewCount={viewCount}
        showViews={!isPublicViewer}
        isPublicViewer={isPublicViewer}
        agencyLogo={loaderAgencyLogo}
        sharerName={shareContext?.sharer_name}
        sharerRoles={shareContext?.sharer_roles_in_agency}
        configSummary={shareConfigSummary}
        experienceFlags={{
          meta: (metaModelsList?.length || 0) > 0,
          twin: (matterportsList?.length || 0) > 0,
          route: (routeViews?.length || 0) > 0,
        }}
        agencyId={agencyId}
        showDistribution={canSeeDistribution}
        embedLive={embedLive}
        onOpenDistribution={() => setPublishOpen(true)}
      />
      <div className="shell">
        <Hero
          mode={mode}
          onMode={setMode}
          metaModels={metaModelsList}
          matterports={matterportsList}
          routeViews={routeViews}
          urlString={urlString}
          projectId={resolvedProjectId || ""}
          agencyId={agencyId}
          agencySlug={agencySlug}
          projectSlug={projectSlug}
          clientManagerId={clientManagerId}
          clientAdminId={clientAdminId}
          agentId={agentId}
          customerId={customerId}
          isPublicViewer={isPublicViewer}
          leadgateOn={leadgateOn}
          projectName={projectName}
          sharerName={shareContext?.sharer_name}
          agencyName={agencyName}
          agencyLogo={loaderAgencyLogo}
        />
        <Tabs tabs={visibleTabs} active={active} onChange={setActive} />
        {active === "description" && (
          <DescriptionPanel
            description={description}
            agencyName={agencyName}
            agencyLogo={agencyLogo}
            agencyAddress={agencyAddress}
          />
        )}
        {active === "amenities" && (
          <AmenitiesPanel
            projectAmenities={Array.isArray(projectData?.amenities) ? projectData.amenities : []}
            allAmenities={allAmenities}
          />
        )}
        {active === "location" && (
          <LocationPanel
            projectName={projectName}
            addressLine1={addressLine1}
            addressLine2={addressLine2}
            landmark={landmark}
            city={city}
            province={province}
            country={country}
            postalCode={postalCode}
            lat={lat}
            lng={lng}
            poiGroups={poiGroups}
          />
        )}
        {active === "highlights" && !isCommercial && !isLayout && !isFarmland && (
          <HighlightsPanel
            reraNumber={reraNumber}
            possessionDate={possessionDate}
            projectType={projectType}
            projectArea={projectArea}
            landmark={landmark}
            towers={towers}
            floors={floors}
            totalResidences={totalResidences}
            bhkOptions={bhkOptions}
            amenitiesCount={amenitiesCount}
            amenityCategoryCount={amenityCategoryCount}
            clubhouseSqft={clubhouseSqft}
          />
        )}
        {active === "highlights" && isCommercial && (
          <CommercialHighlightsPanel
            reraNumber={reraNumber}
            possessionDate={possessionDate}
            buildingGrade={buildingGrade}
            totalLeasableSqft={totalLeasableSqft}
            parkingRatio={parkingRatio}
            commonAreaSqft={commonAreaSqft}
            towers={towersList}
            amenitiesCount={amenitiesCount}
            amenityCategoryCount={amenityCategoryCount}
            projectArea={projectArea}
            landmark={landmark}
          />
        )}
        {active === "highlights" && isLayout && (
          <LayoutHighlightsPanel
            reraNumber={reraNumber}
            possessionDate={possessionDate}
            projectArea={projectArea}
            landmark={landmark}
            totalPlots={totalResidences}
            uniquePlotTypes={uniquePlotTypes}
            clubhouseSqft={clubhouseSqft}
            amenitiesCount={amenitiesCount}
            amenityCategoryCount={amenityCategoryCount}
          />
        )}
        {active === "highlights" && isFarmland && (
          <FarmlandHighlightsPanel
            reraNumber={reraNumber}
            possessionDate={possessionDate}
            projectArea={projectArea}
            landmark={landmark}
            totalPlots={totalResidences}
            uniquePlotTypes={uniquePlotTypes}
            amenitiesCount={amenitiesCount}
            amenityCategoryCount={amenityCategoryCount}
          />
        )}
        {active === "unit-details" && !isCommercial && !isLayout && !isFarmland && (
          <UnitDetailsPanel unitConfigurations={unitConfigsList as UnitConfig[]} />
        )}
        {active === "unit-details" && (isLayout || isFarmland) && (
          <LayoutPlotsPanel plotConfigurations={unitConfigsList as PlotConfig[]} />
        )}
        {active === "media" && (
          <MediaPanel
            images={Array.isArray(projectData?.project_images) ? projectData.project_images : []}
            videos={Array.isArray(projectData?.property_videos) ? projectData.property_videos : []}
            brochureUrl={projectData?.project_brochure || ""}
            projectName={projectName}
          />
        )}
      </div>
      </div>

      {/* Publish on your website — assets × websites, one embed code per pair.
          Replaces the old Distribution-as-modal, which was meta-model-only and
          predates per-site keys. The portfolio Distribution PAGE stays: it
          answers a different question ("what's out there across every
          project?"). Route views are deliberately absent — no lead gate exists
          for them, so they can't honour a site's gate (spec §4). */}
      {publishOpen && canSeeDistribution && (
        <PublishEmbedModal
          onClose={() => setPublishOpen(false)}
          projectId={resolvedProjectId}
          projectName={projectName}
          metaModels={(metaModelsList || [])
            .filter((m: any) => m?._id)
            .map((m: any) => ({
              id: String(m._id),
              name: String(m.name || "Untitled walkthrough"),
              sub: String(m.configuration || m.tower || ""),
            }))}
          digitalTwins={(matterportsList || [])
            .filter((m: any) => m?.matterport_id || m?._id)
            .map((m: any) => ({
              id: String(m.matterport_id || m._id),
              name: String(m.name || "Digital twin"),
              sub: String(m.description || ""),
            }))}
        />
      )}
    </>
  );
};

export default ProjectDetailsV3;
export { ProjectDetailsV3 };
